import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Building2, CheckCircle, ChevronDown, ChevronRight, Clock, Download, Loader2, Mail, MessageCircle, Minus, Package, Pencil, Plus, Search, Share2, ShoppingCart, Sparkles, StickyNote, Wand2, Wrench, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OrderResponse } from '../../../services/OrderService';
import { orderExportService, PdfPayload } from '../../../services/OrderExportService';
import { orderService } from '../../../services/OrderService';
import { useOrder } from '../../../contexts/OrderContext';
import { useAuth } from '../../../contexts/AuthContext';
import { RepairEstimate, RepairType } from '../../../services/CartOrderService';
import { discountService, OrderPriceResolveResult, DiscountAgreement } from '../../../services/DiscountService';
import { adminService } from '../../../services/AdminService';
import { requestedOriginalTerm } from '../../../utils/partLabel';
import { composeNumberRequestLabel } from '../../../utils/partLabel';
import {
  parsePrice,
  toRealDotAmount,
  deriveAvailability,
  getIntegrationData,
  getIntegrationPrice,
  resolveChipStatus,
  availableQtyForType,
  supplierSkuForType,
  locationOptions,
  orderedLocationOptions,
  isHomeLocation,
  activeLocation,
  priceForLocation,
  type AvailLocation,
  formatCheckedAt,
  type AvailConfig,
  type LocationPriority,
  type AvailPriority,
  type PricePriority,
  type AvailabilityView,
  type DmsTypeGroup,
  type DeliveryOption,
  type StockStatus,
  type StockRemark,
} from '../../../utils/inventoryAvailability';
import { CART_IMAGE_PRESENTATION } from './cartImagePresentation';

// "Add Labor" (AI repair-hours estimate) is hidden for now; flip to bring it back.
const SHOW_ADD_LABOR = false;

const PRICING_FLOW_FLAG = 'pricing_flow_wait_for_pricing';
const INVENTORY_FLAG = 'inventory_data';
const DELIVERY_FLAG = 'delivery_time';

// Pending-data polling: refresh every PENDING_POLL_INTERVAL_MS while lines wait
// for pricing/integration data. Each tick also asks the backend whether the
// order's pricing workflow is still queued/running in Temporal; once it is
// idle (finished, failed, or none matched) the stuck lines swap the spinner for
// a manual "Re-check" button, which re-arms polling. Without a status answer
// the old timer budget (MAX_PENDING_POLLS, ~21 s) applies; with one, a safety
// cap (MAX_TRACKED_POLLS, ~3 min) stops a run that never ends.
const PENDING_POLL_INTERVAL_MS = 3000;
const MAX_PENDING_POLLS = 7;
const MAX_TRACKED_POLLS = 60;
const QUEUED_NOTICE_SECONDS = 30;

interface MagicEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  part: any;
  onSubmit: (prompt: string) => void;
}

const MagicEditModal: React.FC<MagicEditModalProps> = ({ isOpen, onClose, part, onSubmit }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const partData = part?.structured_data?.part_data || part?.structured_data?.PART_DATA || {};
  const partId = partData.part_id || partData.PART_ID || 'Unknown';
  const description = partData.description || partData.DESCRIPTION || '';
  const translatedDesc = part?.translated_description;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(prompt);
      setPrompt('');
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('cart.magicEdit.title', 'Magic Part Edit')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Part Info */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="font-mono text-sm font-medium text-gray-700">{partId}</div>
          {translatedDesc && translatedDesc !== description ? (
            <div className="flex flex-col leading-tight mt-1">
              <span className="text-sm text-gray-600">{translatedDesc}</span>
              <span className="text-xs text-gray-400">{description}</span>
            </div>
          ) : (
            <div className="text-sm text-gray-500 mt-1">{description}</div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('cart.magicEdit.promptLabel', 'How would you like to change this part?')}
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('cart.magicEdit.placeholder', 'e.g., Find a cheaper alternative, or find the OEM version...')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
            rows={4}
            autoFocus
          />

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={!prompt.trim() || isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <Wand2 className="w-4 h-4" />
              {isSubmitting
                ? t('cart.magicEdit.submitting', 'Processing...')
                : t('cart.magicEdit.submit', 'Apply Magic')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface LaborEstimateModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any;
  onSave: (item: any, hours: number, rate: number, note: string, repairType: RepairType) => void | Promise<void>;
  onRemove: (item: any) => void | Promise<void>;
  defaultRate: number;
}

const REPAIR_TYPES: { value: RepairType; labelKey: string; defaultLabel: string }[] = [
  { value: 'repair', labelKey: 'cart.sidePanel.labor.repairTypes.repair', defaultLabel: 'Repair' },
  { value: 'replace', labelKey: 'cart.sidePanel.labor.repairTypes.replace', defaultLabel: 'Replace' },
  { value: 'paint', labelKey: 'cart.sidePanel.labor.repairTypes.paint', defaultLabel: 'Paint' },
  { value: 'diagnose', labelKey: 'cart.sidePanel.labor.repairTypes.diagnose', defaultLabel: 'Diagnose' },
  { value: 'other', labelKey: 'cart.sidePanel.labor.repairTypes.other', defaultLabel: 'Other' },
];

const LaborEstimateModal: React.FC<LaborEstimateModalProps> = ({
  isOpen,
  onClose,
  item,
  onSave,
  onRemove,
  defaultRate,
}) => {
  const { t } = useTranslation();
  const partData = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
  const existing = partData.labor_estimate as LaborEstimate | undefined;

  const [hours, setHours] = useState<string>(existing ? String(existing.labor_hours) : '');
  const [rate, setRate] = useState<string>(existing ? String(existing.labor_rate ?? defaultRate) : String(defaultRate));
  const [note, setNote] = useState<string>(existing?.labor_note ?? '');
  const [repairType, setRepairType] = useState<RepairType>(existing?.repair_type ?? 'repair');
  const [isSavingLabor, setIsSavingLabor] = useState(false);
  const [isRemovingLabor, setIsRemovingLabor] = useState(false);

  // Reset fields when item changes
  useEffect(() => {
    const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
    const ex = pd.labor_estimate as LaborEstimate | undefined;
    setHours(ex ? String(ex.labor_hours) : '');
    setRate(ex ? String(ex.labor_rate ?? defaultRate) : String(defaultRate));
    setNote(ex?.labor_note ?? '');
    setRepairType(ex?.repair_type ?? 'repair');
  }, [item, defaultRate]);

  if (!isOpen) return null;

  const partId = partData.part_number || partData.PART_ID || '';
  const description = partData.description || partData.DESCRIPTION || '';
  const translatedDesc = item?.translated_description;

  const hoursNum = parseFloat(hours);
  const rateNum = parseFloat(rate);
  const isValid = !isNaN(hoursNum) && hoursNum > 0 && !isNaN(rateNum) && rateNum >= 0;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setIsSavingLabor(true);
    try {
      await onSave(item, hoursNum, rateNum, note.trim(), repairType);
      onClose();
    } finally {
      setIsSavingLabor(false);
    }
  };

  const handleRemove = async () => {
    setIsRemovingLabor(true);
    try {
      await onRemove(item);
      onClose();
    } finally {
      setIsRemovingLabor(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('cart.sidePanel.labor.modalTitle', 'Labor Estimate')}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Part Info */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          {partId && <div className="font-mono text-sm font-medium text-gray-700">{partId}</div>}
          <div className="text-sm text-gray-500 mt-0.5">
            {translatedDesc || description}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="space-y-3">

          {/* Repair Type selector */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              {t('cart.sidePanel.labor.repairTypeLabel', 'Work Type')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {REPAIR_TYPES.map(({ value, labelKey, defaultLabel }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRepairType(value)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${repairType === value
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                    }`}
                >
                  {t(labelKey, defaultLabel)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('cart.sidePanel.labor.hoursLabel', 'Hours')}
              </label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder={t('cart.sidePanel.labor.hoursPlaceholder', 'e.g. 2.5')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('cart.sidePanel.labor.rateLabel', 'Rate / hr ($)')}
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder={t('cart.sidePanel.labor.ratePlaceholder', 'e.g. 85')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Cost preview */}
          {isValid && (
            <div className="flex items-center justify-between bg-indigo-50 rounded-lg px-3 py-2 text-sm">
              <span className="text-indigo-700 font-medium">
                {t('cart.sidePanel.labor.estCost', 'Est. labor cost')}
              </span>
              <span className="font-semibold text-indigo-900">${formatPrice(hoursNum * rateNum)}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('cart.sidePanel.labor.noteLabel', 'Notes (optional)')}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('cart.sidePanel.labor.notePlaceholder', 'e.g. Includes R&I time')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            {existing && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={isRemovingLabor || isSavingLabor}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRemovingLabor && <Loader2 className="w-3 h-3 animate-spin" />}
                {t('cart.sidePanel.labor.remove', 'Remove Estimate')}
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={onClose}
                disabled={isSavingLabor || isRemovingLabor}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={!isValid || isSavingLabor || isRemovingLabor}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {isSavingLabor ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
                {isSavingLabor ? t('cart.sidePanel.labor.saving', 'Saving...') : t('cart.sidePanel.labor.submit', 'Save Estimate')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Labor estimate data — re-exported from CartOrderService for consumers */
export type { RepairEstimate as LaborEstimate, RepairType };
/** Local alias */
type LaborEstimate = RepairEstimate;

/** Default hourly labor rate (USD) */
const DEFAULT_LABOR_RATE = 85;

interface CartSidePanelProps {
  cartItems: any[];
  onRemovePart: (item: any) => void;
  onUpdateQuantity: (item: any, quantity: number) => void;
  onMagicEdit?: (item: any, prompt: string) => void;
  onLaborUpdate?: (item: any, estimate: LaborEstimate | null) => void;
  /** `selection` (optional) records WHERE the price came from — the DMS line-type
   *  and warehouse / point of sale the user picked — so the parent can persist it
   *  on the line (part_data.dms_type / dms_location) next to the price. */
  onPriceUpdate?: (item: any, newPrice: number | null, selection?: { dms_type: string | null; dms_location: string | null }) => void;
  /** Persist a manual per-line delivery-time choice (days, or null to reset to auto). */
  onDeliveryUpdate?: (item: any, days: number | null) => void;
  onDescriptionUpdate?: (item: any, newDescription: string) => void;
  /** Persist a per-line free-text note (structured_data.part_data.note); null clears it. */
  onNoteUpdate?: (item: any, note: string | null) => void | Promise<void>;
  onLineDiscountUpdate?: (item: any, value: number | null, type: 'percentage' | 'fixed') => Promise<void>;
  /** Called with all parts to trigger AI estimation; receives updated items to upsert */
  onEstimateRepairs?: (cartItems: any[]) => Promise<void>;
  /** Called after a successful order submission so the parent can refresh order state */
  onOrderSubmitted?: () => void;
  /** Called to refresh cart items from the server (for async price loading) */
  onRefreshItems?: () => Promise<void>;
  /** Called to re-check a single line's inventory/price availability. Resolves
   *  once fresh integration_data has been fetched. Falls back to a full refresh
   *  via onRefreshItems when omitted. */
  onRecheckInventory?: (item: any) => Promise<void>;
  /** Called to append a workflow audit note (fire-and-forget) */
  onWorkflowLog?: (note: string, stepType: 'edit_price' | 'edit_discount' | 'edit_description') => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  /** When true, all editing actions are disabled (e.g. completed or shared orders) */
  readOnly?: boolean;
  /** Notifies the parent when the desktop panel minimizes/expands, so the chat
   *  composer can reserve the matching right-side width. */
  onMinimizedChange?: (minimized: boolean) => void;
}

/** Classify an item as part vs service */
const isServiceItem = (item: any): boolean => {
  return !!(item.is_service || item.structured_data?.part_data?.is_service);
};

/** Check if an item is a not-found part */
const isNotFoundItem = (item: any): boolean => {
  return !!(item.structured_data?.part_data?.not_found);
};

/** Datasource (vector-search suggestion) line: priced from the org's own data
 *  source row at add time. No DMS pricing/inventory will EVER arrive for it, so
 *  it must never count as pending or offer an availability re-check. */
const isDatasourceItem = (item: any): boolean => {
  return item.structured_data?.part_data?.price_source === 'datasource'
    || String(item.part_id || item.structured_data?.part_data?.part_id || '').startsWith('vs-');
};

/** Get the related user query for an item */
const getRelatedQuery = (item: any): string | null => {
  return item.query || item.structured_data?.part_data?.query || item.structured_data?.PART_DATA?.QUERY || null;
};

/** The user's original requested term for an item, normalized the SAME way the
 *  chat results table does. The stored query is sometimes the full resolved label
 *  ("Friso de Porta, Dianteiro, Esquerdo (friso porta frente esquerdo)") when the
 *  verbatim term wasn't captured separately; requestedOriginalTerm pulls out
 *  the parenthetical original ("friso porta frente esquerdo"), so the cart's
 *  "Requested" column matches the chat's. Already-clean terms pass through. */
const requestedTermFor = (item: any): string => {
  const raw = (getRelatedQuery(item) || '').trim();
  if (!raw) return '';
  const term = requestedOriginalTerm(raw);
  // A part-number request ("SKU 10588708") reads as the resolved catalog
  // description with the number kept as the parenthetical reference; name
  // requests pass through unchanged.
  const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
  return composeNumberRequestLabel(term, item?.translated_description || pd.description || pd.DESCRIPTION || '');
};


const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', MXN: '$', BRL: 'R$',
  ARS: '$', CLP: '$', COP: '$', PEN: 'S/', CAD: '$', AUD: '$',
  ILS: '₪', JPY: '¥', CNY: '¥', INR: '₹', KRW: '₩',
  TRY: '₺', THB: '฿', RUB: '₽', CHF: 'Fr', SEK: 'kr',
  NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  RON: 'lei', ZAR: 'R', SAR: '﷼', AED: 'د.إ', QAR: '﷼',
};
const getCurrencySymbol = (code?: string | null): string =>
  code ? (CURRENCY_SYMBOLS[code] ?? code) : '$';

/** Format a money value for display. Prices are NEVER rounded to whole units —
 *  the source value is shown as-is (spec: don't round the data).
 *
 *  numberFormat:
 *   - 'comma' (US: 1,000.00) → dot is the decimal point; show the value as-is,
 *     grouped with commas.
 *   - 'dot'   (CL/EU: 1.000) → the value's dot is a THOUSANDS separator. Values
 *     reach us in two shapes: a misparsed dot-thousands decimal (7.817 actually
 *     means 7,817 — verified: Valor Venta ÷ stock = value×1000) or the real
 *     amount already (693975). toRealDotAmount recovers the former (×1000) and
 *     leaves the latter untouched — a blanket ×1000 here used to inflate real
 *     integers to 693.975.000. Group with dots. */
const formatPrice = (num: number, numberFormat: string = 'comma'): string => {
  if (numberFormat === 'dot') {
    return toRealDotAmount(num).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

interface PriceOption {
  idx: number;
  type: 'retail' | 'wholesale';
  price: number;
  label: string;
}

const getPriceOptions = (pricingArr: any[], t: any, numberFormat: string = 'comma'): PriceOption[] => {
  const options: PriceOption[] = [];
  pricingArr.forEach((entry, idx) => {
    // numberFormat normalizes dot-format entries to REAL amounts (see parsePrice)
    // so option prices agree with getIntegrationPrice and formatPrice.
    const retail = parsePrice(entry.retail_price ?? entry.price, numberFormat);
    const wholesale = parsePrice(entry.wholesale_price, numberFormat);

    const supplierStr = entry.supplier || entry.vendor || entry.source || entry.description || '';
    const brandStr = entry.brand ? ` · ${entry.brand}` : '';
    const prefix = supplierStr ? `${supplierStr}${brandStr}` : `${t('cart.sidePanel.pricing.option', 'Option')} ${idx + 1}${brandStr}`;

    if (retail !== null) {
      options.push({
        idx,
        type: 'retail',
        price: retail,
        label: `${prefix} (${t('cart.sidePanel.retail', 'Retail')})`
      });
    }
    if (wholesale !== null) {
      options.push({
        idx,
        type: 'wholesale',
        price: wholesale,
        label: `${prefix} (${t('cart.sidePanel.wholesale', 'Wholesale')})`
      });
    }
  });
  return options;
};

/* Inventory availability business logic (types, deriveAvailability,
 * getIntegrationData/getIntegrationPrice, selectDmsGroup, resolveChipStatus,
 * parse helpers, formatCheckedAt) lives in the decoupled, unit-tested module
 * src/utils/inventoryAvailability.ts — imported at the top of this file. */

// Stock status chip palette (design spec §3). `chipBorder` is the dashed outline
// the Unknown chip carries in addition to its tint. `dot` colours the status dot.
const STATUS_STYLES: Record<StockStatus, { text: string; bg: string; dot: string; chipBorder?: string }> = {
  in_stock: { text: '#137F67', bg: '#C7FFE8', dot: '#137F67' },
  unknown: { text: '#B45309', bg: '#FEF3C7', dot: '#B45309', chipBorder: '#F59E0B' },
  out_of_stock: { text: '#B91C1C', bg: '#FEE2E2', dot: '#B91C1C' },
};

/** 6px status dot (design spec §3) — color is never the only signal:
 *  filled = stock available, hollow SOLID border = confirmed zero,
 *  hollow DASHED border = unverified. */
const StatusDot: React.FC<{ status: StockStatus; color: string }> = ({ status, color }) => {
  const base: React.CSSProperties = { width: 6, height: 6, borderRadius: '50%', flexShrink: 0, display: 'inline-block', boxSizing: 'border-box' };
  if (status === 'in_stock') return <span style={{ ...base, background: color }} />;
  if (status === 'unknown') return <span style={{ ...base, border: `1.5px dashed ${color}` }} />;
  return <span style={{ ...base, border: `1.5px solid ${color}` }} />;
};

interface IntegrationAvailabilityProps {
  view: AvailabilityView;
  t: any;
  variant: 'card' | 'table';
  /** Currently-active DMS line-type (highlighted in the switcher). Falls back to
   *  view.selectedType (the org-configured default) at the call site. */
  selectedType?: string | null;
  /** When provided, renders a per-line new/alternative price switcher; called
   *  with the chosen group so the parent applies + persists it as the line price. */
  onSelectType?: (group: DmsTypeGroup) => void;
  /** True while a switch for this line is being saved — disables the switcher. */
  savingType?: boolean;
  /** Whether the line has a known effective price (manual / pricing_data /
   *  integration). When false, a yellow "Unknown Price" tag is shown. Defaults to
   *  whether integration_data itself carried a price. */
  priceKnown?: boolean;
  /** When provided, renders a "Re-check" button next to the last-checked time
   *  that triggers a fresh availability lookup for this line. */
  onRecheck?: () => void | Promise<void>;
  /** True while a re-check for this line is in flight — spins the button. */
  rechecking?: boolean;
  /** Warehouse / point-of-sale key the user explicitly picked for this line
   *  (null/undefined → the default, i.e. the location the group price came from). */
  selectedLocation?: string | null;
  /** When provided and the active line-type is stocked in ≥2 locations, renders a
   *  point-of-sale picker; called with the chosen location so the parent applies
   *  ITS price (prices can differ per point of sale) and persists the choice. */
  onSelectLocation?: (group: DmsTypeGroup, location: AvailLocation) => void;
  /** Formats a unit price for the picker's per-location price column. */
  formatPriceFn?: (n: number) => string;
}

/** Per-line delivery-time picker (spec §6). A muted "🕒 {n} days" that — when an
 *  onSelect handler + predefined options are supplied (i.e. not read-only) — opens
 *  a dropdown of zone options + a "Custom…" manual day entry. Rendered on EVERY
 *  cart line the feature covers, independent of stock/availability status: it's a
 *  manual delivery promise the aftersales team sets, not a stock-derived estimate. */
const DeliveryPicker: React.FC<{
  days: number;
  options: DeliveryOption[] | null;
  onSelect?: (days: number | null) => void;
  t: any;
}> = ({ days, options, onSelect, t }) => {
  // Menu anchor coords (null = closed). The menu is portaled to <body> with
  // position:fixed so it escapes the cart panel's overflow/transform clipping
  // (same reason the stock-breakdown tooltip portals) — a plain absolute dropdown
  // gets clipped inside the panel / table cell.
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [customDays, setCustomDays] = useState('');
  const daysLabel = (n: number) => n === 1
    ? t('cart.sidePanel.inventory.deliveryOneDay', '1 day')
    : `${n} ${t('cart.sidePanel.inventory.deliveryDays', 'days')}`;
  const clock = (
    <svg width={10} height={10} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" /><path d="M8 4.6V8l2.6 1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const muted: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 500, color: '#999999' };
  const editable = !!onSelect && !!options && options.length > 0;
  // The current value matches a zone option iff a predefined option shares its
  // day count — otherwise the active choice is a "Custom" value.
  const matchedZone = options?.find(o => o.days === days) ?? null;
  const close = () => { setMenuPos(null); setCustomDays(''); };
  const toggle = (e: React.MouseEvent) => {
    if (menuPos) { close(); return; }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ x: r.left, y: r.bottom });
  };
  const commitCustom = () => {
    const n = parseInt(customDays, 10);
    if (!Number.isNaN(n) && n >= 0) { onSelect?.(n); close(); }
  };
  if (!editable) {
    return <span style={muted}>{clock}{daysLabel(days)}</span>;
  }
  return (
    <span style={{ display: 'inline-flex' }}>
      <button
        type="button" aria-haspopup="listbox" aria-expanded={!!menuPos}
        onClick={(e) => { e.stopPropagation(); toggle(e); }}
        title={t('cart.sidePanel.inventory.deliveryPick', 'Set delivery time')}
        style={{ ...muted, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        {clock}
        {daysLabel(days)}
        <svg width={7} height={7} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path d="M3 6l5 5 5-5" /></svg>
      </button>
      {menuPos && createPortal(
        <>
          <span onClick={(e) => { e.stopPropagation(); close(); }} style={{ position: 'fixed', inset: 0, zIndex: 99998 }} />
          <span role="listbox" style={{ position: 'fixed', left: menuPos.x, top: menuPos.y + 5, background: '#fff', border: '0.5px solid rgba(32,30,26,0.12)', borderRadius: '8px', boxShadow: '0 4px 16px rgba(32,30,26,0.14)', minWidth: '200px', padding: '4px', zIndex: 99999, display: 'flex', flexDirection: 'column' }}>
            {options!.map((o) => {
              const sel = matchedZone?.label === o.label && matchedZone?.days === o.days;
              return (
                <span
                  key={`${o.label}-${o.days}`} role="option" aria-selected={sel}
                  onClick={(e) => { e.stopPropagation(); onSelect!(o.days); close(); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '7px 9px', borderRadius: '6px', cursor: 'pointer', background: sel ? '#EEF0F3' : 'transparent' }}
                >
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#201E1A' }}>{o.label}</span>
                  <span style={{ fontSize: '11px', color: '#6b6b6b' }}>{daysLabel(o.days)}</span>
                </span>
              );
            })}
            {/* Custom manual entry */}
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 9px', borderTop: '0.5px solid rgba(32,30,26,0.08)', marginTop: '2px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: matchedZone ? '#201E1A' : '#1E0DB8' }}>{t('cart.sidePanel.inventory.deliveryCustom', 'Custom')}</span>
              <input
                type="number" min={0} autoFocus
                value={customDays}
                placeholder={matchedZone ? '' : String(days)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setCustomDays(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitCustom(); } }}
                style={{ width: '48px', fontSize: '11px', padding: '3px 5px', border: '0.5px solid rgba(32,30,26,0.2)', borderRadius: '5px', textAlign: 'right' }}
              />
              <span style={{ fontSize: '11px', color: '#6b6b6b' }}>{t('cart.sidePanel.inventory.deliveryDays', 'days')}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); commitCustom(); }}
                style={{ fontSize: '11px', fontWeight: 700, color: '#1E0DB8', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
              >{t('common.apply', 'Apply')}</button>
            </span>
          </span>
        </>,
        document.body,
      )}
    </span>
  );
};

/** Availability row: a colored status pill (In Stock / Unknown / Out of Stock ·
 *  remark) + an info icon opening the per-location breakdown. The supplier/DMS
 *  SKU lives INSIDE the ⓘ breakdown (following the selected line-type), not on
 *  the row itself. Rendered the same in both the table and card views. */
const IntegrationAvailability: React.FC<IntegrationAvailabilityProps> = ({ view, t, selectedType, onSelectType, savingType, priceKnown, onRecheck, rechecking, selectedLocation, onSelectLocation, formatPriceFn }) => {
  // Viewport-anchored coords for the breakdown popup. `position: fixed` (portaled
  // to <body>) escapes the panel's `transform`/`overflow` clipping.
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  // Org setting: importer stock as a unit count instead of In / Out of Stock.
  const showImporterQty = (useAuth().currentUser as any)?.inventory_show_importer_qty === true;
  // Part-type (OE / Alternative) variant-switcher dropdown open state (spec §4.1).
  // Part-type switcher menu anchor coords (null = closed). Portaled to <body> with
  // position:fixed so it escapes the cart panel's overflow/transform clipping —
  // a plain absolute dropdown was rendering behind / clipped by the panel.
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  // Point-of-sale (warehouse) picker menu anchor coords (null = closed).
  const [locMenuPos, setLocMenuPos] = useState<{ x: number; y: number } | null>(null);
  const openTip = (e: React.MouseEvent) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.top });
  };
  const closeTip = () => setTip(null);

  // ── Per-type availability ────────────────────────────────────────────────
  // The chip reflects the SELECTED OE/Alternative line-type's OWN stock, not a
  // global aggregate across types, resolved through the shared spec cascade
  // (resolveChipStatus). Presence (missing vs empty vs present) decides Unknown
  // vs Out-of-Stock per the Inventory Logic spec: a source that is simply not
  // integrated is ignored, an "empty" (part-not-found) source is Unknown rather
  // than a confirmed zero, and stock anywhere → In Stock.
  const dmsGroups = view.source === 'dms' ? view.dmsGroups : [];
  const activeType = selectedType ?? view.selectedType;
  const activeGroup = dmsGroups.find(g => g.type === activeType) ?? null;
  // Warehouses / points of sale of the active type. Once the user EXPLICITLY picks
  // one, the line ships (and is priced) from it, so the chip reflects that
  // location's stock rather than the national total.
  const locOpts = locationOptions(activeGroup);
  const explicitLoc = selectedLocation ? (locOpts.find(o => o.key === selectedLocation) ?? null) : null;
  const curLoc = activeLocation(activeGroup, selectedLocation);
  // Price and stock come from ONE warehouse whenever the line is priced from one:
  // the explicit choice, else the default the org's warehouse policy resolved.
  // Under the legacy 'list_order' policy (no default resolved) the chip keeps
  // showing the line-type's national total, exactly as before.
  const pricedLoc = explicitLoc ?? (activeGroup?.defaultKey ? curLoc : null);
  const activeQty = pricedLoc ? pricedLoc.qty : activeGroup ? activeGroup.totalQty : null;   // null → no rows / unknown
  // Why the default warehouse was chosen + how the member's home warehouse stands
  // (null when the org has no home warehouse configured for this member).
  const defaultReason = !explicitLoc ? (activeGroup?.defaultReason ?? null) : null;
  const homeStatus = activeGroup?.homeStatus ?? null;
  // The member's home city (or, for a legacy single home warehouse, that
  // warehouse's label) — shown in "Not in {home}".
  const homeKeys = activeGroup?.homeKeys ?? [];
  const homeLabel = activeGroup?.homeLabel
    || (homeKeys.length ? (locOpts.find(o => o.key.toUpperCase() === homeKeys[0].toUpperCase())?.label || homeKeys[0]) : null);
  // The importer only stocks the automaker OE part, so it reinforces stock for
  // the 'genuine' line-type only. For OEM / Aftermarket (or when that type is
  // selected) the chip + breakdown reflect that type's OWN DMS stock/price and
  // the importer is ignored. An importer-only part (no DMS type) reads as genuine.
  const importerApplies = (activeType ?? 'genuine') === 'genuine';
  const { status: chipStatus, remarkKind: chipRemarkKind }: { status: StockStatus; remarkKind: StockRemark } =
    resolveChipStatus({
      dmsPresence: view.dmsPresence,
      activeQty,
      importerPresence: importerApplies ? view.importerPresence : 'missing',
      importerInStock: importerApplies ? view.importerInStock : null,
    });
  // Winning warehouse (single) for the ACTIVE type only, so "In Stock · {wh}" never
  // names a location that is zero for the selected line-type.
  const activeInStockNames = activeGroup
    ? Array.from(new Set(activeGroup.locations.filter(l => (l.qty ?? 0) > 0).map(l => l.label).filter(Boolean)))
    : [];
  const chipRemarkWarehouse = chipRemarkKind !== 'warehouse' ? null
    : pricedLoc ? (pricedLoc.label || pricedLoc.key || null)
      : activeInStockNames.length === 1 ? activeInStockNames[0] : null;
  // Supplier SKU follows the selected line-type (each type has its own SKU); falls
  // back to the view-level SKU (importer / OE) when the active group carries none.
  // Shown inside the ⓘ breakdown, so switching OE/Alternative swaps it there.
  const displaySku = supplierSkuForType(view, selectedType);

  const s = STATUS_STYLES[chipStatus];
  const statusLabel = chipStatus === 'in_stock'
    ? t('cart.sidePanel.inventory.inStock', 'In Stock')
    : chipStatus === 'unknown'
      ? t('cart.sidePanel.inventory.unknownStock', 'Unknown Stock')
      : t('cart.sidePanel.inventory.outOfStock', 'Out of Stock');
  // Price tag: a separate "Unknown Price" pill when the line has no known price.
  const priceUnknown = priceKnown !== undefined ? !priceKnown : view.price === null;

  // The DMS was queried and answered with an EMPTY list — it does not carry this
  // part number at all (distinct from 'missing', where no DMS is integrated).
  // Without a note the line reads as a bare "Unknown Stock" and, when no importer
  // is integrated either, carries no ⓘ — so nothing on screen says the part was
  // looked up and not found. Stays quiet when the importer returned a real row:
  // that concrete position is the more useful answer, and an importer-only org
  // must never read a note about a DMS it does not use.
  const dmsNotFound = view.dmsPresence === 'empty'
    && !(importerApplies && view.importerPresence === 'present');
  const partNotFoundLbl = t('cart.sidePanel.inventory.partNotFound', 'Part not found');

  // Fills the remark slot only when nothing more specific claimed it, so a
  // positive result from another source ("In Stock · Importer") is never
  // overwritten. The unknown-stock/unknown-price REASONS (not connected vs part
  // not found) live inside the ⓘ breakdown, not inline — the chips stay short.
  const remark =
    chipRemarkKind === 'warehouse' ? chipRemarkWarehouse
      : chipRemarkKind === 'importer' ? t('cart.sidePanel.inventory.importer', 'Importer')
        : chipRemarkKind === 'retailer_oos' ? t('cart.sidePanel.inventory.retailerOutOfStock', 'Retailer Out of Stock')
          : null;

  // Why the chips read Unknown — surfaced under the ⓘ (null-vs-empty per spec):
  //   stock: dms_data null → the DMS stock feed isn't connected; empty → the DMS
  //          was queried and does not carry this part.
  //   price: empty → same part-not-found; null (feed absent, or rows carry no
  //          price) → pricing isn't connected.
  const stockReason = chipStatus !== 'unknown' ? null
    : view.dmsPresence === 'missing' && !(importerApplies && view.importerPresence === 'present')
      ? t('cart.sidePanel.inventory.stockNotConnected', 'Stock not connected')
      : null; // 'empty' is reported by the Local DMS "Part not found" row below
  const priceReason = !priceUnknown ? null
    : dmsNotFound ? partNotFoundLbl
      : t('cart.sidePanel.inventory.priceNotConnected', 'Pricing not connected');

  // Show the ⓘ breakdown whenever there's something to report: the retailer was
  // actually queried (rows returned, or an empty "not found" answer), OR the
  // importer was actually checked (present or empty — an empty importer surfaces
  // as "Not found in ERP" in the tooltip). A source that is simply not integrated
  // (missing) contributes nothing to reveal. The importer only counts when it
  // applies to the selected type (OE), not for Alternative.
  // A SKU also counts: the ⓘ is now its only home, so it must stay reachable.
  // Unknown-stock/unknown-price reasons also live there, so they force the ⓘ too.
  const hasDetail = view.dmsPresence !== 'missing' || (importerApplies && view.importerPresence !== 'missing') || !!displaySku
    || !!stockReason || !!priceReason;
  const stockLbl = t('cart.sidePanel.inventory.stockLabel', 'Stock');
  const noData = t('cart.sidePanel.inventory.noStockData', 'No data');
  const unitStr = (q: number) => `${q} ${q === 1 ? t('cart.sidePanel.inventory.unit', 'unit') : t('cart.sidePanel.inventory.units', 'units')}`;

  // ── Info tooltip: "Stock by location" (spec §5) — one row per retailer
  // warehouse (positive → white "N units", confirmed zero → grey "0", unknown →
  // amber "No data"), then the importer level (In Stock / Out of Stock / No data —
  // the importer never exposes a count). Stock only; no prices. ──
  // Locations for the ACTIVE line-type only — keeps the breakdown consistent with
  // the per-type chip (selecting Alternative shows Alternative's warehouses).
  const locMap = new Map<string, number | null>();
  for (const l of (activeGroup ? activeGroup.locations : [])) {
    const key = (l.label || stockLbl) + (explicitLoc && l.key === explicitLoc.key ? ' ✓' : '');
    if (!locMap.has(key) || (locMap.get(key) === null && l.qty !== null)) locMap.set(key, l.qty);
  }
  const tipQty = (q: number | null) => q === null
    ? { text: noData, color: '#F59E0B', weight: 700 }
    : q === 0 ? { text: '0', color: '#B8B6B0', weight: 500 }
      : { text: unitStr(q), color: '#FFFFFF', weight: 700 };
  const tipRow = (label: string, v: { text: string; color: string; weight: number }, key?: string) => (
    <span key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', padding: '2px 0' }}>
      <span style={{ color: '#D6D4CF' }}>{label}</span>
      <span style={{ fontWeight: v.weight, color: v.color }}>{v.text}</span>
    </span>
  );

  // Importer reports availability only ("In Stock" / "Out of Stock"), deriving it
  // from the qty when the ERP gave one — unless the org opts into the count.
  const importerLbl = t('cart.sidePanel.inventory.importer', 'Importer');
  const importerStockTip = (v: number | boolean | null) => {
    if (showImporterQty && typeof v === 'number') return tipQty(v);
    const inStock = typeof v === 'number' ? v > 0 : v;
    return inStock == null
      ? { text: noData, color: '#F59E0B', weight: 700 }
      : inStock
        ? { text: t('cart.sidePanel.inventory.inStock', 'In Stock'), color: '#FFFFFF', weight: 700 }
        : { text: t('cart.sidePanel.inventory.outOfStock', 'Out of Stock'), color: '#B8B6B0', weight: 500 };
  };

  const popupBody = (
    <>
      {Array.from(locMap.entries()).map(([label, q], i) => tipRow(label, tipQty(q), `l-${i}`))}
      {/* DMS queried, part not in its catalogue — otherwise this breakdown would
          open with no rows at all (there are no warehouses to list). */}
      {dmsNotFound && tipRow(
        t('cart.sidePanel.inventory.localDms', 'Local stock'),
        { text: partNotFoundLbl, color: '#F59E0B', weight: 700 },
        'dms-not-found',
      )}
      {importerApplies && view.importerPresence === 'empty' && tipRow(
        importerLbl,
        { text: t('cart.sidePanel.inventory.notFoundInErp', 'Not found in ERP'), color: '#F59E0B', weight: 700 },
      )}
      {/* One line per importer warehouse when the ERP breaks stock down by
          location, else the single "Importer" line. */}
      {importerApplies && view.importerPresence === 'present' && (view.importerLocations.length
        ? view.importerLocations.map((l, i) => tipRow(`${importerLbl} · ${l.label}`, importerStockTip(l.qty), `imp-${i}`))
        : tipRow(importerLbl, importerStockTip(showImporterQty && view.importerQty != null ? view.importerQty : view.importerInStock ?? (view.importerQty != null ? view.importerQty > 0 : null))))}
    </>
  );

  const breakdownPopup = tip && hasDetail ? createPortal(
    <div style={{
      position: 'fixed', left: tip.x, top: tip.y - 8, transform: 'translate(-50%, -100%)',
      zIndex: 99999, background: '#201E1A', color: '#fff', padding: '9px 12px', borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(32,30,26,0.18)', fontSize: '11px', lineHeight: 1.5,
      whiteSpace: 'nowrap', textAlign: 'left', pointerEvents: 'none',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Supplier/DMS SKU for the SELECTED line-type — swaps when the user picks
          another part type (OE / Alternative) from the switcher. */}
      {displaySku && (
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '18px', marginBottom: '5px', paddingBottom: '5px', borderBottom: '0.5px solid rgba(255,255,255,0.18)' }}>
          <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#B8B6B0' }}>
            {t('cart.sidePanel.inventory.supplierSku', 'Supplier SKU')}
          </span>
          <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '11px', fontWeight: 700, color: '#A9B2FF' }}>{displaySku}</span>
        </span>
      )}
      <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#B8B6B0', marginBottom: '5px' }}>
        {t('cart.sidePanel.inventory.stockByLocation', 'Stock by location')}
      </span>
      {popupBody}
      {/* Unknown-stock explainer — why the chip says Unknown rather than a count.
          Suppressed when the DMS miss is the ONLY signal (the row above already
          says it precisely) and when the feed is simply not connected (the
          stock-reason line below owns that story). */}
      {chipStatus === 'unknown' && !stockReason && !(dmsNotFound && (!importerApplies || view.importerPresence === 'missing')) && (
        <span style={{ marginTop: '5px', paddingTop: '5px', borderTop: '0.5px solid rgba(255,255,255,0.18)', fontSize: '10px', fontWeight: 600, color: '#F59E0B' }}>
          {t('cart.sidePanel.inventory.unknownStockHint', 'Stock not recognized — not found in ERP')}
        </span>
      )}
      {/* Unknown-chip reasons (null vs empty): stock feed not connected / price
          not found vs pricing not connected — the chips themselves stay short. */}
      {(stockReason || priceReason) && (
        <span style={{ marginTop: '5px', paddingTop: '5px', borderTop: '0.5px solid rgba(255,255,255,0.18)', display: 'flex', flexDirection: 'column' }}>
          {stockReason && tipRow(stockLbl, { text: stockReason, color: '#F59E0B', weight: 700 }, 'stock-reason')}
          {priceReason && tipRow(t('cart.sidePanel.inventory.priceLabel', 'Price'), { text: priceReason, color: '#F59E0B', weight: 700 }, 'price-reason')}
        </span>
      )}
    </div>,
    document.body,
  ) : null;

  // ── Part-type tag / variant switcher (spec §4) — OE (brand) vs Alternative
  // (neutral), positioned between the SKU and the status chip. A single option is
  // a static tag; ≥2 PRICED DMS line-types become a dropdown (+N badge + chevron)
  // whose options each show the type tag, SKU and a stock indicator, and which on
  // select applies that line-type's price (spec §4.1). ──
  const pricedGroups = dmsGroups.filter(g => g.price !== null);
  // Three data-driven part-types: Genuine (automaker OE, brand blue), OEM
  // (equipment-maker, teal), Aftermarket (third-party, neutral). Unknown types
  // fall back to the aftermarket style.
  const partTypeMeta = (ty: string) => ty === 'genuine'
    ? { label: t('cart.sidePanel.inventory.genuine', 'Genuine'), bg: '#E5E2FF', fg: '#3627BF' }
    : ty === 'oem'
      ? { label: t('cart.sidePanel.inventory.oem', 'OEM'), bg: '#D6ECE6', fg: '#0F766E' }
      : { label: t('cart.sidePanel.inventory.aftermarket', 'Aftermarket'), bg: '#E0DEED', fg: '#696682' };
  // Single static tag. The cart line is a catalog OE part, so it reads GENUINE
  // unless the DMS explicitly types it otherwise: importer part → Genuine; DMS
  // with line-type(s) → the active/first type; NO type data at all (part not in
  // DMS, feed not connected, unknown stock/price) → still Genuine — missing
  // integration data doesn't make the automaker part any less genuine, and the
  // tag must always show so same-looking rows never differ only by data luck.
  const singleTagType = view.source === 'importer'
    ? 'genuine'
    : (dmsGroups.length >= 1 ? (activeType ?? dmsGroups[0].type) : 'genuine');
  // Switcher shows whenever there are ≥2 line-types to offer (incl. synthesized
  // empty ones), so OE + Alternative are always selectable even when a type has
  // no rows (it renders as "Not in Stock").
  const isSwitcher = !!onSelectType && dmsGroups.length >= 2;
  const ptagBase: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
    borderRadius: '6px', padding: '2px 6px', whiteSpace: 'nowrap', lineHeight: 1.6, border: 'none',
  };
  // Per-line-type availability indicator for the dropdown menu (5px dot + label).
  // Mirrors the chip's per-type resolution so each option reflects its TRUE
  // status — including the importer for the OE line-type (so OE-via-importer reads
  // "In Stock", not "Out of Stock" from its empty DMS stock).
  const groupAvail = (g: DmsTypeGroup) => {
    // The importer stocks the automaker OE part, so it only reinforces the
    // 'genuine' line-type's availability (not OEM / aftermarket).
    const applies = g.type === 'genuine';
    const { status } = resolveChipStatus({
      dmsPresence: view.dmsPresence,
      activeQty: g.totalQty,
      importerPresence: applies ? view.importerPresence : 'missing',
      importerInStock: applies ? view.importerInStock : null,
    });
    return status === 'in_stock'
      ? { label: t('cart.sidePanel.inventory.inStock', 'In Stock'), color: '#137F67' }
      : status === 'out_of_stock'
        ? { label: t('cart.sidePanel.inventory.outOfStock', 'Out of Stock'), color: '#B91C1C' }
        : { label: t('cart.sidePanel.inventory.unknownStock', 'Unknown Stock'), color: '#B45309' };
  };
  const activeMeta = partTypeMeta(activeType ?? pricedGroups[0]?.type ?? 'genuine');

  const partTypeEl = isSwitcher ? (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button" aria-haspopup="listbox" aria-expanded={!!menuPos} disabled={savingType}
        onClick={(e) => {
          e.stopPropagation();
          if (menuPos) { setMenuPos(null); return; }
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setMenuPos({ x: r.left, y: r.bottom });
        }}
        style={{ ...ptagBase, background: activeMeta.bg, color: activeMeta.fg, cursor: savingType ? 'default' : 'pointer', opacity: savingType ? 0.6 : 1 }}
      >
        {activeMeta.label}
        {dmsGroups.length > 1 && (
          <span style={{ background: '#fff', borderRadius: '4px', padding: '0 4px', fontSize: '8px', lineHeight: '12px', color: activeMeta.fg }}>+{dmsGroups.length - 1}</span>
        )}
        <svg width={8} height={8} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M3 6l5 5 5-5" /></svg>
      </button>
      {menuPos && createPortal(
        <>
          <span onClick={(e) => { e.stopPropagation(); setMenuPos(null); }} style={{ position: 'fixed', inset: 0, zIndex: 99998 }} />
          <span role="listbox" style={{ position: 'fixed', left: menuPos.x, top: menuPos.y + 5, background: '#fff', border: '0.5px solid rgba(32,30,26,0.12)', borderRadius: '8px', boxShadow: '0 4px 16px rgba(32,30,26,0.14)', minWidth: '235px', padding: '4px', zIndex: 99999, display: 'flex', flexDirection: 'column' }}>
            {dmsGroups.map((g) => {
              const meta = partTypeMeta(g.type); const av = groupAvail(g); const sel = g.type === activeType;
              return (
                <span
                  key={g.type} role="option" aria-selected={sel}
                  onClick={(e) => { e.stopPropagation(); onSelectType!(g); setMenuPos(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 9px', borderRadius: '6px', cursor: 'pointer', background: sel ? '#DBD8FF' : 'transparent' }}
                >
                  <span style={{ ...ptagBase, background: meta.bg, color: meta.fg }}>{meta.label}</span>
                  {(g.sku ?? view.supplierSku) && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '11px', fontWeight: 700, color: '#201E1A', flex: 1 }}>{g.sku ?? view.supplierSku}</span>}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9px', fontWeight: 700, color: av.color }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: av.color }} />{av.label}
                  </span>
                </span>
              );
            })}
          </span>
        </>,
        document.body,
      )}
    </span>
  ) : singleTagType ? (
    (() => { const m = partTypeMeta(singleTagType); return <span style={{ ...ptagBase, background: m.bg, color: m.fg }}>{m.label}</span>; })()
  ) : null;

  // ── Point-of-sale (warehouse) picker ── The same part is often priced
  // differently per point of sale, so when the active line-type is stocked in ≥2
  // locations the user picks WHERE to buy from; each option shows that location's
  // stock and its own price, and choosing one re-prices the line. Read-only views
  // show only an explicit choice, as a static tag.
  const pinIcon = (
    <svg width={9} height={9} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7} aria-hidden="true">
      <path d="M8 14.5s-4.5-4.2-4.5-7.6a4.5 4.5 0 0 1 9 0c0 3.4-4.5 7.6-4.5 7.6z" strokeLinejoin="round" /><circle cx="8" cy="6.9" r="1.5" />
    </svg>
  );
  const locTagStyle: React.CSSProperties = {
    ...ptagBase, textTransform: 'none', letterSpacing: 0, fontSize: '10px', fontWeight: 600,
    background: '#F1F0EC', color: '#3D3A33', maxWidth: '160px',
  };
  const fmtLocPrice = (n: number | null) => n === null ? '—' : (formatPriceFn ? formatPriceFn(n) : String(n));
  const locQtyLabel = (q: number | null) => q === null
    ? { text: noData, color: '#B45309' }
    : q > 0 ? { text: unitStr(q), color: '#137F67' } : { text: t('cart.sidePanel.inventory.outOfStock', 'Out of Stock'), color: '#B91C1C' };
  const canPickLoc = !!onSelectLocation && !!activeGroup && locOpts.length >= 2;
  const locOptsOrdered = orderedLocationOptions(activeGroup);
  const isHomeLoc = (o: AvailLocation) => isHomeLocation(activeGroup, o);
  // Tooltip on the point-of-sale tag: why this warehouse prices the line.
  const locReasonTitle = explicitLoc
    ? t('cart.sidePanel.inventory.pointOfSaleChosen', 'Point of sale — your choice')
    : defaultReason === 'home' ? t('cart.sidePanel.inventory.pointOfSaleHome', 'Your home warehouse')
      : defaultReason === 'lowest' ? t('cart.sidePanel.inventory.pointOfSaleLowest', 'Lowest in-stock price{{home}}', { home: homeStatus && homeStatus !== 'in_stock' ? ' — ' + t('cart.sidePanel.inventory.noneAtHome', 'none in your home warehouse') : '' })
        : defaultReason === 'first' ? t('cart.sidePanel.inventory.pointOfSaleFirst', 'First warehouse with stock')
          : t('cart.sidePanel.inventory.pointOfSale', 'Point of sale');
  // Flag when the part is not available in the member's own city.
  const notAtHome = !!homeLabel && (homeStatus === 'out_of_stock' || homeStatus === 'absent');
  const locationEl = canPickLoc && curLoc ? (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button" aria-haspopup="listbox" aria-expanded={!!locMenuPos} disabled={savingType}
        title={locReasonTitle}
        onClick={(e) => {
          e.stopPropagation();
          if (locMenuPos) { setLocMenuPos(null); return; }
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setLocMenuPos({ x: r.left, y: r.bottom });
        }}
        style={{ ...locTagStyle, cursor: savingType ? 'default' : 'pointer', opacity: savingType ? 0.6 : 1, border: explicitLoc ? '1px solid #C9C6BE' : '1px dashed #C9C6BE' }}
      >
        {pinIcon}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{curLoc.label || curLoc.key}</span>
        <span style={{ background: '#fff', borderRadius: '4px', padding: '0 4px', fontSize: '8px', lineHeight: '12px' }}>{locOpts.length}</span>
        <svg width={8} height={8} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M3 6l5 5 5-5" /></svg>
      </button>
      {locMenuPos && createPortal(
        <>
          <span onClick={(e) => { e.stopPropagation(); setLocMenuPos(null); }} style={{ position: 'fixed', inset: 0, zIndex: 99998 }} />
          <span role="listbox" style={{ position: 'fixed', left: locMenuPos.x, top: locMenuPos.y + 5, background: '#fff', border: '0.5px solid rgba(32,30,26,0.12)', borderRadius: '8px', boxShadow: '0 4px 16px rgba(32,30,26,0.14)', minWidth: '260px', maxHeight: '320px', overflowY: 'auto', padding: '4px', zIndex: 99999, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#999999', padding: '5px 9px 4px' }}>
              {t('cart.sidePanel.inventory.choosePointOfSale', 'Choose point of sale')}
            </span>
            {locOptsOrdered.map((o) => {
              const sel = o.key === curLoc.key;
              const q = locQtyLabel(o.qty);
              const p = priceForLocation(activeGroup!, o);
              const home = isHomeLoc(o);
              return (
                <span
                  key={o.key} role="option" aria-selected={sel}
                  onClick={(e) => { e.stopPropagation(); onSelectLocation!(activeGroup!, o); setLocMenuPos(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 9px', borderRadius: '6px', cursor: 'pointer', background: sel ? '#DBD8FF' : 'transparent' }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#201E1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.label || o.key}
                      {home && <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: '#4B48C7', background: '#EDEDFF', borderRadius: '4px', padding: '0 4px' }}>{t('cart.sidePanel.inventory.homeBadge', 'Home')}</span>}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9px', fontWeight: 700, color: q.color }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: q.color }} />{q.text}
                    </span>
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: p === null ? '#B8B6B0' : '#201E1A', whiteSpace: 'nowrap' }}>{fmtLocPrice(p)}</span>
                </span>
              );
            })}
          </span>
        </>,
        document.body,
      )}
    </span>
  ) : explicitLoc ? (
    <span style={locTagStyle} title={t('cart.sidePanel.inventory.pointOfSale', 'Point of sale')}>
      {pinIcon}<span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{explicitLoc.label || explicitLoc.key}</span>
    </span>
  ) : null;

  // Status chip (spec §3): tint bg + status dot + bold label + regular remark.
  const chip = (status: StockStatus, style: typeof s, label: string, rm: string | null) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '6px', background: style.bg, border: style.chipBorder ? `1px dashed ${style.chipBorder}` : undefined, lineHeight: 1.4, whiteSpace: 'nowrap', maxWidth: '100%' }}>
      <StatusDot status={status} color={style.dot} />
      <span style={{ fontSize: '10px', fontWeight: 700, color: style.text }}>{label}</span>
      {rm && <span style={{ fontSize: '10px', fontWeight: 500, opacity: 0.85, color: style.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>&nbsp;·&nbsp;{rm}</span>}
    </span>
  );

  // Relative "checked N ago" label from integration_data.time (null when absent).
  const checkedLabel = formatCheckedAt(view.checkedAt);

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px 6px', marginTop: '4px', overflowWrap: 'normal', wordBreak: 'normal' }}>
      {/* Part type tag / variant switcher (the supplier SKU moved into the ⓘ breakdown) */}
      {partTypeEl}
      {/* Point-of-sale picker (price follows the chosen warehouse) */}
      {locationEl}
      {/* Status chip */}
      {chip(chipStatus, s, statusLabel, remark)}
      {/* Part is not available in the member's own city — the default moved elsewhere */}
      {notAtHome && (
        <span
          title={homeStatus === 'absent'
            ? t('cart.sidePanel.inventory.notAtHomeAbsentTitle', 'Your home warehouse ({{home}}) does not carry this part', { home: homeLabel })
            : t('cart.sidePanel.inventory.notAtHomeTitle', 'Out of stock in your home warehouse ({{home}})', { home: homeLabel })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: '#FFF7E6', color: '#8A5A00', fontSize: '10px', fontWeight: 700, lineHeight: 1.4, whiteSpace: 'nowrap' }}
        >
          {t('cart.sidePanel.inventory.notAtHome', 'Not in {{home}}', { home: homeLabel })}
        </span>
      )}
      {/* Separate "Unknown Price" chip — only when the line has no known price */}
      {priceUnknown && chip('unknown', STATUS_STYLES.unknown, t('cart.sidePanel.inventory.unknownPrice', 'Unknown Price'), null)}
      {/* Info icon → per-location stock tooltip */}
      {hasDetail && (
        <span
          onMouseEnter={openTip}
          onMouseLeave={closeTip}
          onClick={(e) => { e.stopPropagation(); tip ? closeTip() : openTip(e); }}
          tabIndex={0}
          role="button"
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
          aria-label={t('cart.sidePanel.inventory.stockByLocation', 'Stock by location')}
        >
          <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="#999999" strokeWidth={1.5} aria-hidden="true">
            <circle cx="8" cy="8" r="6.5" /><line x1="8" y1="7.2" x2="8" y2="11.2" /><circle cx="8" cy="4.7" r="0.4" fill="#999999" />
          </svg>
          {breakdownPopup}
        </span>
      )}
      {/* Importer status is reported inside the ⓘ breakdown (e.g. "Not found in
          ERP"), not as an inline chip — so the row stays compact. */}
      {/* Last-checked time (integration_data.time / .datetime) + re-check button. */}
      {(checkedLabel || onRecheck) && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: '#999999', whiteSpace: 'nowrap' }}>
          {checkedLabel && (
            <span title={view.checkedAt ? new Date(view.checkedAt).toLocaleString() : undefined}>
              {t('cart.sidePanel.inventory.checkedAgo', { defaultValue: 'checked {{when}}', when: checkedLabel })}
            </span>
          )}
          {onRecheck && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (!rechecking) onRecheck(); }}
              disabled={rechecking}
              title={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
              aria-label={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', padding: 0, cursor: rechecking ? 'default' : 'pointer', color: '#1E0DB8', fontWeight: 700, fontSize: '10px', opacity: rechecking ? 0.6 : 1 }}
            >
              {rechecking
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                  <path d="M13.6 8a5.6 5.6 0 1 1-1.7-4" strokeLinecap="round" />
                  <path d="M13.8 2.5V5.2H11.1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>}
              {rechecking
                ? t('cart.sidePanel.inventory.checking', 'Checking...')
                : t('cart.sidePanel.inventory.recheckShort', 'Re-check')}
            </button>
          )}
        </span>
      )}
    </div>
  );
};

const CartSidePanel: React.FC<CartSidePanelProps> = ({
  cartItems,
  onRemovePart,
  onUpdateQuantity,
  onMagicEdit,
  onLaborUpdate,
  onPriceUpdate,
  onDeliveryUpdate,
  onDescriptionUpdate,
  onNoteUpdate,
  onLineDiscountUpdate,
  onEstimateRepairs,
  onOrderSubmitted,
  onRefreshItems,
  onRecheckInventory,
  onWorkflowLog,
  isMobileOpen = false,
  onMobileClose,
  readOnly = false,
  onMinimizedChange,
}) => {

  const isMobile = window.innerWidth < 768;
  const { t } = useTranslation();
  // Start expanded whenever the cart already has items (on desktop it otherwise
  // starts minimized); mobile always starts expanded. Items added later are
  // handled by the auto-expand effect below.
  const [isMinimized, setIsMinimized] = useState(!isMobile && cartItems.length === 0);
  const [prevCartLength, setPrevCartLength] = useState(cartItems.length);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [magicEditPart, setMagicEditPart] = useState<any>(null);
  const [laborEditPart, setLaborEditPart] = useState<any>(null);
  const [priceEditItem, setPriceEditItem] = useState<any>(null);
  const [priceEditValue, setPriceEditValue] = useState<string>('');
  const [descEditItem, setDescEditItem] = useState<any>(null);
  const [descEditValue, setDescEditValue] = useState<string>('');
  // Per-item note editing state (structured_data.part_data.note)
  const [noteEditItemKey, setNoteEditItemKey] = useState<string | null>(null);
  const [noteEditValue, setNoteEditValue] = useState<string>('');
  const [noteSavingItemKey, setNoteSavingItemKey] = useState<string | null>(null);
  // Per-item manual line discount editing state
  const [lineDiscountEditKey, setLineDiscountEditKey] = useState<string | null>(null);
  const [lineDiscountEditValue, setLineDiscountEditValue] = useState<string>('0');
  const [lineDiscountEditType, setLineDiscountEditType] = useState<'percentage' | 'fixed'>('percentage');
  // Table view: which row's inline editor is expanded, plus its draft field values
  const [tableEditKey, setTableEditKey] = useState<string | null>(null);
  const [tableEditPrice, setTableEditPrice] = useState<string>('');
  const [tableEditDiscVal, setTableEditDiscVal] = useState<string>('');
  const [tableEditDiscType, setTableEditDiscType] = useState<'percentage' | 'fixed'>('percentage');
  const [tableEditDesc, setTableEditDesc] = useState<string>('');
  const [lineDiscountSavingKey, setLineDiscountSavingKey] = useState<string | null>(null); const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  // Tracks prices the user has set manually (itemKey → price) so the summary
  // and submit button re-enable immediately without waiting for a server round-trip.
  const [manualPrices, setManualPrices] = useState<Record<string, number | null>>({});
  const [showShareMenu, setShowShareMenu] = useState(false);
  // Cart list display: rich cards (default) vs a compact spreadsheet-style table.
  // Default to cards on mobile (the table is too wide for a phone) and the
  // compact table on desktop. The user can still switch either way.
  const [cartViewMode, setCartViewMode] = useState<'cards' | 'table'>(isMobile ? 'cards' : 'table');
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  // Tracks which pricing_data option the user selected for items with multiple options
  const [selectedPricingIndices, setSelectedPricingIndices] = useState<Record<string, string>>({});
  // Tracks which DMS price line-type ('original' | 'alternative') the user switched a
  // line to (itemKey → type). Highlights the switcher; the chosen price is also
  // written into manualPrices so it flows into the quote total + persists.
  const [selectedDmsType, setSelectedDmsType] = useState<Record<string, string>>({});
  // Warehouse / point of sale the user priced a line from (itemKey → location key,
  // null = cleared back to default). Like selectedDmsType it is also persisted on
  // the line (part_data.dms_location) and hydrated from there after a reload.
  const [selectedDmsLocation, setSelectedDmsLocation] = useState<Record<string, string | null>>({});
  /** Effective DMS line-type / location choice for a line: this session's pick,
   *  else what was persisted on the line (part_data.dms_type / dms_location). */
  const dmsTypeFor = (item: any, itemKey: string): string | undefined => {
    if (itemKey in selectedDmsType) return selectedDmsType[itemKey];
    const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
    return pd.dms_type ?? undefined;
  };
  const dmsLocationFor = (item: any, itemKey: string): string | null => {
    if (itemKey in selectedDmsLocation) return selectedDmsLocation[itemKey];
    const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
    return pd.dms_location ?? null;
  };
  // Tracks a pending option selection waiting for user confirmation: { itemKey, option }
  const [confirmPricingOption, setConfirmPricingOption] = useState<{ itemKey: string; item: any; option: any } | null>(null);
  const [sharingChannel, setSharingChannel] = useState<'email' | 'whatsapp' | 'csv' | 'pdf' | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [orderDiscount, setOrderDiscount] = useState<OrderPriceResolveResult | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  // Discount agreement selector
  const [availableAgreements, setAvailableAgreements] = useState<DiscountAgreement[]>([]);
  const [selectedCustomerNames, setSelectedCustomerNames] = useState<string[]>([]);
  const [agreementFilter, setAgreementFilter] = useState<string>('');
  const [showAgreementDropdown, setShowAgreementDropdown] = useState(false);
  const agreementDropdownRef = useRef<HTMLDivElement>(null);
  // Keep old single-name for backward-compat with buyerName in submit
  const selectedCustomerName = selectedCustomerNames[0] ?? '';
  const [taxRate, setTaxRate] = useState<number>(0);
  const [taxRateEditing, setTaxRateEditing] = useState(false);
  const [taxRateInputValue, setTaxRateInputValue] = useState<string>('0');
  // Custom manual discount (applied on top of agreement discounts)
  const [customDiscountType, setCustomDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [customDiscountValue, setCustomDiscountValue] = useState<number>(0);
  const [customDiscountEditing, setCustomDiscountEditing] = useState(false);
  const [customDiscountInputValue, setCustomDiscountInputValue] = useState<string>('0');
  const [extraDiscountSaving, setExtraDiscountSaving] = useState(false);
  // Tracks which item's description is currently being saved to the backend
  const [descSavingItemKey, setDescSavingItemKey] = useState<string | null>(null);
  // Tracks which item's price is currently being saved to the backend
  const [priceSavingItemKey, setPriceSavingItemKey] = useState<string | null>(null);
  // Tracks which line is currently re-checking its inventory/price availability.
  const [recheckingItemKey, setRecheckingItemKey] = useState<string | null>(null);
  // True once the pending-data poller exhausted its budget (MAX_PENDING_POLLS)
  // without integration/pricing data arriving — stuck lines then show a manual
  // "Re-check" button instead of the endless "Checking..." spinner.
  const [pendingPollStopped, setPendingPollStopped] = useState(false);
  // Pending-set membership the poller gave up on — a different set (e.g. a newly
  // added line) automatically re-arms polling with a fresh budget.
  const stoppedPendingKeyRef = useRef<string | null>(null);
  // Seconds the order's pricing run has waited for a worker (0 = not queued).
  const [pricingQueuedSeconds, setPricingQueuedSeconds] = useState(0);
  // Re-check a single line's availability. Prefers a targeted onRecheckInventory
  // handler; falls back to a full order refresh (onRefreshItems) so the button
  // still works wherever a per-line endpoint isn't wired.
  const handleRecheck = async (item: any, itemKey: string) => {
    if (recheckingItemKey) return;
    setRecheckingItemKey(itemKey);
    try {
      if (onRecheckInventory) await onRecheckInventory(item);
      else if (onRefreshItems) await onRefreshItems();
    } catch {
      // ignore — the availability row simply keeps its last-known state.
    } finally {
      setRecheckingItemKey(null);
      // A manual re-check re-arms the pending poller with a fresh budget.
      stoppedPendingKeyRef.current = null;
      setPendingPollStopped(false);
    }
  };
  // True while any item is being saved — blocks new edits until the backend ACKs
  const isSavingAny = !!(descSavingItemKey || priceSavingItemKey || lineDiscountSavingKey);
  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };
  const shareMenuRef = useRef<HTMLDivElement>(null);
  // Tracks whether we've done at least one live discount resolution since mount.
  // While false, we won't wipe the DB-restored snapshot with null.
  const discountResolved = useRef(false);
  // Keep a ref to orderDiscount so saveExtraDiscount never reads a stale closure value
  const orderDiscountRef = useRef<OrderPriceResolveResult | null>(null);
  const { getActiveOrderId, getOrderObject, setOrderObject } = useOrder();
  const { currentUser } = useAuth();
  const currencySymbol = getCurrencySymbol(currentUser?.currency);
  const numberFormat = (currentUser as any)?.number_format ?? 'comma';
  /** Shorthand: format a price using the org's configured number format */
  const fmt = (num: number) => formatPrice(num, numberFormat);
  // Org-configured cart availability display (admin → Organization Settings).
  const availabilityConfig = useMemo<AvailConfig>(() => ({
    priority: ((currentUser as any)?.inventory_source_priority as AvailPriority) || 'dms_first',
    showAlternative: (currentUser as any)?.inventory_show_alternative !== false,
    pricePriority: ((currentUser as any)?.inventory_price_priority as PricePriority) || 'original_first',
    locationPriority: ((currentUser as any)?.inventory_location_priority as LocationPriority) || 'list_order',
    // Every warehouse in the member's home city (server-resolved); falls back to
    // the legacy single home warehouse for older payloads.
    homeLocation: ((currentUser as any)?.home_warehouses as string[] | undefined)?.length
      ? (currentUser as any).home_warehouses
      : ((currentUser as any)?.home_warehouse ?? null),
    homeLabel: (currentUser as any)?.home_city ?? null,
  }), [currentUser]);
  const [waitForPricing, setWaitForPricing] = useState<boolean>(true);
  const [inventoryEnabled, setInventoryEnabled] = useState<boolean>(false);
  // Delivery time (spec §6) — org feature flag + a per-line editable estimate.
  const [deliveryEnabled, setDeliveryEnabled] = useState<boolean>(false);
  // Optimistic per-line manual selection (days), keyed by itemKey. Persisted onto
  // the order line via onDeliveryUpdate; this map just avoids a flash before the
  // order round-trips.
  const [selectedDelivery, setSelectedDelivery] = useState<Record<string, number | null>>({});
  // Predefined options for the per-line dropdown, built from the org zone table.
  const deliveryOptions = useMemo<DeliveryOption[] | null>(() => {
    if (!deliveryEnabled) return null;
    const z = (currentUser as any)?.delivery_zones || {};
    return [
      { label: z.home_city_label || 'Santiago', days: Number(z.home_city_days ?? 3) },
      { label: t('cart.sidePanel.inventory.deliveryOutside', 'Outside'), days: Number(z.outside_days ?? 5) },
      { label: t('cart.sidePanel.inventory.deliveryInternational', 'International'), days: Number(z.international_days ?? 45) },
    ];
  }, [deliveryEnabled, currentUser, t]);
  // Auto-resolved estimate (spec §6.2): per-customer override ?? org default (5).
  const autoDeliveryDays = (): number => {
    const override = getOrderObject()?.b2b_customer?.delivery_days_override;
    if (override != null && override !== '') return Number(override);
    const def = (currentUser as any)?.delivery_default_days;
    return typeof def === 'number' ? def : 5;
  };
  // Resolved days for a line: manual selection ?? persisted line value ?? auto.
  const deliveryDaysFor = (item: any, itemKey: string): number | null => {
    if (!deliveryEnabled) return null;
    if (itemKey in selectedDelivery) return selectedDelivery[itemKey];
    const pd = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
    if (pd.delivery_days != null) return Number(pd.delivery_days);
    return autoDeliveryDays();
  };
  // Apply + persist a manual per-line delivery choice.
  const selectDeliveryDays = (item: any, itemKey: string, days: number | null) => {
    if (readOnly) return;
    setSelectedDelivery(prev => ({ ...prev, [itemKey]: days }));
    if (onDeliveryUpdate) onDeliveryUpdate(item, days);
  };

  // Keep orderDiscountRef in sync so saveExtraDiscount never reads a stale closure
  useEffect(() => {
    orderDiscountRef.current = orderDiscount;
  }, [orderDiscount]);

  /**
   * Persist extra discount (value + type) into the order's discount_snapshot on the backend.
   * This is called whenever the user commits a custom discount value.
   */
  const saveExtraDiscount = async (value: number, type: 'percentage' | 'fixed') => {
    const orderId = getActiveOrderId();
    if (!orderId) return;
    // Read from ref to avoid stale closure issues
    const currentOrderDiscount = orderDiscountRef.current;
    const baseSnapshot = currentOrderDiscount
      ? { ...currentOrderDiscount }
      : { has_discount: value > 0, retail_total: 0, discounted_total: 0, total_discount_amount: 0, items: [] };
    const snapshot = {
      ...baseSnapshot,
      has_discount: baseSnapshot.has_discount || value > 0,
      extra_discount_value: value,
      extra_discount_type: type,
    };
    // When clearing (value === 0): send has_discount:false so the backend sets snapshot to null.
    // When saving: send the full snapshot.
    const discountToSave = value > 0
      ? snapshot
      : { has_discount: false, extra_discount_value: 0, extra_discount_type: type, retail_total: 0, discounted_total: 0, total_discount_amount: 0, items: [], currency: currentUser?.currency || 'USD' };
    setExtraDiscountSaving(true);
    try {
      const updated = await orderService.updateOrder(orderId, { discount_snapshot: discountToSave });
      // Keep in-memory order cache in sync so extra discount survives re-mount/refresh
      if (updated) setOrderObject(updated);
    } catch {
      // non-blocking — fail silently
    } finally {
      setExtraDiscountSaving(false);
    }
  };

  // Sync taxRate from org settings when currentUser loads
  useEffect(() => {
    const orgVat = (currentUser as any)?.vat_rate;
    if (orgVat != null && orgVat > 0) {
      setTaxRate(orgVat);
      setTaxRateInputValue(String(orgVat));
    }
  }, [currentUser?.vat_rate]);

  useEffect(() => {
    const orgId = (currentUser as any)?.organization_id;
    if (!orgId) return;
    adminService.getOrgFeatureFlagPublic(orgId, PRICING_FLOW_FLAG)
      .then(res => setWaitForPricing(res.data.is_enabled ?? true))
      .catch(() => setWaitForPricing(true));
    adminService.getOrgFeatureFlagPublic(orgId, INVENTORY_FLAG)
      .then(res => setInventoryEnabled(res.data.is_enabled ?? false))
      .catch(() => setInventoryEnabled(false));
    adminService.getOrgFeatureFlagPublic(orgId, DELIVERY_FLAG)
      .then(res => setDeliveryEnabled(res.data.is_enabled ?? false))
      .catch(() => setDeliveryEnabled(false));
  }, [currentUser]);

  // Load active discount agreements for the org
  useEffect(() => {
    discountService.listAgreements({ is_active: true })
      .then(res => setAvailableAgreements(res.data.agreements || []))
      .catch(() => setAvailableAgreements([]));
  }, [currentUser]);

  // Derive the current vehicle brand from cart items' vin_data (most reliable source)
  // Falls back to the order's vin_data if no cart items have it yet.
  const vehicleBrand = useMemo(() => {
    // Try cart items first — they always carry vin_data
    for (const item of cartItems) {
      const vd = item.structured_data?.vin_data || {};
      const b = (vd.make || vd.brand || vd.manufacturer || '').trim();
      if (b) return b.toLowerCase();
    }
    // Fall back to the order object
    const order = getOrderObject() as any;
    const vd = order?.vin_data || {};
    return (vd.make || vd.brand || vd.manufacturer || '').toLowerCase().trim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  // On order load: restore selected customer names from the discount_snapshot and
  // restore orderDiscount from the persisted discount_snapshot so the UI
  // shows the discount immediately without waiting for user re-selection.
  useEffect(() => {
    const order = getOrderObject();
    if (!order) return;

    // Restore selected customer names only from the discount snapshot
    // (never fall back to order.buyer — that field is used for free-text Notes)
    if (selectedCustomerNames.length === 0) {
      const savedNames: string[] = order.discount_snapshot?.selected_customer_names?.length
        ? order.discount_snapshot.selected_customer_names
        : [];
      if (savedNames.length > 0) {
        setSelectedCustomerNames(savedNames);
      }
    }

    // Restore discount display from persisted snapshot
    if (order.discount_snapshot && order.discount_snapshot.has_discount) {
      setOrderDiscount(order.discount_snapshot);
      // Restore extra (custom) discount if it was saved
      if (order.discount_snapshot.extra_discount_value != null && order.discount_snapshot.extra_discount_value > 0) {
        setCustomDiscountValue(order.discount_snapshot.extra_discount_value);
        setCustomDiscountInputValue(String(order.discount_snapshot.extra_discount_value));
        if (order.discount_snapshot.extra_discount_type) {
          setCustomDiscountType(order.discount_snapshot.extra_discount_type as 'percentage' | 'fixed');
        }
      }
    } else if (order.discount_snapshot && order.discount_snapshot.extra_discount_value != null && order.discount_snapshot.extra_discount_value > 0) {
      // No agreement discount but a standalone extra discount exists
      setCustomDiscountValue(order.discount_snapshot.extra_discount_value);
      setCustomDiscountInputValue(String(order.discount_snapshot.extra_discount_value));
      if (order.discount_snapshot.extra_discount_type) {
        setCustomDiscountType(order.discount_snapshot.extra_discount_type as 'percentage' | 'fixed');
      }
    }
    // Reset live-resolution flag so we don't wipe the restored snapshot
    discountResolved.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOrderObject()?.id]);



  // Close agreement dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (agreementDropdownRef.current && !agreementDropdownRef.current.contains(e.target as Node)) {
        setShowAgreementDropdown(false);
      }
    };
    if (showAgreementDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAgreementDropdown]);

  // Close share menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  // Auto-expand when items are added to the cart
  useEffect(() => {
    if (cartItems.length > prevCartLength) {
      setIsMinimized(false);
    }
    setPrevCartLength(cartItems.length);
  }, [cartItems.length, prevCartLength]);

  // Sync manualPrices with server data on every cartItems change:
  // 1. Drop manual overrides for items that now have real pricing_data from the server.
  // 2. Hydrate manual prices from persisted structured_data.part_data.price for items
  //    that have no pricing_data (i.e. the price was set manually and saved to the server).
  useEffect(() => {
    setManualPrices(prev => {
      const next = { ...prev };
      let changed = false;
      cartItems.forEach((item: any) => {
        const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
        const key = item.id || item.part_id || partData.part_number || '';
        if (!key) return;
        const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
        const hasPricingData = pricingArr.length > 0 && parsePrice(pricingArr[0]?.price) !== null;

        if (hasPricingData) {
          // Server returned real pricing — drop any manual override UNLESS a price
          // was explicitly saved (i.e. user picked an option and it was persisted).
          const savedPrice = parsePrice(partData.price, numberFormat) ?? parsePrice(item.price, numberFormat);
          if (key in next) {
            // Keep if it matches the saved server price (user already confirmed)
            if (savedPrice !== null && next[key] === savedPrice) {
              // already in sync — leave it
            } else if (savedPrice === null) {
              // no saved price on server yet — drop so the selector re-appears
              delete next[key];
              changed = true;
            }
          } else if (savedPrice !== null) {
            // Hydrate from persisted price (user had previously selected an option)
            next[key] = savedPrice;
            changed = true;
          }
        } else {
          // No pricing_data — check if a manual price was persisted on the server
          // (saved via handlePriceUpdate into structured_data.part_data.price or item.price)
          if (!(key in next)) {
            const savedPrice = parsePrice(partData.price, numberFormat) ?? parsePrice(item.price, numberFormat);
            if (savedPrice !== null) {
              next[key] = savedPrice;
              changed = true;
            }
          }
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems]);

  // Report minimize/expand up so the chat composer can reserve the matching
  // right-side width (a minimized panel is just a thin w-14 strip).
  useEffect(() => {
    onMinimizedChange?.(isMinimized);
  }, [isMinimized, onMinimizedChange]);

  // Poll for prices on items that are still pending (no price yet)
  const pricePendingItemIds = useMemo(() => {
    return new Set(
      cartItems
        .filter(item => {
          if (isNotFoundItem(item) || isServiceItem(item)) return false;
          // Only poll if the pricing_data field hasn't arrived yet
          return !('pricing_data' in item) || item.pricing_data === undefined;
        })
        .map((item, idx) => item.id || item.part_id || `idx-${idx}`)
    );
  }, [cartItems]);

  // Track items that are pending inventory data
  const inventoryPendingItemIds = useMemo(() => {
    if (!inventoryEnabled) return new Set<string>();
    return new Set(
      cartItems
        .filter(item => {
          if (isNotFoundItem(item) || isServiceItem(item) || isDatasourceItem(item)) return false;
          // Resolved once either availability source has arrived.
          if (getIntegrationData(item) !== undefined) return false;
          // Pending if inventory_data field hasn't arrived yet
          return !('inventory_data' in item) || item.inventory_data === undefined;
        })
        .map((item, idx) => item.id || item.part_id || `idx-${idx}`)
    );
  }, [cartItems, inventoryEnabled]);

  // Stable identity of the pending set. Refreshes rebuild the Sets each time,
  // but as long as membership is unchanged this string is unchanged — so the
  // poll interval (and its attempt counter) survives across refreshes, while a
  // real membership change (line added/resolved) restarts it with fresh budget.
  const pendingPollKey = useMemo(() => {
    const ids: string[] = [];
    if (waitForPricing) ids.push(...Array.from(pricePendingItemIds, id => `p:${id}`));
    if (inventoryEnabled) ids.push(...Array.from(inventoryPendingItemIds, id => `i:${id}`));
    return ids.sort().join(',');
  }, [pricePendingItemIds, inventoryPendingItemIds, waitForPricing, inventoryEnabled]);

  // Latest onRefreshItems, read at tick time. ChatPage passes an inline closure
  // (new identity every render), so having the prop as an effect dep restarted
  // the interval — and its attempt counter — on every poll-triggered re-render,
  // meaning the give-up budget never ran out.
  const onRefreshItemsRef = useRef(onRefreshItems);
  useEffect(() => { onRefreshItemsRef.current = onRefreshItems; }, [onRefreshItems]);

  useEffect(() => {
    if (!pendingPollKey) return;
    if (pendingPollStopped) {
      // Poller gave up on this exact pending set — stay stopped until a manual
      // re-check or a membership change re-arms it.
      if (stoppedPendingKeyRef.current === pendingPollKey) return;
      stoppedPendingKeyRef.current = null;
      setPendingPollStopped(false);
      return;
    }
    let polls = 0;
    let tracked = false;
    const stop = () => {
      clearInterval(interval);
      stoppedPendingKeyRef.current = pendingPollKey;
      setPricingQueuedSeconds(0);
      setPendingPollStopped(true);
    };
    const interval = setInterval(async () => {
      polls += 1;
      if (polls > (tracked ? MAX_TRACKED_POLLS : MAX_PENDING_POLLS)) {
        // Data never arrived — stop polling; stuck lines show a Re-check button.
        stop();
        return;
      }
      let state: string | null = null;
      const orderId = getActiveOrderId();
      if (orderId) {
        try {
          const status = await orderService.getPricingStatus(orderId);
          state = status.state;
          tracked = true;
          setPricingQueuedSeconds(status.state === 'queued' ? status.queued_seconds : 0);
        } catch {
          tracked = false; // status unavailable — fall back to the timer budget
        }
      }
      try {
        await onRefreshItemsRef.current?.();
      } catch {
        // ignore poll errors silently
      }
      // No run in flight and this refresh still left lines pending → done.
      if (state === 'idle') stop();
    }, PENDING_POLL_INTERVAL_MS);
    return () => { clearInterval(interval); setPricingQueuedSeconds(0); };
  }, [pendingPollKey, pendingPollStopped]);

  // Resolve order-level discounts whenever all prices are available and agreements are selected
  useEffect(() => {
    // Count items that are still waiting for pricing_data to arrive from server
    const pendingCount = cartItems.filter(item => {
      if (isNotFoundItem(item) || isServiceItem(item)) return false;
      return !('pricing_data' in item) || item.pricing_data === undefined;
    }).length;

    if (pendingCount > 0) return;

    // Build line items — include only parts that have a resolvable price
    const items = cartItems
      .filter(item => !isNotFoundItem(item) && !isServiceItem(item))
      .map((item, idx) => {
        const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
        const itemKey = item.id || item.part_id || partData.part_number || `${idx}`;
        const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
        const options = getPriceOptions(pricingArr, t, numberFormat);
        const selectedStr = selectedPricingIndices[itemKey];
        let selectedOption: PriceOption | null = null;
        if (selectedStr) {
          const [idxStr, type] = selectedStr.split(':');
          selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
        }
        if (!selectedOption && options.length > 0) selectedOption = options[0];

        const price = itemKey in manualPrices ? manualPrices[itemKey] : (selectedOption?.price ?? getIntegrationPrice(item, availabilityConfig, numberFormat));
        if (price === null || price === undefined) return null;

        const selectedEntry = selectedOption ? pricingArr[selectedOption.idx] : null;
        const description = item.translated_description || partData.description || partData.DESCRIPTION || '';
        const vinData = item.structured_data?.vin_data || {};
        const vehicleBrand = vinData.brand || vinData.make || vinData.manufacturer || '';
        return {
          line_id: itemKey,
          description,
          retail_price: price,
          quantity: item.quantity || 1,
          part_brand: partData.brand || selectedEntry?.brand || vehicleBrand || undefined,
          part_name: description || undefined,
          part_category: partData.category || partData.CATEGORY || selectedEntry?.category || undefined,
          oe_sku: partData.part_number || partData.oe_number || item.part_id || undefined,
          supplier_sku: selectedEntry?.part_number || selectedEntry?.sku || undefined,
        };
      })
      .filter(Boolean) as any[];

    if (items.length === 0) {
      // Only wipe the snapshot if we've already done a live resolution
      // (don't clear the DB-restored snapshot before prices have loaded)
      if (discountResolved.current) setOrderDiscount(null);
      return;
    }

    // Only resolve discounts when the user has explicitly selected agreements
    if (selectedCustomerNames.length === 0) {
      if (discountResolved.current) setOrderDiscount(null);
      return;
    }
    const namesToResolve = selectedCustomerNames;

    setDiscountLoading(true);
    Promise.all(
      namesToResolve.map(name =>
        discountService.resolveOrderPricing({
          customer_name: name,
          currency: currentUser?.currency || 'USD',
          items,
        }).then(r => r.data).catch(() => null)
      )
    ).then(results => {
      discountResolved.current = true;
      const validResults = results.filter(Boolean) as any[];
      if (validResults.length === 0) { setOrderDiscount(null); return; }
      if (validResults.length === 1) {
        setOrderDiscount(validResults[0].has_discount ? validResults[0] : null);
        return;
      }
      // Merge: best discount per line wins
      const mergedItems = items.map(li => {
        let best: any = null;
        for (const res of validResults) {
          const ri = res.items.find((i: any) => i.line_id === li.line_id);
          if (ri && (!best || ri.discount_amount > best.discount_amount)) best = ri;
        }
        return best || { ...li, discount_type: null, discount_value: 0, scope_type: null, scope_value: null, discount_amount: 0, final_price: li.retail_price, line_total: li.retail_price * li.quantity, agreement_id: null };
      });
      const retailTotal = mergedItems.reduce((s: number, i: any) => s + i.retail_price * i.quantity, 0);
      const discountedTotal = mergedItems.reduce((s: number, i: any) => s + i.line_total, 0);
      const merged = {
        customer_name: namesToResolve.join(', '),
        currency: currentUser?.currency || 'USD',
        items: mergedItems,
        retail_total: Math.round(retailTotal * 100) / 100,
        discounted_total: Math.round(discountedTotal * 100) / 100,
        total_discount_amount: Math.round((retailTotal - discountedTotal) * 100) / 100,
        has_discount: retailTotal > discountedTotal,
      };
      setOrderDiscount(merged.has_discount ? merged : null);
    }).catch(() => {
      setOrderDiscount(null);
    }).finally(() => setDiscountLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, manualPrices, selectedPricingIndices, selectedCustomerNames]);

  // Categorize items
  const { parts, services, notFoundParts, queryGroups, summary } = useMemo(() => {
    const partsArr: any[] = [];
    const servicesArr: any[] = [];
    const notFoundArr: any[] = [];
    const queryMap: Record<string, any[]> = {};

    cartItems.forEach((item: any) => {
      // Categorize
      if (isNotFoundItem(item)) {
        notFoundArr.push(item);
      } else if (isServiceItem(item)) {
        servicesArr.push(item);
      } else {
        partsArr.push(item);
      }

      // Group by query
      const query = getRelatedQuery(item);
      if (query) {
        if (!queryMap[query]) queryMap[query] = [];
        queryMap[query].push(item);
      }
    });

    // Calculate summary
    let totalPrice = 0;
    let priceableCount = 0;
    let pendingPriceCount = 0;   // field not yet arrived — still fetching
    let missingPriceCount = 0;   // field arrived as null — not found
    let totalQuantity = 0;
    let totalLaborHours = 0;
    let totalLaborCost = 0;

    cartItems.forEach((item: any) => {
      const qty = item.quantity || 1;
      totalQuantity += qty;
      const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
      const itemKey = item.id || item.part_id || partData.part_number || item.structured_data?.part_data?.description || '';

      // Resolve price from pricing_data array
      const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
      const pricingFieldExists = 'pricing_data' in item;
      const options = getPriceOptions(pricingArr, t, numberFormat);

      const selectedStr = selectedPricingIndices[itemKey];
      let selectedOption: PriceOption | null = null;
      if (selectedStr) {
        const [idxStr, type] = selectedStr.split(':');
        selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
      }
      if (!selectedOption && options.length > 0) selectedOption = options[0];

      const rawPriceFromPricing = selectedOption?.price;
      // integration_data (DMS/importer) price is a fallback source below manual
      // overrides and pricing_data. Its mere arrival also resolves "pending".
      const hasIntegration = getIntegrationData(item) !== undefined;
      const integrationPrice = hasIntegration ? getIntegrationPrice(item, availabilityConfig, numberFormat) : null;

      // Merge manually-set price over pricing_data, then integration_data
      const effectivePrice = itemKey in manualPrices
        ? manualPrices[itemKey]
        : (rawPriceFromPricing ?? integrationPrice ?? null);
      const fieldExists = itemKey in manualPrices
        ? true
        : ((pricingFieldExists && pricingArr !== undefined) || hasIntegration);
      const fieldIsNull = itemKey in manualPrices
        ? manualPrices[itemKey] === null
        : hasIntegration
          ? (rawPriceFromPricing == null && integrationPrice === null)
          : (pricingFieldExists && pricingArr.length === 0);

      if (!isNotFoundItem(item) && !isServiceItem(item)) {
        if (effectivePrice !== null) {
          // Apply per-item manual line discount if present
          const lineDiscount = partData.line_discount as { value: number; type: 'percentage' | 'fixed' } | undefined;
          let priceAfterLineDiscount = effectivePrice;
          if (lineDiscount && lineDiscount.value > 0) {
            if (lineDiscount.type === 'percentage') {
              priceAfterLineDiscount = Math.max(0, effectivePrice * (1 - lineDiscount.value / 100));
            } else {
              priceAfterLineDiscount = Math.max(0, effectivePrice - lineDiscount.value);
            }
          }
          totalPrice += priceAfterLineDiscount * qty;
          priceableCount += qty;
        } else if (!fieldExists && waitForPricing) {
          pendingPriceCount += qty;  // still fetching
        } else if (fieldIsNull || (!fieldExists && !waitForPricing)) {
          missingPriceCount += qty;  // fetched but not found, or not fetching
        }
      }
      // Labor estimate (per-item, not multiplied by quantity)
      const laborEst = partData.labor_estimate as LaborEstimate | undefined;
      if (laborEst && laborEst.labor_hours > 0) {
        totalLaborHours += laborEst.labor_hours;
        totalLaborCost += laborEst.labor_hours * (laborEst.labor_rate ?? DEFAULT_LABOR_RATE);
      }
    });

    return {
      parts: partsArr,
      services: servicesArr,
      notFoundParts: notFoundArr,
      queryGroups: queryMap,
      summary: {
        totalItems: cartItems.length,
        totalQuantity,
        totalPrice,
        priceableCount,
        pendingPriceCount,
        missingPriceCount,
        partsCount: partsArr.length,
        servicesCount: servicesArr.length,
        notFoundCount: notFoundArr.length,
        totalLaborHours,
        totalLaborCost,
      }
    };
  }, [cartItems, manualPrices]);

  /**
   * Build the PdfPayload that captures all live manual overrides:
   *  - items_override: EVERY item with the panel's effective unit price baked
   *    into structured_data.part_data.price (manual override → selected/default
   *    pricing_data option → integration DMS/importer price — the exact chain
   *    the summary uses), so the PDF never has to re-derive prices.
   *  - discount_override: the resolved discount snapshot (from agreement
   *    selection) converted to grand totals (parts + labor) with the extra
   *    custom discount folded in — mirroring the on-screen summary math.
   *  - tax_rate: the panel's current (possibly user-edited) tax rate.
   */
  const buildPdfPayload = (): PdfPayload => {
    // ── items with the effective price baked in ────────────────────
    const items_override = cartItems.map((item: any) => {
      if (isNotFoundItem(item) || isServiceItem(item)) return item;
      const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
      // Same key chain as the summary computation
      const itemKey = item.id || item.part_id || partData.part_number || partData.description || '';

      const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
      const options = getPriceOptions(pricingArr, t, numberFormat);
      const selectedStr = selectedPricingIndices[itemKey];
      let selectedOption: PriceOption | null = null;
      if (selectedStr) {
        const [idxStr, type] = selectedStr.split(':');
        selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
      }
      if (!selectedOption && options.length > 0) selectedOption = options[0];

      const hasIntegration = getIntegrationData(item) !== undefined;
      const integrationPrice = hasIntegration ? getIntegrationPrice(item, availabilityConfig, numberFormat) : null;

      const effectivePrice = itemKey in manualPrices
        ? manualPrices[itemKey]
        : (selectedOption?.price ?? integrationPrice ?? null);

      if (effectivePrice == null) return item;
      return {
        ...item,
        structured_data: {
          ...item.structured_data,
          part_data: { ...partData, price: effectivePrice },
        },
      };
    });

    // ── discount snapshot with labor + custom discount folded in ──
    const laborCost = summary.totalLaborCost;
    const laborHours = summary.totalLaborHours;

    // Agreement resolution totals are parts-only; convert to grand totals
    // (parts + labor) so the PDF matches the panel's Grand Total math.
    let discount_override: Record<string, any> | null = orderDiscount
      ? {
        ...orderDiscount,
        retail_total: orderDiscount.retail_total + laborCost,
        discounted_total: orderDiscount.discounted_total + laborCost,
      }
      : null;
    if (customDiscountValue > 0) {
      // Same base as the on-screen Extra Discount / Tax rows:
      // discounted grand total when a real B2B discount exists, else parts + labor
      const base = (discount_override && (discount_override.discounted_total ?? 0) > 0)
        ? discount_override.discounted_total
        : summary.totalPrice + laborCost;
      const customAmt = customDiscountType === 'percentage'
        ? Math.min(base, (base * customDiscountValue) / 100)
        : Math.min(base, customDiscountValue);
      discount_override = {
        ...(discount_override ?? {}),
        retail_total: discount_override?.retail_total ?? base,
        discounted_total: Math.max(0, base - customAmt),
        total_discount_amount: (discount_override?.total_discount_amount ?? 0) + customAmt,
        has_discount: true,
        extra_discount_type: customDiscountType,
        extra_discount_value: customDiscountValue,
        extra_discount_amount: customAmt,
      };
    }
    if (discount_override) {
      discount_override.labor_cost = laborCost;
      discount_override.labor_hours = laborHours;
      // Tells the PDF renderer the totals above already contain labor
      discount_override.includes_labor = true;
    }

    return { discount_override, items_override, tax_rate: taxRate };
  };

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ── Per-line note (structured_data.part_data.note) ────────────────────────
  // A small 📝 line under the description in both the card and table views:
  // click the note (or "Add note") to edit inline; Enter/blur saves, Escape
  // cancels, empty text clears the note.
  const noteFor = (item: any): string => {
    const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA || {};
    return typeof pd.note === 'string' ? pd.note : '';
  };
  const commitNote = async (item: any, itemKey: string) => {
    const trimmed = noteEditValue.trim();
    setNoteEditItemKey(null);
    if (trimmed === noteFor(item).trim() || !onNoteUpdate) return;
    setNoteSavingItemKey(itemKey);
    try {
      await onNoteUpdate(item, trimmed || null);
    } finally {
      setNoteSavingItemKey(null);
    }
  };
  const renderNoteLine = (item: any, itemKey: string) => {
    const note = noteFor(item);
    if (!onNoteUpdate && !note) return null;
    if (noteEditItemKey === itemKey) {
      return (
        <input
          type="text"
          value={noteEditValue}
          onChange={e => setNoteEditValue(e.target.value)}
          onClick={e => e.stopPropagation()}
          onBlur={() => commitNote(item, itemKey)}
          onKeyDown={e => {
            if (e.key === 'Enter') commitNote(item, itemKey);
            else if (e.key === 'Escape') setNoteEditItemKey(null);
          }}
          placeholder={t('cart.sidePanel.note.placeholder', 'Add a note for this part…')}
          className="mt-1 w-full text-[10px] text-[#201e1a] bg-white border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          autoFocus
        />
      );
    }
    if (noteSavingItemKey === itemKey) {
      return (
        <div className="mt-0.5 flex items-center gap-1 text-[10px] text-[#9a958c]">
          <Loader2 className="w-2.5 h-2.5 animate-spin" /> {t('cart.sidePanel.note.saving', 'Saving note…')}
        </div>
      );
    }
    if (note) {
      return (
        <div
          className={`mt-0.5 flex items-start gap-1 text-[10px] leading-snug text-[#6b6b6b] ${readOnly || !onNoteUpdate ? '' : 'cursor-pointer hover:text-[#201e1a]'}`}
          title={readOnly || !onNoteUpdate ? undefined : t('cart.sidePanel.note.edit', 'Click to edit note')}
          onClick={(e) => { if (readOnly || !onNoteUpdate) return; e.stopPropagation(); setNoteEditItemKey(itemKey); setNoteEditValue(note); }}
        >
          <StickyNote className="w-2.5 h-2.5 flex-shrink-0 mt-[1px] text-amber-500" />
          <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note}</span>
        </div>
      );
    }
    if (readOnly) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setNoteEditItemKey(itemKey); setNoteEditValue(''); }}
        className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-[#b8b3a9] hover:text-[#1E0DB8] transition-colors"
      >
        <StickyNote className="w-2.5 h-2.5" /> {t('cart.sidePanel.note.add', 'Add note')}
      </button>
    );
  };

  const renderItemCard = (item: any, index: number, showQuery: boolean = true) => {
    const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
    const partId = partData.part_number;
    const description = partData.description || partData.DESCRIPTION || t('cart.sidePanel.noDescription');

    // ── pricing_data array handling ──
    const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
    const pricingFieldExists = 'pricing_data' in item;
    const itemKey = item.id || item.part_id || partData.part_number || `${description}-${index}`;

    const options = getPriceOptions(pricingArr, t, numberFormat);
    const selectedStr = selectedPricingIndices[itemKey];
    let selectedOption: PriceOption | null = null;
    if (selectedStr) {
      const [idxStr, type] = selectedStr.split(':');
      selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
    }
    if (!selectedOption && options.length > 0) selectedOption = options[0];

    const isNotFound = isNotFoundItem(item);
    // integration_data (DMS/importer) price is a fallback below manual + pricing_data.
    const hasIntegration = getIntegrationData(item) !== undefined;
    const integrationPrice = hasIntegration ? getIntegrationPrice(item, availabilityConfig, numberFormat) : null;
    // Max orderable stock for the selected line-type — warn (don't block) when the
    // ordered qty exceeds a known, positive available count. See renderCartTable.
    const cardAvailView = hasIntegration ? deriveAvailability(getIntegrationData(item), availabilityConfig) : null;
    const cardLegacyEntry = Array.isArray(item.inventory_data) ? item.inventory_data[0] : undefined;
    const cardLegacyQty: number | null = cardLegacyEntry ? ((cardLegacyEntry.super_session ? cardLegacyEntry.super_session_qty : cardLegacyEntry.stock_qty) ?? null) : null;
    const cardAvailableQty = availableQtyForType(cardAvailView, dmsTypeFor(item, itemKey), cardLegacyQty, dmsLocationFor(item, itemKey));
    const cardOverStock = !readOnly && cardAvailableQty != null && cardAvailableQty > 0 && (item.quantity || 1) > cardAvailableQty;
    // pending = neither pricing_data nor integration_data has arrived (only when waitForPricing is enabled)
    const pricePendingBase = !isNotFound && !isServiceItem(item) && !pricingFieldExists && !hasIntegration && !(itemKey in manualPrices) && waitForPricing;
    // Poller exhausted its budget with no data — swap the endless spinner for a Re-check.
    const isPricePollStopped = pricePendingBase && pendingPollStopped;
    const isPricePending = pricePendingBase && !pendingPollStopped;
    // not found = data arrived but no usable price AND no manual override, OR waitForPricing is false
    const isPriceNotFound = !isNotFound && !isServiceItem(item) && (
      (itemKey in manualPrices && manualPrices[itemKey] === null) ||
      (!(itemKey in manualPrices) && (selectedOption?.price ?? integrationPrice) == null && ((pricingFieldExists && options.length === 0) || hasIntegration || (!pricingFieldExists && !waitForPricing)))
    );
    // multiple options available
    const hasMultiplePricing = !isNotFound && !isServiceItem(item) && options.length > 1;

    // Effective display price (manual override wins, then pricing_data, then integration_data)
    const effectivePriceNum = itemKey in manualPrices
      ? manualPrices[itemKey]
      : (selectedOption?.price ?? integrationPrice ?? null);
    const priceDisplay = effectivePriceNum !== null
      ? `${currencySymbol}${fmt(effectivePriceNum)}`
      : '--';

    // Per-item discount from resolved order pricing
    const discountItem = orderDiscount?.items.find(d => d.line_id === itemKey);
    const hasItemDiscount = !!(discountItem && discountItem.discount_amount > 0);

    // Manual line discount (stored in part_data.line_discount)
    const existingLineDiscount = partData.line_discount as { value: number; type: 'percentage' | 'fixed' } | undefined;
    const hasLineDiscount = !!(existingLineDiscount && existingLineDiscount.value > 0);
    // Effective price after manual line discount
    const effectivePriceAfterLineDiscount = (() => {
      if (effectivePriceNum === null || !hasLineDiscount) return effectivePriceNum;
      if (existingLineDiscount!.type === 'percentage') {
        return Math.max(0, effectivePriceNum * (1 - existingLineDiscount!.value / 100));
      }
      return Math.max(0, effectivePriceNum - existingLineDiscount!.value);
    })();

    const relatedQuery = getRelatedQuery(item);
    const translatedDesc = item.translated_description;
    const displayName = translatedDesc || description;
    const subDescription = translatedDesc && translatedDesc !== description ? description : null;
    const laborEst = partData.labor_estimate as LaborEstimate | undefined;

    // Derive supersession part number from the first inventory entry (if any)
    const firstInventoryEntry = Array.isArray(item.inventory_data) ? item.inventory_data[0] : null;
    const superSessionPartNumber: string | null = firstInventoryEntry?.super_session ?? null;

    // Stable key for tracking removal spinner
    const isRemoving = removingItemId === itemKey;

    return (
      <div
        key={`part-${getOrderObject()?.vin}-${index}`}
        className={`py-3 ${isNotFound ? 'opacity-80' : ''}`}
      >
        {/* ── Badge row: query / not-found ── */}
        {!isNotFound && showQuery && relatedQuery && (
          <div className="flex items-center gap-1 mb-1.5">
            <Search className="w-3 h-3 text-[#6866ff] flex-shrink-0" />
            <span className="text-xs text-[#6866ff] truncate italic">"{relatedQuery}"</span>
          </div>
        )}
        {/* Datasource (vector-search suggestion) line: price comes from the org's
            own data source, not the OE catalog / DMS — say where it came from. */}
        {partData.price_source === 'datasource' && (
          <div className="flex items-center gap-1 mb-1.5">
            <span className="inline-flex items-center text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">
              {partData.supplier_name
                ? t('cart.sidePanel.fromDataSource', { defaultValue: 'Data source: {{name}}', name: partData.supplier_name })
                : t('cart.sidePanel.fromDataSourceGeneric', { defaultValue: 'From your data sources' })}
            </span>
          </div>
        )}
        {isNotFound && (
          <div className="flex items-center gap-1 mb-1.5">
            <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
            <span className="text-xs font-medium text-amber-600">
              {t('cart.sidePanel.notFound', { defaultValue: 'Not found in catalog' })}
            </span>
          </div>
        )}

        {/* ── Main two-column layout ── */}
        <div className="flex items-start gap-2">

          {/* LEFT: thumbnail + info */}
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {/* Thumbnail */}
            {item.image ? (
              <button
                type="button"
                onClick={() => setZoomedImage(item.image)}
                title={t('cart.sidePanel.table.zoomImage', { defaultValue: 'Zoom image' })}
                className="flex-shrink-0 rounded-lg overflow-hidden bg-white border border-gray-200 cursor-zoom-in hover:shadow-md transition-all mt-0.5 p-1"
                style={{ width: CART_IMAGE_PRESENTATION.card.width, height: CART_IMAGE_PRESENTATION.card.height }}
              >
                <img
                  src={item.image}
                  alt={description}
                  className="w-full h-full block"
                  style={{ objectFit: CART_IMAGE_PRESENTATION.fit }}
                  onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                />
              </button>
            ) : (
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center mt-0.5">
                <Package className="w-4 h-4 text-gray-300" />
              </div>
            )}

            {/* Part ID + name */}
            <div className="flex-1 min-w-0">
              {partId && !isNotFound && (
                <span className="flex items-center gap-1 font-mono text-[10px] tracking-wide">
                  {superSessionPartNumber ? (
                    <>
                      <span className="font-semibold text-gray-300 line-through">{partId}</span>
                      <span className="font-semibold text-blue-600">{superSessionPartNumber}</span>
                    </>
                  ) : (
                    <span className="font-semibold text-[#999999]">{partId}</span>
                  )}
                </span>
              )}
              {descEditItem === item ? (
                <input
                  type="text"
                  value={descEditValue}
                  onChange={(e) => setDescEditValue(e.target.value)}
                  onBlur={async () => {
                    const trimmed = descEditValue.trim();
                    setDescEditItem(null);
                    if (trimmed && trimmed !== displayName) {
                      if (onWorkflowLog) onWorkflowLog(t('cart.workflow.descriptionUpdated', { part: partId || itemKey, from: displayName, to: trimmed, defaultValue: `Description updated for part "{{part}}": "{{from}}" → "{{to}}"` }), 'edit_description');
                      if (onDescriptionUpdate) {
                        setDescSavingItemKey(itemKey);
                        try {
                          await onDescriptionUpdate(item, trimmed);
                        } finally {
                          setDescSavingItemKey(null);
                        }
                      }
                    }
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const trimmed = descEditValue.trim();
                      setDescEditItem(null);
                      if (trimmed && trimmed !== displayName) {
                        if (onWorkflowLog) onWorkflowLog(t('cart.workflow.descriptionUpdated', { part: partId || itemKey, from: displayName, to: trimmed, defaultValue: `Description updated for part "{{part}}": "{{from}}" → "{{to}}"` }), 'edit_description');
                        if (onDescriptionUpdate) {
                          setDescSavingItemKey(itemKey);
                          try {
                            await onDescriptionUpdate(item, trimmed);
                          } finally {
                            setDescSavingItemKey(null);
                          }
                        }
                      }
                    } else if (e.key === 'Escape') {
                      setDescEditItem(null);
                    }
                  }}
                  className="text-xs font-semibold text-[#201e1a] bg-white border border-gray-300 rounded px-1 py-0.5 w-full focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              ) : descSavingItemKey === itemKey ? (
                <span className="flex items-center gap-1 text-xs font-semibold text-[#201e1a]">
                  <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                  {displayName}
                </span>
              ) : (
                <p
                  className={`text-xs font-semibold leading-snug rounded px-0.5 -mx-0.5 ${isSavingAny || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-100'} ${isNotFound ? 'text-amber-600' : 'text-[#201e1a]'}`}
                  onClick={() => { if (isSavingAny || readOnly) return; setDescEditItem(item); setDescEditValue(displayName); }}
                  title={isSavingAny ? t('cart.sidePanel.savingInProgress', 'Saving…') : readOnly ? undefined : t('cart.sidePanel.descEdit', 'Click to edit description')}
                >
                  {displayName}
                </p>
              )}
              {subDescription && (
                <p className="text-[10px] text-[#999999] leading-snug mt-0.5 truncate">{subDescription}</p>
              )}
              {renderNoteLine(item, itemKey)}
            </div>
          </div>

          {/* Remove button — top-right */}
          {!readOnly && <button
            onClick={async () => {
              if (removingItemId || isSavingAny) return;
              setRemovingItemId(itemKey);
              try {
                await onRemovePart(item);
              } finally {
                setRemovingItemId(null);
              }
            }}
            disabled={!!removingItemId || isSavingAny}
            className="flex-shrink-0 p-0.5 text-gray-400 hover:text-red-500 transition-colors mt-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('cart.sidePanel.actions.remove')}
          >
            {isRemoving
              ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-400" />
              : <X className="w-3.5 h-3.5" />
            }
          </button>}
        </div>

        {/* ── Bottom row: qty + price / labor button ── */}
        {!isNotFound && !isServiceItem(item) && (
          <div className="flex flex-col gap-1.5 mt-2">
            {/* Top: qty stepper + price */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-white rounded-full px-1.5 py-0.5 shadow-sm border border-gray-100">
                <button
                  onClick={() => { if (isSavingAny || readOnly) return; onUpdateQuantity(item, (item.quantity || 1) - 1); }}
                  className="text-[#201e1a] hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  disabled={(item.quantity || 1) <= 1 || isSavingAny || readOnly}
                >
                  <Minus className="w-2.5 h-2.5" />
                </button>
                <span className={`text-xs min-w-[1rem] text-center ${cardOverStock ? 'font-bold text-red-600' : 'font-medium text-[#201e1a]'}`}>
                  {item.quantity || 1}
                </span>
                <button
                  onClick={() => { if (isSavingAny || readOnly) return; onUpdateQuantity(item, (item.quantity || 1) + 1); }}
                  className="text-[#201e1a] hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  disabled={isSavingAny || readOnly}
                >
                  <Plus className="w-2.5 h-2.5" />
                </button>
              </div>
              {priceEditItem === item ? (
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceEditValue}
                  onChange={(e) => setPriceEditValue(e.target.value)}
                  onBlur={async () => {
                    const newPrice = parsePrice(priceEditValue, numberFormat);
                    setManualPrices(prev => ({ ...prev, [itemKey]: newPrice }));
                    setPriceEditItem(null);
                    if (onPriceUpdate) {
                      if (onWorkflowLog) onWorkflowLog(t('cart.workflow.priceUpdated', { name: displayName, value: newPrice !== null ? newPrice : t('cart.workflow.cleared', 'cleared'), defaultValue: `Price updated for "{{name}}": {{value}}` }), 'edit_price');
                      setPriceSavingItemKey(itemKey);
                      try {
                        await onPriceUpdate(item, newPrice);
                      } finally {
                        setPriceSavingItemKey(null);
                      }
                    }
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const newPrice = parsePrice(priceEditValue, numberFormat);
                      setManualPrices(prev => ({ ...prev, [itemKey]: newPrice }));
                      setPriceEditItem(null);
                      if (onPriceUpdate) {
                        if (onWorkflowLog) onWorkflowLog(t('cart.workflow.priceUpdated', { name: displayName, value: newPrice !== null ? newPrice : t('cart.workflow.cleared', 'cleared'), defaultValue: `Price updated for "{{name}}": {{value}}` }), 'edit_price');
                        setPriceSavingItemKey(itemKey);
                        try {
                          await onPriceUpdate(item, newPrice);
                        } finally {
                          setPriceSavingItemKey(null);
                        }
                      }
                    } else if (e.key === 'Escape') {
                      setPriceEditItem(null);
                    }
                  }}
                  className="text-xs font-bold text-[#201e1a] bg-white border border-gray-300 rounded px-1 py-0.5 w-16 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              ) : priceSavingItemKey === itemKey ? (
                <span className="flex items-center gap-1 text-xs font-bold text-[#201e1a]">
                  <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                  {t('cart.sidePanel.priceSaving', 'Saving...')}
                </span>
              ) : !readOnly && isPricePollStopped ? (
                <button
                  onClick={() => { if (recheckingItemKey) return; handleRecheck(item, itemKey); }}
                  disabled={recheckingItemKey === itemKey}
                  className="flex items-center gap-1 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ color: '#1E0DB8' }}
                  title={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                >
                  {recheckingItemKey === itemKey
                    ? <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                    : <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                        <path d="M13.6 8a5.6 5.6 0 1 1-1.7-4" strokeLinecap="round" />
                        <path d="M13.8 2.5V5.2H11.1" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>}
                  <span className="hover:underline">
                    {recheckingItemKey === itemKey
                      ? t('cart.sidePanel.inventory.checking', 'Checking...')
                      : t('cart.sidePanel.inventory.recheckShort', 'Re-check')}
                  </span>
                </button>
              ) : isPricePending ? (
                <button
                  onClick={() => { if (isSavingAny || readOnly) return; setPriceEditItem(item); setPriceEditValue(''); }}
                  disabled={isSavingAny || readOnly}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors group disabled:cursor-not-allowed disabled:opacity-60"
                  title={isSavingAny ? t('cart.sidePanel.savingInProgress', 'Saving…') : readOnly ? undefined : t('cart.sidePanel.priceEdit', 'Click to set price manually')}
                >
                  <Loader2 className="w-3 h-3 animate-spin group-hover:hidden" />
                  <span className="group-hover:underline">{t('cart.sidePanel.pricePending', 'Fetching price...')}</span>
                </button>
              ) : isPriceNotFound ? (
                <button
                  onClick={() => { if (isSavingAny || readOnly) return; setPriceEditItem(item); setPriceEditValue(''); }}
                  disabled={isSavingAny || readOnly}
                  className="flex items-center gap-1 text-xs text-amber-500 hover:text-blue-500 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  title={isSavingAny ? t('cart.sidePanel.savingInProgress', 'Saving…') : readOnly ? undefined : t('cart.sidePanel.priceEdit', 'Click to set price manually')}
                >
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                  <span className="hover:underline">{t('cart.sidePanel.priceNotFound', 'Price not found')}</span>
                </button>
              ) : effectivePriceNum !== null && (
                <span
                  className={`flex items-baseline gap-1 px-1 py-0.5 rounded ${isSavingAny || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-100'}`}
                  onClick={() => { if (isSavingAny || readOnly) return; setPriceEditItem(item); setPriceEditValue(String(effectivePriceNum)); }}
                  title={isSavingAny ? t('cart.sidePanel.savingInProgress', 'Saving…') : readOnly ? undefined : t('cart.sidePanel.priceEdit', 'Click to edit price')}
                >
                  {hasItemDiscount ? (
                    <>
                      <span className="text-xs text-gray-400 line-through">{priceDisplay}</span>
                      <span className="text-xs font-bold text-green-600">
                        {currencySymbol}{fmt(discountItem!.final_price)}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs font-bold text-[#201e1a]">{priceDisplay}</span>
                  )}
                </span>
              )}
            </div>

            {/* Over-stock warning: ordered more than the known available count. */}
            {cardOverStock && (
              <div className="flex items-center gap-1 text-[10px] font-semibold text-red-600">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                <span>{t('cart.sidePanel.table.maxAvailableTip', { defaultValue: 'Only {{qty}} in stock', qty: cardAvailableQty })}</span>
              </div>
            )}

            {/* Bottom: labor button + estimate */}
            <div className="flex items-center gap-1.5">
              {laborEst && (
                <div className="flex items-center gap-1 text-[10px] text-indigo-500 font-medium whitespace-nowrap">
                  {laborEst.repair_type && (
                    <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[10px] font-semibold text-indigo-600 uppercase tracking-wide">
                      {t(`cart.sidePanel.labor.repairTypes.${laborEst.repair_type}`, laborEst.repair_type)}
                    </span>
                  )}
                  <span>
                    {laborEst.labor_hours}h
                    {laborEst.labor_rate != null ? ` · ${currencySymbol}${fmt(laborEst.labor_hours * laborEst.labor_rate)}` : ''}
                  </span>
                </div>
              )}
              {laborEst && (
                <button
                  onClick={() => { if (isSavingAny || readOnly) return; setLaborEditPart(item); }}
                  disabled={isSavingAny || readOnly}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                >
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  {t('cart.sidePanel.labor.buttonEdit', 'Edit Hours')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Discount badge ── */}
        {hasItemDiscount && discountItem && (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-[10px] font-semibold text-green-700">
              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 4.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 11H11a3 3 0 013 3 1 1 0 102 0 5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" /></svg>
              {discountItem.discount_type === 'percentage'
                ? `-${discountItem.discount_value}%`
                : `-${currencySymbol}${fmt(discountItem.discount_value)}`}
              {discountItem.scope_type && discountItem.scope_type !== 'b2b_default' && (
                <span className="text-green-500 font-normal">
                  · {discountItem.scope_type}: {discountItem.scope_value}
                </span>
              )}
              {discountItem.scope_type === 'b2b_default' && (
                <span className="text-green-500 font-normal">· {t('cart.sidePanel.discount.b2b', 'B2B')}</span>
              )}
            </span>
          </div>
        )}

        {/* ── Per-item manual line discount ── */}
        {!readOnly && !isNotFound && !isServiceItem(item) && effectivePriceNum !== null && onLineDiscountUpdate && (
          <div className="flex items-center gap-1.5 mt-1.5">
            {lineDiscountEditKey === itemKey ? (
              <div className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5 w-full">
                {/* Type toggle */}
                <button
                  onClick={() => setLineDiscountEditType(prev => prev === 'percentage' ? 'fixed' : 'percentage')}
                  className="flex-shrink-0 text-[10px] font-bold text-orange-600 bg-orange-100 hover:bg-orange-200 px-1.5 py-0.5 rounded transition-colors"
                  title={t('cart.sidePanel.lineDiscount.toggleType', 'Toggle discount type')}
                >
                  {lineDiscountEditType === 'percentage' ? '%' : currencySymbol}
                </button>
                <input
                  type="number"
                  min="0"
                  max={lineDiscountEditType === 'percentage' ? 100 : undefined}
                  step="0.01"
                  value={lineDiscountEditValue}
                  onChange={(e) => setLineDiscountEditValue(e.target.value)}
                  className="flex-1 text-xs font-bold text-[#201e1a] bg-white border border-orange-200 rounded px-1 py-0.5 w-12 focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
                  autoFocus
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const v = parseFloat(lineDiscountEditValue) || 0;
                      setLineDiscountEditKey(null);
                      if (onLineDiscountUpdate) {
                        if (onWorkflowLog) onWorkflowLog(
                          t('cart.workflow.lineDiscountUpdated', { name: displayName, value: v, type: lineDiscountEditType, defaultValue: `Line discount set for "{{name}}": {{value}}{{type}}` }),
                          'edit_discount'
                        );
                        setLineDiscountSavingKey(itemKey);
                        try { await onLineDiscountUpdate(item, v > 0 ? v : null, lineDiscountEditType); }
                        finally { setLineDiscountSavingKey(null); }
                      }
                    } else if (e.key === 'Escape') {
                      setLineDiscountEditKey(null);
                    }
                  }}
                  onBlur={async () => {
                    const v = parseFloat(lineDiscountEditValue) || 0;
                    setLineDiscountEditKey(null);
                    if (onLineDiscountUpdate) {
                      if (onWorkflowLog) onWorkflowLog(
                        t('cart.workflow.lineDiscountUpdated', { name: displayName, value: v, type: lineDiscountEditType, defaultValue: `Line discount set for "{{name}}": {{value}}{{type}}` }),
                        'edit_discount'
                      );
                      setLineDiscountSavingKey(itemKey);
                      try { await onLineDiscountUpdate(item, v > 0 ? v : null, lineDiscountEditType); }
                      finally { setLineDiscountSavingKey(null); }
                    }
                  }}
                />
                <span className="text-[10px] text-orange-500 whitespace-nowrap">
                  {lineDiscountEditType === 'percentage' ? '%' : currencySymbol}
                  {lineDiscountEditType === 'percentage' && parseFloat(lineDiscountEditValue) > 0 && effectivePriceNum !== null
                    ? ` = ${currencySymbol}${fmt(effectivePriceNum * parseFloat(lineDiscountEditValue) / 100)}`
                    : ''}
                </span>
                <button
                  onClick={() => setLineDiscountEditKey(null)}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : lineDiscountSavingKey === itemKey ? (
              <span className="flex items-center gap-1 text-[10px] text-orange-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                {t('cart.sidePanel.lineDiscount.saving', 'Saving...')}
              </span>
            ) : hasLineDiscount ? (
              <button
                onClick={() => {
                  if (isSavingAny) return;
                  setLineDiscountEditKey(itemKey);
                  setLineDiscountEditValue(String(existingLineDiscount!.value));
                  setLineDiscountEditType(existingLineDiscount!.type);
                }}
                disabled={isSavingAny}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-[10px] font-semibold text-orange-700 hover:bg-orange-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title={t('cart.sidePanel.lineDiscount.editTitle', 'Click to edit line discount')}
              >
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 4.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 11H11a3 3 0 013 3 1 1 0 102 0 5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" /></svg>
                {existingLineDiscount!.type === 'percentage'
                  ? `-${existingLineDiscount!.value}%`
                  : `-${currencySymbol}${fmt(existingLineDiscount!.value)}`}
                {effectivePriceAfterLineDiscount !== null && (
                  <span className="font-normal text-orange-500">
                    → {currencySymbol}{fmt(effectivePriceAfterLineDiscount)}
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={() => {
                  if (isSavingAny) return;
                  setLineDiscountEditKey(itemKey);
                  setLineDiscountEditValue('0');
                  setLineDiscountEditType('percentage');
                }}
                disabled={isSavingAny}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 hover:bg-orange-50 hover:text-orange-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                title={t('cart.sidePanel.lineDiscount.addTitle', 'Add a manual discount for this item')}
              >
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 4.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 11H11a3 3 0 013 3 1 1 0 102 0 5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" /></svg>
                {t('cart.sidePanel.lineDiscount.add', 'Add Discount')}
              </button>
            )}
          </div>
        )}

        {/* ── Multiple pricing options selector ── */}
        {hasMultiplePricing && !(itemKey in manualPrices) && (
          <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-2">
            <p className="text-[10px] font-semibold text-blue-600 mb-1.5">
              {t('cart.sidePanel.multipleOptions', 'Multiple price options found — select one:')}
            </p>
            {/* Confirmation prompt */}
            {confirmPricingOption?.itemKey === itemKey ? (
              <div className="bg-white border border-blue-300 rounded-md px-3 py-2.5 space-y-2">
                <p className="text-xs font-medium text-gray-700">
                  {t('cart.sidePanel.confirmOption', 'Set price to')}{' '}
                  <span className="font-bold text-blue-700">
                    {currencySymbol}{fmt(confirmPricingOption!.option.price)}
                  </span>{' '}
                  ({confirmPricingOption!.option.label})?
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      if (!confirmPricingOption) return;
                      const { itemKey: key, item: it, option } = confirmPricingOption;
                      setSelectedPricingIndices(prev => ({ ...prev, [key]: `${option.idx}:${option.type}` }));
                      // Set manual price so the selector hides immediately
                      setManualPrices(prev => ({ ...prev, [key]: option.price }));
                      setConfirmPricingOption(null);
                      if (onWorkflowLog) onWorkflowLog(t('cart.workflow.priceOptionSelected', { name: it.translated_description || it.structured_data?.part_data?.description || key, label: option.label, price: option.price, defaultValue: `Price option selected for "{{name}}": {{label}} = {{price}}` }), 'edit_price');
                      if (onPriceUpdate) {
                        setPriceSavingItemKey(key);
                        try {
                          await onPriceUpdate(it, option.price);
                        } finally {
                          setPriceSavingItemKey(null);
                        }
                      }
                    }}
                    className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    {t('cart.sidePanel.confirmYes', 'Yes, set price')}
                  </button>
                  <button
                    onClick={() => setConfirmPricingOption(null)}
                    className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {t('cart.sidePanel.confirmNo', 'Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {options.map((option) => {
                  const isSelected = selectedOption?.idx === option.idx && selectedOption?.type === option.type;
                  return (
                    <button
                      key={`${option.idx}-${option.type}`}
                      onClick={() => setConfirmPricingOption({ itemKey, item, option })}
                      className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-xs transition-colors border ${isSelected
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:text-blue-700'
                        }`}
                    >
                      <span className="truncate text-left">
                        {option.label}
                      </span>
                      <span className="font-bold flex-shrink-0">
                        {`${currencySymbol}${fmt(option.price)}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Price & availability (integration_data) ──
            Preferred path: the backend enriches parts with `integration_data`
            (DMS / importer). It carries price as well as stock, so we render it
            whenever the data is present — its presence is the gate, independent
            of the legacy `inventory_data` org feature flag. */}
        {!isNotFound && !isServiceItem(item) && getIntegrationData(item) !== undefined && (
          <IntegrationAvailability
            view={deriveAvailability(getIntegrationData(item), availabilityConfig)}
            t={t}
            variant="card"
            selectedType={dmsTypeFor(item, itemKey)}
            selectedLocation={dmsLocationFor(item, itemKey)}
            onSelectLocation={readOnly ? undefined : (g, loc) => selectDmsLocation(item, itemKey, g, loc, displayName)}
            formatPriceFn={(n) => formatPrice(n, numberFormat)}
            savingType={priceSavingItemKey === itemKey}
            priceKnown={getItemUnitPrice(item, index) !== null}
            onSelectType={readOnly ? undefined : (g) => selectDmsPriceType(item, itemKey, g, displayName)}
            onRecheck={readOnly ? undefined : () => handleRecheck(item, itemKey)}
            rechecking={recheckingItemKey === itemKey}
          />
        )}

        {/* ── Delivery time (spec §6) — per-line manual picker, shown on EVERY part
            line when the feature is on, independent of stock/availability data. */}
        {(() => {
          const dd = deliveryDaysFor(item, itemKey);
          return !isNotFound && !isServiceItem(item) && dd != null ? (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
              <DeliveryPicker
                days={dd}
                options={deliveryOptions}
                onSelect={readOnly ? undefined : (days) => selectDeliveryDays(item, itemKey, days)}
                t={t}
              />
            </div>
          ) : null;
        })()}

        {/* ── Inventory data (legacy) ── */}
        {inventoryEnabled && !isNotFound && !isServiceItem(item) && !isDatasourceItem(item) && getIntegrationData(item) === undefined && (() => {
          const inventoryFieldExists = 'inventory_data' in item;
          const inventoryArr: any[] = Array.isArray(item.inventory_data) ? item.inventory_data : [];
          // Still waiting for inventory_data from server
          if (!inventoryFieldExists || item.inventory_data === undefined) {
            return (
              <div className="mt-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-2">
                <p className="text-[10px] font-semibold text-gray-400 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" /></svg>
                  {t('cart.sidePanel.inventory.title', 'Inventory')}
                </p>
                <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400">
                  {pendingPollStopped ? (
                    <>
                      {t('cart.sidePanel.inventory.noDataYet', 'No availability data yet')}
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRecheck(item, itemKey); }}
                          disabled={recheckingItemKey === itemKey}
                          title={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                          aria-label={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', padding: 0, cursor: recheckingItemKey === itemKey ? 'default' : 'pointer', color: '#1E0DB8', fontWeight: 700, fontSize: '10px', opacity: recheckingItemKey === itemKey ? 0.6 : 1 }}
                        >
                          {recheckingItemKey === itemKey
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                              <path d="M13.6 8a5.6 5.6 0 1 1-1.7-4" strokeLinecap="round" />
                              <path d="M13.8 2.5V5.2H11.1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>}
                          {recheckingItemKey === itemKey
                            ? t('cart.sidePanel.inventory.checking', 'Checking...')
                            : t('cart.sidePanel.inventory.recheckShort', 'Re-check')}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                      {t('cart.sidePanel.inventory.checking', 'Checking inventory...')}
                    </>
                  )}
                </div>
              </div>
            );
          }
          // inventory_data exists but is empty string → "no inventory data"
          if (inventoryFieldExists && item.inventory_data === '') {
            return (
              <div className="mt-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-2">
                <p className="text-[10px] font-semibold text-gray-400 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" /></svg>
                  {t('cart.sidePanel.inventory.title', 'Inventory')}
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {t('cart.sidePanel.inventory.noData', 'No inventory data')}
                </p>
              </div>
            );
          }
          if (!inventoryArr.length) return null;
          return (
            <div className="mt-2 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-2 space-y-1.5">
              <p className="text-[10px] font-semibold text-gray-400 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" /></svg>
                {t('cart.sidePanel.inventory.title', 'Inventory')}
              </p>
              {inventoryArr.map((entry: any, i: number) => {
                const stockQty: number | null = entry.stock_qty ?? null;
                const superSession: string | null = entry.super_session ?? null;
                const superSessionQty: number | null = entry.super_session_qty ?? null;
                // When a superSession exists, use its qty as the effective stock
                const effectiveQty = superSession ? superSessionQty : stockQty;
                return (
                  <div key={i} className="space-y-1">
                    {/* Stock row — shows superSession part number replacing original when available */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {superSession ? (
                          <>
                            {/* Original part number struck through */}
                            {entry.part_number && (
                              <span className="font-mono text-[10px] text-gray-300 line-through flex-shrink-0">
                                {entry.part_number}
                              </span>
                            )}
                            {/* Supersession replacement */}
                            <span className="font-mono text-[10px] font-semibold text-blue-600 flex-shrink-0">
                              {superSession}
                            </span>
                          </>
                        ) : null}
                        <span className={`text-[10px] font-semibold tabular-nums flex-shrink-0 ${effectiveQty === null ? 'text-gray-300' : effectiveQty > 0 ? 'text-green-600' : 'text-red-400'}`}>
                          {effectiveQty === null ? '--' : effectiveQty > 0 ? t('cart.sidePanel.inventory.inStock', 'In Stock') : t('cart.sidePanel.inventory.outOfStock', 'Out of stock')}
                        </span>
                      </div>
                      {effectiveQty !== null && effectiveQty > 0 && (
                        <span className="relative group flex-shrink-0 cursor-default">
                          <svg className="w-3 h-3 text-gray-400 hover:text-gray-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" /></svg>
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded bg-gray-800 text-white text-[10px] font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                            {effectiveQty} {t('cart.sidePanel.inventory.units', 'units')}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ── AI labor note (service items) ── */}
        {isServiceItem(item) && laborEst?.labor_note && (
          <div className="mt-1.5 flex items-start gap-1.5 bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-2">
            <Sparkles className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-indigo-600 leading-relaxed">{laborEst.labor_note}</span>
          </div>
        )}

        {/* ── Service item: labor summary ── */}
        {isServiceItem(item) && laborEst && (
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            {laborEst.repair_type && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-xs font-semibold text-indigo-600 uppercase tracking-wide">
                {t(`cart.sidePanel.labor.repairTypes.${laborEst.repair_type}`, laborEst.repair_type)}
              </span>
            )}
            <span className="text-xs text-indigo-500 font-medium">
              {laborEst.labor_hours}h
              {laborEst.labor_rate != null
                ? ` · ${currencySymbol}${fmt(laborEst.labor_hours * laborEst.labor_rate)}`
                : ''}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderSectionHeader = (
    icon: React.ReactNode,
    title: string,
    count: number,
    sectionKey: string,
    _accentColor: string = 'blue'
  ) => {
    const isCollapsed = collapsedSections[sectionKey];

    return (
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between mb-2 hover:opacity-80 transition-opacity"
      >
        <div className="flex items-center gap-2">
          <span className="text-[#201e1a]">
            {icon}
          </span>
          <span className="text-xs font-semibold text-[#201e1a]">
            {title} ({count})
          </span>
        </div>
        {isCollapsed
          ? <ChevronRight className="w-4 h-4 text-[#999999]" />
          : <ChevronDown className="w-4 h-4 text-[#999999]" />
        }
      </button>
    );
  };

  // Net effective unit price for an item: manual override (or the selected
  // pricing option) minus any per-item line discount — mirrors the card +
  // summary math so the table agrees with them.
  const getItemUnitPrice = (item: any, index: number): number | null => {
    const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
    const description = partData.description || partData.DESCRIPTION || '';
    const itemKey = item.id || item.part_id || partData.part_number || `${description}-${index}`;
    const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
    const options = getPriceOptions(pricingArr, t, numberFormat);
    const selectedStr = selectedPricingIndices[itemKey];
    let selectedOption: PriceOption | null = null;
    if (selectedStr) {
      const [idxStr, type] = selectedStr.split(':');
      selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
    }
    if (!selectedOption && options.length > 0) selectedOption = options[0];
    const base = itemKey in manualPrices ? manualPrices[itemKey] : (selectedOption?.price ?? getIntegrationPrice(item, availabilityConfig, numberFormat));
    if (base === null || base === undefined) return null;
    const ld = partData.line_discount as { value: number; type: 'percentage' | 'fixed' } | undefined;
    if (ld && ld.value > 0) {
      return ld.type === 'percentage' ? Math.max(0, base * (1 - ld.value / 100)) : Math.max(0, base - ld.value);
    }
    return base;
  };

  /** Base unit price BEFORE any manual line discount — used to seed the table
   *  editor's price field and to preview a percentage discount amount. */
  const getItemBasePrice = (item: any, index: number): number | null => {
    const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
    const description = partData.description || partData.DESCRIPTION || '';
    const itemKey = item.id || item.part_id || partData.part_number || `${description}-${index}`;
    const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
    const options = getPriceOptions(pricingArr, t, numberFormat);
    const selectedStr = selectedPricingIndices[itemKey];
    let selectedOption: PriceOption | null = null;
    if (selectedStr) {
      const [idxStr, type] = selectedStr.split(':');
      selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
    }
    if (!selectedOption && options.length > 0) selectedOption = options[0];
    return itemKey in manualPrices ? manualPrices[itemKey] : (selectedOption?.price ?? getIntegrationPrice(item, availabilityConfig, numberFormat));
  };

  /** Switch a DMS line to its "original" or "alternative" price. The chosen price
   *  becomes the line's manual price so it flows into the line/order total, and is
   *  persisted through the same onPriceUpdate path as the manual price editor. */
  const selectDmsPriceType = async (item: any, itemKey: string, group: DmsTypeGroup, displayName: string) => {
    if (readOnly) return;
    // Selecting a line-type switches BOTH its inventory/stock (via the active type)
    // AND its price. A type with no price (synthesized/empty) switches the line to
    // an unknown price (null) so the UI reflects "Unknown Price · Not in Stock" for
    // that type — never leaves the previous type's price showing. group.price is
    // the raw DMS value — normalize dot-format to the real amount so the stored
    // manual price agrees with every other price source.
    const newPrice = group.price != null
      ? (numberFormat === 'dot' ? toRealDotAmount(group.price) : group.price)
      : null;
    setSelectedDmsType(prev => ({ ...prev, [itemKey]: group.type }));
    // A new line-type has its own warehouses — fall back to its default location.
    setSelectedDmsLocation(prev => ({ ...prev, [itemKey]: null }));
    setManualPrices(prev => ({ ...prev, [itemKey]: newPrice }));
    if (onWorkflowLog) onWorkflowLog(
      t('cart.workflow.priceOptionSelected', { name: displayName, label: group.type, price: newPrice ?? '—', defaultValue: `Price option selected for "{{name}}": {{label}} = {{price}}` }),
      'edit_price',
    );
    if (onPriceUpdate) {
      setPriceSavingItemKey(itemKey);
      try { await onPriceUpdate(item, newPrice, { dms_type: group.type, dms_location: null }); } finally { setPriceSavingItemKey(null); }
    }
  };

  /** Price a DMS line from a specific warehouse / point of sale. The same part can
   *  cost differently per point of sale, so the location's OWN price becomes the
   *  line price (falling back to the line-type price when that row has none), and
   *  the choice is persisted with it so the quote shows where it is bought from. */
  const selectDmsLocation = async (item: any, itemKey: string, group: DmsTypeGroup, loc: AvailLocation, displayName: string) => {
    if (readOnly) return;
    const raw = priceForLocation(group, loc);
    const newPrice = raw != null ? (numberFormat === 'dot' ? toRealDotAmount(raw) : raw) : null;
    setSelectedDmsType(prev => ({ ...prev, [itemKey]: group.type }));
    setSelectedDmsLocation(prev => ({ ...prev, [itemKey]: loc.key }));
    setManualPrices(prev => ({ ...prev, [itemKey]: newPrice }));
    if (onWorkflowLog) onWorkflowLog(
      t('cart.workflow.pointOfSaleSelected', { name: displayName, location: loc.label || loc.key, price: newPrice ?? '—', defaultValue: `Point of sale selected for "{{name}}": {{location}} = {{price}}` }),
      'edit_price',
    );
    if (onPriceUpdate) {
      setPriceSavingItemKey(itemKey);
      try { await onPriceUpdate(item, newPrice, { dms_type: group.type, dms_location: loc.key }); } finally { setPriceSavingItemKey(null); }
    }
  };

  /** Toggle the inline editor for a table row, seeding drafts from current values. */
  const openTableEditor = (item: any, index: number) => {
    const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
    const description = partData.description || partData.DESCRIPTION || '';
    const itemKey = item.id || item.part_id || partData.part_number || `${description}-${index}`;
    if (tableEditKey === itemKey) { setTableEditKey(null); return; }
    const base = getItemBasePrice(item, index);
    const ld = partData.line_discount as { value: number; type: 'percentage' | 'fixed' } | undefined;
    setTableEditPrice(base != null ? String(base) : '');
    setTableEditDiscVal(ld && ld.value > 0 ? String(ld.value) : '');
    setTableEditDiscType(ld?.type ?? 'percentage');
    setTableEditDesc(item.translated_description || partData.description || partData.DESCRIPTION || '');
    setTableEditKey(itemKey);
  };

  /** Commit handlers reuse the exact same server calls / optimistic state / audit
   *  logging as the card view's inline editors. */
  const commitTablePrice = async (item: any, itemKey: string, displayName: string) => {
    const newPrice = parsePrice(tableEditPrice, numberFormat);
    setManualPrices(prev => ({ ...prev, [itemKey]: newPrice }));
    if (!onPriceUpdate) return;
    if (onWorkflowLog) onWorkflowLog(t('cart.workflow.priceUpdated', { name: displayName, value: newPrice !== null ? newPrice : t('cart.workflow.cleared', 'cleared'), defaultValue: `Price updated for "{{name}}": {{value}}` }), 'edit_price');
    setPriceSavingItemKey(itemKey);
    try { await onPriceUpdate(item, newPrice); } finally { setPriceSavingItemKey(null); }
  };

  const commitTableDiscount = async (item: any, itemKey: string, displayName: string) => {
    if (!onLineDiscountUpdate) return;
    const v = parseFloat(tableEditDiscVal) || 0;
    if (onWorkflowLog) onWorkflowLog(t('cart.workflow.lineDiscountUpdated', { name: displayName, value: v, type: tableEditDiscType, defaultValue: `Line discount set for "{{name}}": {{value}}{{type}}` }), 'edit_discount');
    setLineDiscountSavingKey(itemKey);
    try { await onLineDiscountUpdate(item, v > 0 ? v : null, tableEditDiscType); } finally { setLineDiscountSavingKey(null); }
  };

  const commitTableDesc = async (item: any, itemKey: string, partId: string, displayName: string) => {
    const trimmed = tableEditDesc.trim();
    if (!trimmed || trimmed === displayName || !onDescriptionUpdate) return;
    if (onWorkflowLog) onWorkflowLog(t('cart.workflow.descriptionUpdated', { part: partId || itemKey, from: displayName, to: trimmed, defaultValue: `Description updated for part "{{part}}": "{{from}}" → "{{to}}"` }), 'edit_description');
    setDescSavingItemKey(itemKey);
    try { await onDescriptionUpdate(item, trimmed); } finally { setDescSavingItemKey(null); }
  };

  // Export the cart as a CSV with the SAME columns/data the table shows — OE#,
  // Requested, Description, Qty, net Unit price, Line total — using the live
  // manual price/option/line-discount overrides via getItemUnitPrice. Prices are
  // raw numbers (no symbol/separator) so a spreadsheet can sum them; the currency
  // code is in the header. A UTF-8 BOM keeps Excel's accents correct. In dot-format
  // orgs a value may still be a misparsed dot-thousands decimal (7.817 = 7,817);
  // toRealDotAmount recovers it while leaving already-real integers (693975)
  // untouched, and the real amount is never rounded away (see formatPrice).
  const csvNum = (v: number) => numberFormat === 'dot' ? Math.round(toRealDotAmount(v)) : Math.round(v * 100) / 100;
  const handleDownloadCartCsv = () => {
    const escapeCsv = (val: unknown) => {
      const s = (val ?? '').toString();
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const cur = currentUser?.currency || '';
    const curSuffix = cur ? ` (${cur})` : '';
    const headers = [
      t('cart.sidePanel.table.oeNumber', { defaultValue: 'OE#' }),
      t('cart.sidePanel.table.requested', { defaultValue: 'Requested' }),
      t('cart.sidePanel.table.description', { defaultValue: 'Description' }),
      t('cart.sidePanel.table.qty', { defaultValue: 'Qty' }),
      `${t('cart.sidePanel.table.unitPrice', { defaultValue: 'Unit' })}${curSuffix}`,
      `${t('cart.sidePanel.table.total', { defaultValue: 'Total' })}${curSuffix}`,
    ];
    const rows = [...parts, ...notFoundParts, ...services].map((item, i) => {
      const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
      const description = partData.description || partData.DESCRIPTION || '';
      const displayName = item.translated_description || description;
      const oe = partData.part_number || '';
      const requested = requestedTermFor(item);
      const qty = item.quantity || 1;
      const unit = isNotFoundItem(item) ? null : getItemUnitPrice(item, i);
      const total = unit !== null ? unit * qty : null;
      return [oe, requested, displayName, qty, unit !== null ? csvNum(unit) : '', total !== null ? csvNum(total) : ''];
    });
    const csv = [headers, ...rows].map(r => r.map(escapeCsv).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const orderId = getOrderObject()?.external_order_id || getActiveOrderId() || 'cart';
    a.download = `cart-${orderId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Compact table view of every cart item (parts → not-found → services), an
  // alternative to the rich card list. Columns mirror what the cards surface:
  // OE#, description (+ original when translated), qty, unit price, line total.
  // Qty steppers + remove stay available unless the order is read-only.
  const renderCartTable = () => {
    const th: React.CSSProperties = { fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9a958c', padding: '8px 8px', textAlign: 'left', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '8px 8px', verticalAlign: 'top', borderTop: '1px solid #f0ede9' };
    const stepBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', padding: 0, border: '1px solid #e5e3df', borderRadius: '5px', background: '#ffffff', cursor: 'pointer', color: '#6b6660' };
    const editorLabel: React.CSSProperties = { display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9a958c', marginBottom: '3px' };
    const editorInput: React.CSSProperties = { fontSize: '12px', color: '#201e1a', border: '1px solid #e0ddd6', borderRadius: '6px', padding: '5px 7px', background: '#fff', outline: 'none' };
    const rows = [...parts, ...notFoundParts, ...services];
    if (rows.length === 0) {
      return (
        <div className="flex items-center gap-2 py-4 px-1">
          <ShoppingCart className="w-4 h-4 text-[#999999] flex-shrink-0" />
          <span className="text-xs text-[#999999] italic">{t('cart.sidePanel.table.empty', { defaultValue: 'Your cart is empty' })}</span>
        </div>
      );
    }
    return (
      <div
        className="notranslate"
        // translate="no": browser auto-translate (Google Translate) rewrites text
        // nodes inside these rows with <font> wrappers; React then crashes on
        // reconciliation with "insertBefore … not a child of this node". Row content
        // is already localized server-side via client_language.
        translate="no"
        style={{ border: '1px solid #ebe7df', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#ffffff' }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '464px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: `${CART_IMAGE_PRESENTATION.table.width + 16}px` }} />{/* Image */}
              <col style={{ width: '92px' }} />{/* OE# */}
              <col />{/* Description (+ requested term as sub-line) */}
              <col style={{ width: readOnly ? '36px' : '72px' }} />{/* Qty */}
              <col style={{ width: '84px' }} />{/* Unit */}
              <col style={{ width: '84px' }} />{/* Total */}
              {!readOnly && <col style={{ width: '52px' }} />}{/* Edit + Remove */}
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#f6f4f0' }}>
                <th style={th} aria-hidden="true" />{/* Image */}
                <th style={th}>{t('cart.sidePanel.table.oeNumber', { defaultValue: 'OE#' })}</th>
                <th style={th}>{t('cart.sidePanel.table.description', { defaultValue: 'Description' })}</th>
                <th style={{ ...th, textAlign: 'center' }}>{t('cart.sidePanel.table.qty', { defaultValue: 'Qty' })}</th>
                <th style={{ ...th, textAlign: 'right', paddingLeft: '4px', paddingRight: '6px' }}>{t('cart.sidePanel.table.unitPrice', { defaultValue: 'Unit' })}</th>
                <th style={{ ...th, textAlign: 'right', paddingLeft: '4px', paddingRight: '6px' }}>{t('cart.sidePanel.table.total', { defaultValue: 'Total' })}</th>
                {!readOnly && <th style={th} aria-hidden="true" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((item, i) => {
                const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
                const description = partData.description || partData.DESCRIPTION || t('cart.sidePanel.noDescription');
                const itemKey = item.id || item.part_id || partData.part_number || `${description}-${i}`;
                const translatedDesc = item.translated_description;
                const displayName = translatedDesc || description;
                const subDescription = translatedDesc && translatedDesc !== description ? description : null;
                const oe = partData.part_number || '—';
                const requested = requestedTermFor(item) || '—';
                // Only surface the requested term when it adds information — i.e. it
                // differs from the description we're already showing. When the user's
                // search term IS the description (e.g. "Capó" → "Capó"), it's just noise.
                const norm = (s: string) => s.trim().toLowerCase();
                const showRequested = requested !== '—'
                  && norm(requested) !== norm(displayName)
                  && (!subDescription || norm(requested) !== norm(subDescription));
                const qty = item.quantity || 1;
                const notFound = isNotFoundItem(item);
                const service = isServiceItem(item);
                const unit = notFound ? null : getItemUnitPrice(item, i);
                const lineTotal = unit !== null ? unit * qty : null;
                const isRemoving = removingItemId === itemKey;
                const basePrice = getItemBasePrice(item, i);
                const laborEst = partData.labor_estimate as LaborEstimate | undefined;
                const isEditing = !readOnly && tableEditKey === itemKey;
                // Availability + price source. Preferred: integration_data (DMS /
                // importer). Legacy fallback: the old inventory_data array.
                const integrationRaw = getIntegrationData(item);
                const hasIntegration = integrationRaw !== undefined;
                const integrationView = hasIntegration ? deriveAvailability(integrationRaw, availabilityConfig) : null;
                // Price still being fetched from the server (mirrors the card view).
                // integration_data arriving also resolves the pending price spinner.
                const pricingFieldExists = 'pricing_data' in item;
                const pricePendingBase = !notFound && !service && !pricingFieldExists && !hasIntegration && !(itemKey in manualPrices) && waitForPricing;
                // Poller exhausted its budget with no data — swap the endless spinner for a Re-check.
                const isPricePollStopped = pricePendingBase && pendingPollStopped;
                const isPricePending = pricePendingBase && !pendingPollStopped;
                const invFieldExists = 'inventory_data' in item;
                const invArr: any[] = Array.isArray(item.inventory_data) ? item.inventory_data : [];
                // Pending until either availability source has arrived.
                const invPending = inventoryEnabled && !notFound && !service
                  && !hasIntegration && !isDatasourceItem(item)
                  && (!invFieldExists || item.inventory_data === undefined);
                const invEntry = invArr[0];
                const invQty: number | null = invEntry ? ((invEntry.super_session ? invEntry.super_session_qty : invEntry.stock_qty) ?? null) : null;
                const invSuper: string | null = invEntry?.super_session ?? null;
                // Supersession: the supplier/DMS SKU (or legacy super_session) that
                // REPLACES the requested OE number. When present, the OE# column
                // shows the original struck through with the supersession below it.
                // Compare NORMALIZED (strip apostrophes/dashes/spaces, uppercase) so a
                // cosmetic-only difference — e.g. "263202R001" vs "'263202R001" — is
                // NOT treated as a supersession.
                const normSku = (s: string) => s.replace(/[^0-9a-z]/gi, '').toUpperCase();
                const supplierSku = supplierSkuForType(integrationView, dmsTypeFor(item, itemKey)) || invSuper;
                const supersession = oe !== '—' && supplierSku && normSku(String(supplierSku)) !== normSku(String(oe))
                  ? String(supplierSku)
                  : null;
                // Max orderable stock for the selected line-type (known DMS count
                // only; importer/unknown → null). Warn — don't block — when the
                // ordered qty exceeds a known, positive available count.
                const availableQty = availableQtyForType(integrationView, dmsTypeFor(item, itemKey), invQty, dmsLocationFor(item, itemKey));
                const overStock = !readOnly && !notFound && !service && availableQty != null && availableQty > 0 && qty > availableQty;
                return (
                  <React.Fragment key={`cart-row-${itemKey}-${i}`}>
                    <tr style={isEditing ? { backgroundColor: '#faf9f7' } : undefined}>
                      <td style={{ ...td, paddingLeft: '10px', paddingRight: '2px' }}>
                        {item.image ? (
                          <button
                            type="button"
                            onClick={() => setZoomedImage(item.image)}
                            title={t('cart.sidePanel.table.zoomImage', { defaultValue: 'Zoom image' })}
                            style={{ width: CART_IMAGE_PRESENTATION.table.width, height: CART_IMAGE_PRESENTATION.table.height, borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2ded7', background: '#fff', padding: '4px', cursor: 'zoom-in', display: 'block' }}
                          >
                            <img
                              src={item.image}
                              alt={displayName}
                              style={{ width: '100%', height: '100%', objectFit: CART_IMAGE_PRESENTATION.fit, display: 'block' }}
                              onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                            />
                          </button>
                        ) : (
                          <div style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #f0ede9', background: '#faf9f7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Package size={13} color="#d3cfc7" />
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, fontSize: '11px', fontWeight: 700, color: '#201e1a', fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={oe !== '—' ? (supersession ? `${oe} → ${supersession}` : oe) : undefined}>
                        {supersession ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#9a958c', fontWeight: 500 }}>{oe}</span>
                            <span style={{ display: 'block', color: '#1E0DB8', marginTop: 2 }}>{supersession}</span>
                          </>
                        ) : oe}
                      </td>
                      <td style={{ ...td, fontSize: '11px', color: '#201e1a', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        <div style={{ fontWeight: 600 }}>{displayName}</div>
                        {subDescription && <div style={{ fontSize: '10px', color: '#9a958c' }}>{subDescription}</div>}
                        {showRequested && (
                          <div style={{ fontSize: '10px', color: '#b8b3a9' }}>
                            {t('cart.sidePanel.table.requested', { defaultValue: 'Requested' })}: {requested}
                          </div>
                        )}
                        {isDatasourceItem(item) && (
                          <div style={{ fontSize: '10px', color: '#7c3aed', fontWeight: 600 }}>
                            {partData.supplier_name
                              ? t('cart.sidePanel.fromDataSource', { defaultValue: 'Data source: {{name}}', name: partData.supplier_name })
                              : t('cart.sidePanel.fromDataSourceGeneric', { defaultValue: 'From your data sources' })}
                          </div>
                        )}
                        {notFound && <div style={{ fontSize: '10px', color: '#b45309' }}>{t('cart.sidePanel.table.notFound', { defaultValue: 'Not found' })}</div>}
                        {/* Availability: checking spinner while pending, then price/stock. */}
                        {invPending ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '3px', fontSize: '10px', color: '#9a958c' }}>
                            {pendingPollStopped ? (
                              <>
                                {t('cart.sidePanel.inventory.noDataYet', 'No availability data yet')}
                                {!readOnly && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleRecheck(item, itemKey); }}
                                    disabled={recheckingItemKey === itemKey}
                                    title={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                                    aria-label={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', padding: 0, cursor: recheckingItemKey === itemKey ? 'default' : 'pointer', color: '#1E0DB8', fontWeight: 700, fontSize: '10px', opacity: recheckingItemKey === itemKey ? 0.6 : 1 }}
                                  >
                                    {recheckingItemKey === itemKey
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                                        <path d="M13.6 8a5.6 5.6 0 1 1-1.7-4" strokeLinecap="round" />
                                        <path d="M13.8 2.5V5.2H11.1" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>}
                                    {recheckingItemKey === itemKey
                                      ? t('cart.sidePanel.inventory.checking', 'Checking...')
                                      : t('cart.sidePanel.inventory.recheckShort', 'Re-check')}
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <Loader2 size={10} className="animate-spin" />
                                {t('cart.sidePanel.inventory.checking', 'Checking inventory...')}
                              </>
                            )}
                          </div>
                        ) : integrationView && !notFound && !service ? (
                          <IntegrationAvailability
                            view={integrationView}
                            t={t}
                            variant="table"
                            selectedType={dmsTypeFor(item, itemKey)}
                            selectedLocation={dmsLocationFor(item, itemKey)}
                            onSelectLocation={readOnly ? undefined : (g, loc) => selectDmsLocation(item, itemKey, g, loc, displayName)}
                            formatPriceFn={(n) => formatPrice(n, numberFormat)}
                            savingType={priceSavingItemKey === itemKey}
                            priceKnown={unit !== null}
                            onSelectType={readOnly ? undefined : (g) => selectDmsPriceType(item, itemKey, g, displayName)}
                            onRecheck={readOnly ? undefined : () => handleRecheck(item, itemKey)}
                            rechecking={recheckingItemKey === itemKey}
                          />
                        ) : invEntry ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px', marginTop: '3px' }}>
                            {invSuper && !supersession && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '10px', fontWeight: 600, color: '#2563eb' }} title={t('cart.sidePanel.inventory.title', 'Inventory')}>{invSuper}</span>}
                            <span style={{ fontSize: '10px', fontWeight: 600, color: invQty === null ? '#c4bfb6' : invQty > 0 ? '#16a34a' : '#dc2626' }} title={invQty !== null && invQty > 0 ? `${invQty} ${t('cart.sidePanel.inventory.units', 'units')}` : undefined}>
                              {invQty === null
                                ? '--'
                                : invQty > 0
                                  ? t('cart.sidePanel.inventory.inStock', 'In Stock')
                                  : t('cart.sidePanel.inventory.outOfStock', 'Out of stock')}
                            </span>
                          </div>
                        ) : null}
                        {/* Delivery time (spec §6) — per-line manual picker, on every
                          part row independent of stock/availability. */}
                        {(() => {
                          const dd = deliveryDaysFor(item, itemKey);
                          return !notFound && !service && dd != null ? (
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
                              <DeliveryPicker
                                days={dd}
                                options={deliveryOptions}
                                onSelect={readOnly ? undefined : (days) => selectDeliveryDays(item, itemKey, days)}
                                t={t}
                              />
                            </div>
                          ) : null;
                        })()}
                        {renderNoteLine(item, itemKey)}
                      </td>
                      <td style={{ ...td, textAlign: 'center', fontSize: '11px', color: '#201e1a' }}>
                        {readOnly || notFound || service ? (
                          <span>{qty}</span>
                        ) : (
                          <>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <button type="button" onClick={() => onUpdateQuantity(item, Math.max(1, qty - 1))} disabled={isSavingAny || qty <= 1} style={{ ...stepBtn, opacity: (isSavingAny || qty <= 1) ? 0.5 : 1 }} aria-label={t('cart.sidePanel.table.decrease', { defaultValue: 'Decrease quantity' })}>
                                <Minus size={11} />
                              </button>
                              <span style={{ minWidth: '14px', textAlign: 'center', color: overStock ? '#dc2626' : undefined, fontWeight: overStock ? 700 : undefined }}>{qty}</span>
                              <button type="button" onClick={() => onUpdateQuantity(item, qty + 1)} disabled={isSavingAny} style={{ ...stepBtn, opacity: isSavingAny ? 0.5 : 1 }} aria-label={t('cart.sidePanel.table.increase', { defaultValue: 'Increase quantity' })}>
                                <Plus size={11} />
                              </button>
                            </div>
                            {overStock && (
                              <div
                                style={{ marginTop: '4px', fontSize: '9px', fontWeight: 600, color: '#dc2626', lineHeight: 1.3, whiteSpace: 'normal' }}
                                title={t('cart.sidePanel.table.maxAvailableTip', { defaultValue: 'Only {{qty}} in stock', qty: availableQty })}
                              >
                                {t('cart.sidePanel.table.maxAvailable', { defaultValue: 'Max: {{qty}}', qty: availableQty })}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', paddingLeft: '4px', paddingRight: '6px', fontSize: '11px', color: '#201e1a', whiteSpace: 'nowrap' }}>
                        {unit !== null
                          ? `${currencySymbol}${fmt(unit)}`
                          : !readOnly && isPricePollStopped
                            ? <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); if (!recheckingItemKey) handleRecheck(item, itemKey); }}
                                disabled={recheckingItemKey === itemKey}
                                title={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                                aria-label={t('cart.sidePanel.inventory.recheck', 'Re-check availability')}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', padding: 0, cursor: recheckingItemKey === itemKey ? 'default' : 'pointer', color: '#1E0DB8', fontWeight: 700, fontSize: '10px', opacity: recheckingItemKey === itemKey ? 0.6 : 1 }}
                              >
                                {recheckingItemKey === itemKey
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                                      <path d="M13.6 8a5.6 5.6 0 1 1-1.7-4" strokeLinecap="round" />
                                      <path d="M13.8 2.5V5.2H11.1" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>}
                                {recheckingItemKey === itemKey
                                  ? t('cart.sidePanel.inventory.checking', 'Checking...')
                                  : t('cart.sidePanel.inventory.recheckShort', 'Re-check')}
                              </button>
                            : isPricePending
                              ? <span title={t('cart.sidePanel.pricePending', 'Fetching price...')} style={{ display: 'inline-flex', justifyContent: 'flex-end' }}><Loader2 size={12} className="animate-spin" color="#6866ff" /></span>
                              : '--'}
                      </td>
                      <td style={{ ...td, textAlign: 'right', paddingLeft: '4px', paddingRight: '6px', fontSize: '11px', fontWeight: 600, color: '#201e1a', whiteSpace: 'nowrap' }}>
                        {lineTotal !== null
                          ? `${currencySymbol}${fmt(lineTotal)}`
                          : isPricePending
                            ? <span title={t('cart.sidePanel.pricePending', 'Fetching price...')} style={{ display: 'inline-flex', justifyContent: 'flex-end' }}><Loader2 size={12} className="animate-spin" color="#6866ff" /></span>
                            : '--'}
                      </td>
                      {!readOnly && (
                        <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            <button type="button" onClick={() => openTableEditor(item, i)} aria-label={t('cart.sidePanel.table.edit', { defaultValue: 'Edit' })} title={t('cart.sidePanel.table.edit', { defaultValue: 'Edit' })} style={{ border: 'none', background: isEditing ? '#efedff' : 'transparent', borderRadius: '5px', cursor: 'pointer', color: isEditing ? '#6866ff' : '#8a857c', padding: '3px' }}>
                              <Pencil size={12} />
                            </button>
                            <button type="button" onClick={() => onRemovePart(item)} disabled={isRemoving} aria-label={t('cart.sidePanel.table.remove', { defaultValue: 'Remove' })} style={{ border: 'none', background: 'transparent', cursor: isRemoving ? 'not-allowed' : 'pointer', color: '#bd8c84', padding: '2px' }}>
                              {isRemoving ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    {isEditing && (
                      <tr>
                        <td colSpan={readOnly ? 6 : 7} style={{ padding: 0, borderTop: '1px solid #f0ede9', backgroundColor: '#faf9f7' }}>
                          <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px', animation: 'cartViewSwitch 0.2s ease-out' }}>
                            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              {!service && (
                                <div>
                                  <label style={editorLabel}>{t('cart.sidePanel.table.unitPrice', { defaultValue: 'Unit' })} ({currencySymbol})</label>
                                  <input
                                    type="number" min="0" step="0.01"
                                    value={tableEditPrice}
                                    onChange={(e) => setTableEditPrice(e.target.value)}
                                    onBlur={() => commitTablePrice(item, itemKey, displayName)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                    style={{ ...editorInput, width: '90px' }}
                                    placeholder="0"
                                  />
                                </div>
                              )}
                              {!service && !notFound && onLineDiscountUpdate && (
                                <div>
                                  <label style={editorLabel}>{t('cart.sidePanel.lineDiscount.label', { defaultValue: 'Discount' })}</label>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <button type="button" onClick={() => setTableEditDiscType(p => p === 'percentage' ? 'fixed' : 'percentage')} title={t('cart.sidePanel.lineDiscount.toggleType', { defaultValue: 'Toggle discount type' })} style={{ fontSize: '12px', fontWeight: 700, color: '#c2632b', background: '#fbeee2', border: '1px solid #f0d9c4', borderRadius: '5px', padding: '4px 8px', cursor: 'pointer', lineHeight: 1 }}>
                                      {tableEditDiscType === 'percentage' ? '%' : currencySymbol}
                                    </button>
                                    <input
                                      type="number" min="0" max={tableEditDiscType === 'percentage' ? 100 : undefined} step="0.01"
                                      value={tableEditDiscVal}
                                      onChange={(e) => setTableEditDiscVal(e.target.value)}
                                      onBlur={() => commitTableDiscount(item, itemKey, displayName)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                      style={{ ...editorInput, width: '58px' }}
                                      placeholder="0"
                                    />
                                    {tableEditDiscType === 'percentage' && parseFloat(tableEditDiscVal) > 0 && basePrice !== null && (
                                      <span style={{ fontSize: '11px', color: '#c2632b', whiteSpace: 'nowrap' }}>= {currencySymbol}{fmt(basePrice * parseFloat(tableEditDiscVal) / 100)}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {onLaborUpdate && (
                                <div>
                                  <label style={editorLabel}>{t('cart.sidePanel.labor.modalTitle', { defaultValue: 'Labor' })}</label>
                                  <button type="button" onClick={() => setLaborEditPart(item)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#4f46e5', background: '#eef2ff', border: '1px solid #e0e7ff', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer' }}>
                                    <Clock size={12} /> {laborEst && laborEst.labor_hours > 0 ? `${laborEst.labor_hours}h` : t('cart.sidePanel.labor.add', { defaultValue: 'Add' })}
                                  </button>
                                </div>
                              )}
                            </div>
                            {onDescriptionUpdate && (
                              <div>
                                <label style={editorLabel}>{t('cart.sidePanel.table.description', { defaultValue: 'Description' })}</label>
                                <input
                                  type="text"
                                  value={tableEditDesc}
                                  onChange={(e) => setTableEditDesc(e.target.value)}
                                  onBlur={() => commitTableDesc(item, itemKey, oe !== '—' ? oe : '', displayName)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                  style={{ ...editorInput, width: '100%' }}
                                />
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <button type="button" onClick={() => setTableEditKey(null)} style={{ fontSize: '11px', fontWeight: 600, color: '#6866ff', background: '#efedff', border: 'none', borderRadius: '6px', padding: '5px 14px', cursor: 'pointer' }}>
                                {t('common.done', { defaultValue: 'Done' })}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-[100]"
          onClick={onMobileClose}
        />
      )}

      {/* Cart Panel - Desktop: static in flex layout, Mobile: bottom drawer */}
      <div className={`
        ${isMobileOpen ? 'translate-y-0' : 'translate-y-full'}
        md:translate-y-0
        md:animate-slideInRight
        fixed md:relative
        bottom-0 md:bottom-auto
        left-0 right-0 md:left-auto md:right-auto
        w-full ${isMinimized ? 'md:w-14' : 'md:w-[28rem] lg:w-[34rem] xl:w-[42rem]'}
        md:flex-shrink-0
        ${isMinimized ? 'bg-transparent border-none shadow-none' : 'bg-white border-t md:border-t-0 md:border-l border-[#efebe2] shadow-xl md:shadow-none'}
        flex flex-col
        z-[101] md:z-10
        transition-all duration-300 ease-in-out
        max-h-[85vh] md:max-h-full md:h-full
      `}>
        {/* Minimized state - just a cart icon with badge */}
        {isMinimized ? (
          <div className="hidden md:flex flex-col items-center justify-start pt-4 h-full">
            <button
              onClick={() => setIsMinimized(false)}
              className="relative p-2.5 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
              title={t('cart.sidePanel.actions.expand', 'Expand cart')}
            >
              <ShoppingCart className="w-5 h-5 text-[#201e1a]" />
              {cartItems.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#6866ff] text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                  {cartItems.length}
                </span>
              )}
            </button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 animate-cart-expand">
            {/* Cart Header */}
            <div className={`px-3 md:px-4 py-3 border-b border-[#efebe2] bg-white flex-shrink-0`}>
              {/* Row 1: title + currency + close/minimize */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 text-[#201e1a]" />
                  <h2 className="text-xs md:text-sm font-semibold text-[#201e1a]">
                    {cartItems.length > 0 ? ` (${cartItems.length})` : ''}
                  </h2>
                  {currentUser?.currency && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#f6f4f0] border border-[#e5e3df] text-[10px] font-semibold text-[#6b6660]" title={t('cart.sidePanel.currency', 'Currency')}>
                      {currencySymbol} {currentUser.currency}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Minimize Button - Desktop only */}
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="hidden md:flex p-1.5 hover:bg-[#f6f4f0] rounded-lg transition-colors"
                    title={t('cart.sidePanel.actions.minimize', 'Minimize')}
                  >
                    <ChevronRight className="w-4 h-4 text-[#201e1a]" />
                  </button>
                  {/* Mobile Close Button */}
                  <button
                    onClick={onMobileClose}
                    className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-[#201e1a]" />
                  </button>
                </div>
              </div>
              {/* Row 2: action buttons */}
              <div className="flex items-center gap-1.5">
                {/* B2B Customer badge */}
                {(() => {
                  const customer = getOrderObject()?.b2b_customer;
                  if (!customer) return null;
                  return (
                    <div className="flex items-center gap-1 px-2 py-1 mb-1 bg-blue-50 border border-blue-100 rounded-lg text-[11px] text-blue-700 font-medium truncate max-w-full" title={customer.name}>
                      <Building2 className="w-3 h-3 flex-shrink-0 text-blue-500" />
                      <span className="truncate">{customer.alias || customer.name}</span>
                      {customer.tax_id && <span className="text-blue-400 font-normal flex-shrink-0">· {customer.tax_id}</span>}
                    </div>
                  );
                })()}
              </div>
              {/* Row 3: action buttons */}
              <div className="flex items-center gap-1.5">
                {/* AI Estimate Repairs Button */}
                {SHOW_ADD_LABOR && !readOnly && onEstimateRepairs && cartItems.filter(i => !isServiceItem(i) && !isNotFoundItem(i)).length > 0 && (
                  <button
                    onClick={async () => {
                      setIsEstimating(true);
                      try {
                        await onEstimateRepairs(cartItems);
                      } finally {
                        setIsEstimating(false);
                      }
                    }}
                    disabled={isEstimating}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors border border-indigo-200"
                    title={t('cart.sidePanel.actions.estimateRepairs', 'Auto-estimate repair hours with AI')}
                  >
                    {isEstimating
                      ? <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                      : <Sparkles className="w-3 h-3 flex-shrink-0" />
                    }
                    <span>
                      {isEstimating
                        ? t('cart.sidePanel.actions.estimating', 'Estimating...')
                        : t('cart.sidePanel.actions.estimateRepairs', 'Add Labor')}
                    </span>
                  </button>
                )}
                {/* Share Button */}
                {getOrderObject() && cartItems.length > 0 && (
                  <div className="relative flex-shrink-0 ml-auto" ref={shareMenuRef}>
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      disabled={!!sharingChannel}
                      className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors border border-gray-200"
                      title={t('cart.sidePanel.actions.share', 'Share')}
                    >
                      {sharingChannel
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Share2 className="w-3 h-3" />}
                      <span>{t('cart.sidePanel.actions.share', 'Share')}</span>
                    </button>
                    {showShareMenu && (
                      <div className="absolute top-full right-0 mt-1 bg-white rounded-[10px] shadow-lg border border-[#efebe2] overflow-hidden z-20 min-w-[160px]">
                        <button
                          onClick={async () => {
                            setSharingChannel('whatsapp');
                            setShowShareMenu(false);
                            try {
                              await orderExportService.sendQuotationPdfViaWhatsApp(getOrderObject(), buildPdfPayload());
                              showNotification(t('cart.sidePanel.notifications.whatsappSent', 'Quotation sent via WhatsApp!'));
                            } catch (err) {
                              console.error('WhatsApp share failed:', err);
                              showNotification(t('cart.sidePanel.notifications.sendFailed', 'Failed to send quotation.'), 'error');
                            } finally {
                              setSharingChannel(null);
                            }
                          }}
                          disabled={!!sharingChannel}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-[#201e1a] hover:bg-green-50 hover:text-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {sharingChannel === 'whatsapp'
                            ? <Loader2 className="w-4 h-4 text-green-600 animate-spin" />
                            : <MessageCircle className="w-4 h-4 text-green-600" />}
                          <span>{t('cart.sidePanel.actions.whatsapp', 'WhatsApp')}</span>
                        </button>
                        {currentUser?.email && (
                          <button
                            onClick={async () => {
                              setSharingChannel('email');
                              setShowShareMenu(false);
                              try {
                                await orderExportService.sendQuotationPdfViaEmail(getOrderObject(), currentUser.email ?? undefined, buildPdfPayload());
                                showNotification(t('cart.sidePanel.notifications.emailSentToMe', 'Quotation sent to your email!'));
                              } catch (err) {
                                console.error('Send to me failed:', err);
                                showNotification(t('cart.sidePanel.notifications.sendFailed', 'Failed to send quotation.'), 'error');
                              } finally {
                                setSharingChannel(null);
                              }
                            }}
                            disabled={!!sharingChannel}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-[#201e1a] hover:bg-indigo-50 hover:text-indigo-700 transition-colors border-t border-[#efebe2] disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {sharingChannel === 'email'
                              ? <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
                              : <Mail className="w-4 h-4 text-indigo-600" />}
                            <div className="flex flex-col items-start">
                              <span>{t('cart.sidePanel.actions.sendToMe', 'Send To Me')}</span>
                              <span className="text-[10px] text-gray-400 truncate max-w-[120px]">{currentUser.email}</span>
                            </div>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Parts List Content - Hidden when minimized on desktop only */}
            <div className={`flex-1 overflow-y-auto`}>
              <div className="p-3 md:p-4 space-y-3">

                {/* View toggle: rich cards vs compact table + CSV/PDF export */}
                {cartItems.length > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleDownloadCartCsv}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-[#6b6660] bg-white border border-[#e5e3df] rounded-lg hover:bg-[#f6f4f0] hover:text-[#201e1a] transition-colors"
                        title={t('cart.sidePanel.table.downloadCsv', { defaultValue: 'Download CSV' })}
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>{t('cart.sidePanel.table.csv', { defaultValue: 'CSV' })}</span>
                      </button>
                      <button
                        onClick={async () => {
                          setSharingChannel('pdf');
                          try {
                            await orderExportService.downloadQuotationPdf(getOrderObject(), buildPdfPayload());
                            showNotification(t('cart.sidePanel.notifications.pdfDownloaded', 'PDF downloaded successfully!'));
                          } catch (err) {
                            console.error('PDF download failed:', err);
                            showNotification(t('cart.sidePanel.notifications.pdfFailed', 'PDF download failed. Please try again.'));
                          } finally {
                            setSharingChannel(null);
                          }
                        }}
                        disabled={!!sharingChannel}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-[#6b6660] bg-white border border-[#e5e3df] rounded-lg hover:bg-[#f6f4f0] hover:text-[#201e1a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        title={t('cart.sidePanel.table.downloadPdf', { defaultValue: 'Download PDF' })}
                      >
                        {sharingChannel === 'pdf'
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Download className="w-3.5 h-3.5" />}
                        <span>{t('cart.sidePanel.table.pdf', { defaultValue: 'PDF' })}</span>
                      </button>
                    </div>
                    <div className="inline-flex rounded-lg border border-[#e5e3df] overflow-hidden">
                      <button
                        onClick={() => setCartViewMode('cards')}
                        className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${cartViewMode === 'cards' ? 'bg-[#efedff] text-[#6866ff]' : 'bg-white text-[#8a857c] hover:text-[#201e1a]'}`}
                      >
                        {t('cart.sidePanel.view.cards', { defaultValue: 'Cards' })}
                      </button>
                      <button
                        onClick={() => setCartViewMode('table')}
                        className={`px-2.5 py-1 text-[11px] font-semibold border-l border-[#e5e3df] transition-colors ${cartViewMode === 'table' ? 'bg-[#efedff] text-[#6866ff]' : 'bg-white text-[#8a857c] hover:text-[#201e1a]'}`}
                      >
                        {t('cart.sidePanel.view.table', { defaultValue: 'Table' })}
                      </button>
                    </div>
                  </div>
                )}

                <div key={cartViewMode} className="animate-view-switch space-y-3">
                  {cartViewMode === 'table' ? renderCartTable() : (
                    <>
                      {/* === PARTS SECTION === */}
                      <div className="bg-[#f6f4f0] rounded-[10px] p-3">
                        {renderSectionHeader(
                          <Package className="w-3.5 h-3.5" />,
                          t('cart.sidePanel.sections.parts', { defaultValue: 'Parts' }),
                          parts.length,
                          'parts',
                          'blue'
                        )}
                        {!collapsedSections['parts'] && (
                          parts.length > 0 ? (
                            <div className="divide-y divide-[#b2b2b2]">
                              {parts.map((item, index) => renderItemCard(item, index))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 py-3">
                              <Package className="w-3.5 h-3.5 text-[#999999] flex-shrink-0" />
                              <span className="text-xs text-[#999999] italic">
                                {t('cart.sidePanel.sections.noParts', { defaultValue: 'No parts added yet' })}
                              </span>
                            </div>
                          )
                        )}
                      </div>

                      {/* === NOT FOUND SECTION === */}
                      {notFoundParts.length > 0 && (
                        <div className="bg-amber-50 rounded-[10px] p-4">
                          {renderSectionHeader(
                            <AlertTriangle className="w-4 h-4" />,
                            t('cart.sidePanel.sections.notFound', { defaultValue: 'Not Found' }),
                            notFoundParts.length,
                            'notFound',
                            'amber'
                          )}
                          {!collapsedSections['notFound'] && (
                            <div className="divide-y divide-[#b2b2b2]">
                              {notFoundParts.map((item, index) => renderItemCard(item, parts.length + index))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* === SERVICES SECTION (always visible) === */}
                      <div className="bg-[#f6f4f0] rounded-[10px] p-4">
                        {renderSectionHeader(
                          <Wrench className="w-4 h-4" />,
                          t('cart.sidePanel.sections.services', { defaultValue: 'Services' }),
                          services.length,
                          'services',
                          'purple'
                        )}
                        {!collapsedSections['services'] && (
                          services.length > 0 ? (
                            <div className="divide-y divide-[#b2b2b2]">
                              {services.map((item, index) => renderItemCard(item, parts.length + notFoundParts.length + index))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 py-3">
                              <Wrench className="w-3.5 h-3.5 text-[#999999] flex-shrink-0" />
                              <span className="text-xs text-[#999999] italic">
                                {t('cart.sidePanel.sections.noServices', { defaultValue: 'No additional services added' })}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Cart Summary Footer */}
            {!isMinimized && (
              <div className="flex-shrink-0 border-t border-[#efebe2] bg-white px-4 py-3">
                <div className="space-y-1.5">
                  {/* Total Parts & Services row */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#201e1a]">
                      {t('cart.sidePanel.summary.totalItems', { defaultValue: 'Total Parts & Services' })}
                    </span>
                    <span className="text-xs font-semibold text-[#201e1a]">{summary.totalQuantity}</span>
                  </div>

                  {summary.partsCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-[#999999]">
                        <Package className="w-3.5 h-3.5" />
                        {t('cart.sidePanel.sections.parts', { defaultValue: 'Parts' })}
                      </span>
                      <span className="text-xs text-[#999999]">{summary.partsCount}</span>
                    </div>
                  )}

                  {summary.servicesCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-[#999999]">
                        <Wrench className="w-3.5 h-3.5" />
                        {t('cart.sidePanel.sections.services', { defaultValue: 'Services' })}
                      </span>
                      <span className="text-xs text-[#999999]">{summary.servicesCount}</span>
                    </div>
                  )}

                  {summary.notFoundCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-amber-500">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {t('cart.sidePanel.sections.notFound', { defaultValue: 'Not Found' })}
                      </span>
                      <span className="text-xs text-amber-500">{summary.notFoundCount}</span>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="border-t border-[#efebe2] pt-1.5 mt-1" />

                  {/* Discount Agreement Selector — multi-customer checkboxes */}
                  {availableAgreements.length > 0 && (() => {
                    const uniqueCustomers = Array.from(
                      new Map(availableAgreements.map(a => [a.customer_name, a])).values()
                    );
                    const applySelections = async (newNames: string[]) => {
                      setSelectedCustomerNames(newNames);
                      const orderId = getActiveOrderId();
                      if (!orderId) return;
                      // Build merged discount snapshot from all selected customers
                      const lineItems = cartItems
                        .filter(item => !isNotFoundItem(item) && !isServiceItem(item))
                        .map((item, idx) => {
                          const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
                          const itemKey = item.id || item.part_id || partData.part_number || `${idx}`;
                          const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];
                          const options = getPriceOptions(pricingArr, t, numberFormat);
                          const selectedStr = selectedPricingIndices[itemKey];
                          let selectedOption: PriceOption | null = null;
                          if (selectedStr) {
                            const [idxStr, type] = selectedStr.split(':');
                            selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
                          }
                          if (!selectedOption && options.length > 0) selectedOption = options[0];
                          const price = itemKey in manualPrices ? manualPrices[itemKey] : (selectedOption?.price ?? getIntegrationPrice(item, availabilityConfig, numberFormat));
                          if (price === null) return null;
                          const selectedEntry = selectedOption ? pricingArr[selectedOption.idx] : null;
                          const description = item.translated_description || partData.description || partData.DESCRIPTION || '';
                          const vinData = item.structured_data?.vin_data || {};
                          const vehicleBrand = vinData.brand || vinData.make || vinData.manufacturer || '';
                          return {
                            line_id: itemKey,
                            description,
                            retail_price: price,
                            quantity: item.quantity || 1,
                            part_brand: partData.brand || selectedEntry?.brand || vehicleBrand || undefined,
                            part_name: description || undefined,
                            part_category: partData.category || partData.CATEGORY || selectedEntry?.category || undefined,
                            oe_sku: partData.part_number || partData.oe_number || item.part_id || undefined,
                            supplier_sku: selectedEntry?.part_number || selectedEntry?.sku || undefined,
                          };
                        })
                        .filter(Boolean) as any[];

                      try {
                        let mergedSnapshot: any = null;
                        if (newNames.length > 0 && lineItems.length > 0) {
                          // Resolve for each selected customer and merge: best discount per line wins
                          const results = await Promise.all(
                            newNames.map(name =>
                              discountService.resolveOrderPricing({
                                customer_name: name,
                                currency: currentUser?.currency || 'USD',
                                items: lineItems,
                              }).then(r => r.data).catch(() => null)
                            )
                          );
                          const validResults = results.filter(Boolean) as any[];
                          if (validResults.length > 0) {
                            // Merge: for each line pick the result with the highest discount_amount
                            const mergedItems = lineItems.map(li => {
                              let best: any = null;
                              for (const res of validResults) {
                                const ri = res.items.find((i: any) => i.line_id === li.line_id);
                                if (ri && (!best || ri.discount_amount > best.discount_amount)) {
                                  best = ri;
                                }
                              }
                              return best || { ...li, discount_type: null, discount_value: 0, scope_type: null, scope_value: null, discount_amount: 0, final_price: li.retail_price, line_total: li.retail_price * li.quantity, agreement_id: null };
                            });
                            const retailTotal = mergedItems.reduce((s: number, i: any) => s + i.retail_price * i.quantity, 0);
                            const discountedTotal = mergedItems.reduce((s: number, i: any) => s + i.line_total, 0);
                            mergedSnapshot = {
                              customer_name: newNames.join(', '),
                              currency: currentUser?.currency || 'USD',
                              items: mergedItems,
                              retail_total: Math.round(retailTotal * 100) / 100,
                              discounted_total: Math.round(discountedTotal * 100) / 100,
                              total_discount_amount: Math.round((retailTotal - discountedTotal) * 100) / 100,
                              has_discount: retailTotal > discountedTotal,
                            };
                            setOrderDiscount(mergedSnapshot.has_discount ? mergedSnapshot : null);
                          }
                        } else {
                          setOrderDiscount(null);
                        }
                        // Persist buyer + snapshot to the order and update local cache
                        // Store full selected_customer_names list in snapshot so it survives page refresh.
                        // Also carry over extra discount so it is never wiped by agreement changes.
                        const extraDiscountFields = customDiscountValue > 0
                          ? { extra_discount_value: customDiscountValue, extra_discount_type: customDiscountType }
                          : {};
                        const snapshotToSave = mergedSnapshot
                          ? { ...mergedSnapshot, selected_customer_names: newNames, ...extraDiscountFields }
                          : { has_discount: false, selected_customer_names: newNames, ...extraDiscountFields };
                        try {
                          const updated = await orderService.updateOrder(orderId, {
                            buyer: newNames[0] || '',
                            discount_snapshot: snapshotToSave,
                          });
                          // Keep in-memory order object in sync so restore effect works on re-mount
                          if (updated) setOrderObject(updated);
                        } catch {/* non-critical */ }
                        // Workflow audit log for discount assignment
                        if (onWorkflowLog) {
                          const logMsg = newNames.length > 0
                            ? t('cart.workflow.discountAssigned', { names: newNames.join(', '), total: mergedSnapshot?.has_discount ? mergedSnapshot.total_discount_amount : null, defaultValue: `Discount agreement assigned: {{names}}${mergedSnapshot?.has_discount ? ` (total discount: {{total}})` : ''}` })
                            : t('cart.workflow.discountRemoved', 'Discount agreement removed');
                          onWorkflowLog(logMsg, 'edit_discount');
                        }
                      } catch {
                        // non-critical
                      }
                    };
                    return (
                      <div className="space-y-1" ref={agreementDropdownRef}>
                        <span className="text-[10px] font-semibold text-[#999999] block">
                          {t('cart.sidePanel.discount.agreement', 'Customer Agreements')}
                        </span>
                        {/* Dropdown trigger */}
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowAgreementDropdown(v => !v)}
                            className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border border-gray-200 rounded-md text-[11px] text-[#201e1a] hover:border-green-400 transition-colors"
                          >
                            <span className="truncate">
                              {selectedCustomerNames.length === 0
                                ? t('cart.sidePanel.discount.selectAgreements', 'Select agreements…')
                                : selectedCustomerNames.join(', ')}
                            </span>
                            <ChevronDown className={`w-3 h-3 flex-shrink-0 text-gray-400 transition-transform ${showAgreementDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showAgreementDropdown && (
                            <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
                              {/* Search */}
                              <div className="p-1.5 border-b border-gray-100">
                                <input
                                  type="text"
                                  value={agreementFilter}
                                  onChange={e => setAgreementFilter(e.target.value)}
                                  placeholder={t('cart.sidePanel.discount.filterAgreements', 'Search…')}
                                  className="w-full text-[11px] px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-green-400 bg-white"
                                  autoFocus
                                />
                              </div>
                              {/* Options */}
                              <div className="max-h-48 overflow-y-auto">
                                {(() => {
                                  const q = agreementFilter.toLowerCase();
                                  const filtered = availableAgreements.filter(a =>
                                    !q || a.customer_name.toLowerCase().includes(q) || a.scope_value.toLowerCase().includes(q) || a.scope_type.toLowerCase().includes(q)
                                  );
                                  // Split into brand-matched and others
                                  const brandMatched = vehicleBrand
                                    ? filtered.filter(a => a.scope_type === 'brand' && a.scope_value.toLowerCase().includes(vehicleBrand))
                                    : [];
                                  const others = vehicleBrand
                                    ? filtered.filter(a => !(a.scope_type === 'brand' && a.scope_value.toLowerCase().includes(vehicleBrand)))
                                    : filtered;

                                  const renderAgreement = (a: DiscountAgreement) => {
                                    const checked = selectedCustomerNames.includes(a.customer_name);
                                    const discountLabel = a.discount_type === 'percentage'
                                      ? `-${a.discount_value}%`
                                      : `-${currencySymbol}${a.discount_value}`;
                                    return (
                                      <label
                                        key={`${a.id}`}
                                        className={`flex items-start gap-2 px-3 py-2 cursor-pointer transition-colors ${checked ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => {
                                            const next = checked
                                              ? selectedCustomerNames.filter(n => n !== a.customer_name)
                                              : [...selectedCustomerNames, a.customer_name];
                                            applySelections(next);
                                          }}
                                          className="accent-green-600 w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-[11px] font-semibold text-[#201e1a] truncate">{a.customer_name}</span>
                                            <span className="inline-flex items-center px-1.5 rounded-full bg-green-100 text-[10px] font-bold text-green-700 flex-shrink-0">{discountLabel}</span>
                                          </div>
                                          <div className="text-[10px] text-[#999999] truncate">
                                            {a.scope_type.replace(/_/g, ' ')}: <span className="font-medium text-[#555]">{a.scope_value}</span>
                                          </div>
                                        </div>
                                      </label>
                                    );
                                  };

                                  return (
                                    <>
                                      {brandMatched.length > 0 && (
                                        <>
                                          <div className="px-3 py-1 text-[10px] font-semibold text-blue-500 bg-blue-50 border-b border-blue-100 uppercase tracking-wide">
                                            {t('cart.sidePanel.discount.brandAgreements', '{{brand}} agreements', { brand: vehicleBrand.charAt(0).toUpperCase() + vehicleBrand.slice(1) })}
                                          </div>
                                          {brandMatched.map(renderAgreement)}
                                          {others.length > 0 && (
                                            <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 bg-gray-50 border-b border-gray-100 uppercase tracking-wide">
                                              {t('cart.sidePanel.discount.otherAgreements', 'Other')}
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {others.map(renderAgreement)}
                                    </>
                                  );
                                })()}
                              </div>
                              {/* Footer */}
                              {selectedCustomerNames.length > 0 && (
                                <div className="border-t border-gray-100 px-3 py-1.5">
                                  <button
                                    onClick={() => { applySelections([]); setShowAgreementDropdown(false); }}
                                    className="text-[10px] text-red-400 hover:text-red-600 transition-colors"
                                  >
                                    {t('cart.sidePanel.discount.clearAll', 'Clear all')}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {pricingQueuedSeconds >= QUEUED_NOTICE_SECONDS && (
                    <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {t('cart.sidePanel.pricingQueued', { defaultValue: 'Waiting for the pricing service to pick this up…' })}
                    </div>
                  )}

                  {/* Est. Parts Total */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#201e1a]">
                      {t('cart.sidePanel.summary.estimatedTotal', { defaultValue: 'Est. Total' })}
                    </span>
                    <span className="text-xs font-semibold text-[#201e1a]">
                      {summary.pendingPriceCount > 0 ? (
                        <span className="flex items-center gap-1 text-gray-400">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {summary.totalPrice > 0 ? `${currencySymbol}${fmt(summary.totalPrice)}+` : '...'}
                        </span>
                      ) : summary.totalPrice > 0 ? (
                        orderDiscount?.has_discount && selectedCustomerNames.length > 0 ? (
                          <span className="flex items-center gap-1.5">
                            <span className="line-through text-gray-400 font-normal">{currencySymbol}{fmt(orderDiscount.retail_total)}</span>
                            <span className="text-green-700 font-semibold">{currencySymbol}{fmt(orderDiscount.discounted_total)}</span>
                          </span>
                        ) : `${currencySymbol}${fmt(summary.totalPrice)}`
                      ) : '--'
                      }
                    </span>
                  </div>

                  {/* Inline "You save" row */}
                  {orderDiscount?.has_discount && selectedCustomerNames.length > 0 && summary.pendingPriceCount === 0 && (
                    <div className="flex items-center justify-between -mt-0.5">
                      <span className="text-[11px] text-green-600 font-medium">
                        🎉 {t('cart.sidePanel.discount.youSave', 'You save')}
                      </span>
                      <span className="text-[11px] font-bold text-green-600 tabular-nums">
                        −{currencySymbol}{fmt(orderDiscount.total_discount_amount)}
                      </span>
                    </div>
                  )}

                  {/* Discount breakdown */}
                  {(orderDiscount || discountLoading) && selectedCustomerNames.length > 0 && summary.pendingPriceCount === 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 space-y-1">
                      {discountLoading ? (
                        <div className="flex items-center gap-1.5 text-xs text-green-600">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>{t('cart.sidePanel.discount.loading', 'Checking discounts…')}</span>
                        </div>
                      ) : orderDiscount && (
                        <>
                          <div className="flex items-center justify-between gap-2 text-[11px] text-green-700 font-medium">
                            <span className="shrink-0">{t('cart.sidePanel.discount.retailTotal', 'List price total')}</span>
                            <span className="line-through text-green-500 tabular-nums truncate">{currencySymbol}{fmt(orderDiscount.retail_total)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-[11px] text-green-700 font-semibold">
                            <span className="shrink-0">{t('cart.sidePanel.discount.youSave', 'You save')}</span>
                            <span className="tabular-nums">−{currencySymbol}{fmt(orderDiscount.total_discount_amount)}</span>
                          </div>
                          <div className="border-t border-green-200 pt-1 flex items-center justify-between gap-2 text-xs font-bold text-green-800">
                            <span className="shrink-0">{t('cart.sidePanel.discount.discountedTotal', 'Your price')}</span>
                            <span className="tabular-nums">{currencySymbol}{fmt(orderDiscount.discounted_total)}</span>
                          </div>
                          {/* Per-item agreement hints */}
                          {orderDiscount.items.some(i => i.discount_amount > 0) && (
                            <div className="pt-0.5 space-y-0.5">
                              {orderDiscount.items.filter(i => i.discount_amount > 0).map(i => (
                                <div key={i.line_id} className="flex items-start gap-1 text-[10px] text-green-600">
                                  <CheckCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                  <span className="truncate">
                                    {i.description || i.line_id}
                                    {' '}— {i.discount_type === 'percentage'
                                      ? `${i.discount_value}% ${t('cart.sidePanel.discount.off', 'off')}`
                                      : `−${currencySymbol}${fmt(i.discount_amount)}`}
                                    {i.scope_type && i.scope_type !== 'b2b_default' && i.scope_value
                                      ? ` (${i.scope_type.replace(/_/g, ' ')}: ${i.scope_value})`
                                      : i.scope_type === 'b2b_default' ? ` (${t('cart.sidePanel.discount.b2bDefault', 'B2B default')})` : ''}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Missing price warning */}
                  {summary.missingPriceCount > 0 && summary.pendingPriceCount === 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      {t('cart.sidePanel.summary.missingPrices', {
                        defaultValue: '{{count}} item(s) have no price — click to set manually',
                        count: summary.missingPriceCount,
                      })}
                    </div>
                  )}

                  {summary.priceableCount < summary.totalQuantity && summary.totalPrice > 0 && (
                    <div className="text-xs text-[#999999] text-right">
                      {t('cart.sidePanel.summary.partialPricing', {
                        defaultValue: 'Based on {{count}}/{{total}} priced items',
                        count: summary.priceableCount,
                        total: summary.totalQuantity
                      })}
                    </div>
                  )}

                  {/* Labor Total */}
                  {summary.totalLaborHours > 0 && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-indigo-600 font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          {t('cart.sidePanel.summary.laborTotal', { defaultValue: 'Est. Labor' })}
                          <span className="text-[10px] text-indigo-400">
                            ({summary.totalLaborHours.toFixed(1)}h)
                          </span>
                        </span>
                        <span className="text-xs font-semibold text-indigo-600 tabular-nums">
                          {currencySymbol}{fmt(summary.totalLaborCost)}
                        </span>
                      </div>

                      {/* Grand Total */}
                      {summary.totalPrice > 0 && (
                        <div className="flex items-center justify-between bg-indigo-50 rounded-lg px-2.5 py-1.5">
                          <span className="text-xs font-bold text-indigo-900">
                            {t('cart.sidePanel.summary.grandTotal', { defaultValue: 'Grand Total (Parts + Labor)' })}
                          </span>
                          <span className="text-xs font-bold text-indigo-900 tabular-nums">
                            {orderDiscount?.has_discount
                              ? `${currencySymbol}${fmt(orderDiscount.discounted_total + summary.totalLaborCost)}`
                              : `${currencySymbol}${fmt(summary.totalPrice + summary.totalLaborCost)}`}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Custom Discount Row */}
                  {(summary.totalPrice > 0 || summary.totalLaborCost > 0) && summary.pendingPriceCount === 0 && (() => {
                    const baseTotal = (orderDiscount?.has_discount && (orderDiscount.discounted_total ?? 0) > 0)
                      ? orderDiscount.discounted_total + summary.totalLaborCost
                      : summary.totalPrice + summary.totalLaborCost;
                    const customDiscountAmount = customDiscountValue > 0
                      ? customDiscountType === 'percentage'
                        ? Math.min(baseTotal, (baseTotal * customDiscountValue) / 100)
                        : Math.min(baseTotal, customDiscountValue)
                      : 0;
                    return (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs text-[#999999] font-medium">
                            {t('cart.sidePanel.summary.customDiscount', 'Extra Discount')}
                            {extraDiscountSaving && (
                              <Loader2 className="w-3 h-3 animate-spin text-blue-400" />
                            )}
                            {/* Type toggle */}
                            <button
                              onClick={() => {
                                const newType = customDiscountType === 'percentage' ? 'fixed' : 'percentage';
                                setCustomDiscountType(newType);
                                // Persist the type change immediately if there's an active discount value
                                if (customDiscountValue > 0) {
                                  saveExtraDiscount(customDiscountValue, newType);
                                }
                              }}
                              className="px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-[10px] font-semibold text-gray-500 transition-colors"
                              title="Toggle discount type"
                            >
                              {customDiscountType === 'percentage' ? '%' : currencySymbol}
                            </button>
                            {customDiscountEditing ? (
                              <input
                                type="number"
                                min="0"
                                max={customDiscountType === 'percentage' ? 100 : undefined}
                                step={customDiscountType === 'percentage' ? 0.5 : 1}
                                value={customDiscountInputValue}
                                onChange={(e) => setCustomDiscountInputValue(e.target.value)}
                                onBlur={() => {
                                  const parsed = parseFloat(customDiscountInputValue);
                                  const val = isNaN(parsed) ? 0 : Math.max(0, parsed);
                                  const finalVal = customDiscountType === 'percentage' ? Math.min(100, val) : val;
                                  setCustomDiscountValue(finalVal);
                                  setCustomDiscountEditing(false);
                                  saveExtraDiscount(finalVal, customDiscountType);
                                  if (onWorkflowLog) {
                                    const msg = finalVal > 0
                                      ? t('cart.workflow.customDiscountSet', { value: finalVal, unit: customDiscountType === 'percentage' ? '%' : ` ${currentUser?.currency || ''}`, defaultValue: `Custom discount set: {{value}}{{unit}}` })
                                      : t('cart.workflow.customDiscountCleared', 'Custom discount cleared (set to 0)');
                                    onWorkflowLog(msg, 'edit_discount');
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const parsed = parseFloat(customDiscountInputValue);
                                    const val = isNaN(parsed) ? 0 : Math.max(0, parsed);
                                    const finalVal = customDiscountType === 'percentage' ? Math.min(100, val) : val;
                                    setCustomDiscountValue(finalVal);
                                    setCustomDiscountEditing(false);
                                    saveExtraDiscount(finalVal, customDiscountType);
                                    if (onWorkflowLog) {
                                      const msg = finalVal > 0
                                        ? t('cart.workflow.customDiscountSet', { value: finalVal, unit: customDiscountType === 'percentage' ? '%' : ` ${currentUser?.currency || ''}`, defaultValue: `Custom discount set: {{value}}{{unit}}` })
                                        : t('cart.workflow.customDiscountCleared', 'Custom discount cleared (set to 0)');
                                      onWorkflowLog(msg, 'edit_discount');
                                    }
                                  } else if (e.key === 'Escape') {
                                    setCustomDiscountInputValue(String(customDiscountValue));
                                    setCustomDiscountEditing(false);
                                  }
                                }}
                                className="ml-1 w-14 text-xs border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[#201e1a]"
                                autoFocus
                              />
                            ) : (
                              <button
                                onClick={() => { setCustomDiscountInputValue(String(customDiscountValue)); setCustomDiscountEditing(true); }}
                                className="ml-0.5 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-[10px] font-semibold text-gray-500 transition-colors"
                                title={t('cart.sidePanel.summary.editCustomDiscount', 'Click to set extra discount')}
                              >
                                {customDiscountValue > 0
                                  ? customDiscountType === 'percentage'
                                    ? `${customDiscountValue}%`
                                    : `${currencySymbol}${customDiscountValue}`
                                  : t('cart.sidePanel.summary.addDiscount', 'Add')}
                              </button>
                            )}
                            {customDiscountValue > 0 && (
                              <button
                                onClick={() => {
                                  setCustomDiscountValue(0);
                                  setCustomDiscountInputValue('0');
                                  saveExtraDiscount(0, customDiscountType);
                                  if (onWorkflowLog) onWorkflowLog(t('cart.workflow.customDiscountRemoved', 'Custom discount removed'), 'edit_discount');
                                }}
                                className="text-[10px] text-red-400 hover:text-red-600 transition-colors"
                                title="Remove custom discount"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                          <span className="text-xs font-semibold text-green-700 tabular-nums">
                            {customDiscountAmount > 0 ? `−${currencySymbol}${fmt(customDiscountAmount)}` : '--'}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Tax Row */}
                  {(summary.totalPrice > 0 || summary.totalLaborCost > 0) && summary.pendingPriceCount === 0 && (
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-[#999999] font-medium">
                        {t('cart.sidePanel.summary.tax', { defaultValue: 'Tax' })}
                        {taxRateEditing ? (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={taxRateInputValue}
                            onChange={(e) => setTaxRateInputValue(e.target.value)}
                            onBlur={() => {
                              const parsed = parseFloat(taxRateInputValue);
                              setTaxRate(isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed)));
                              setTaxRateEditing(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const parsed = parseFloat(taxRateInputValue);
                                setTaxRate(isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed)));
                                setTaxRateEditing(false);
                              } else if (e.key === 'Escape') {
                                setTaxRateInputValue(String(taxRate));
                                setTaxRateEditing(false);
                              }
                            }}
                            className="ml-1 w-12 text-xs border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[#201e1a]"
                            autoFocus
                          />
                        ) : (
                          <button
                            onClick={() => { setTaxRateInputValue(String(taxRate)); setTaxRateEditing(true); }}
                            className="ml-0.5 px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-[10px] font-semibold text-gray-500 transition-colors"
                            title={t('cart.sidePanel.summary.editTax', 'Click to edit tax rate')}
                          >
                            {taxRate}%
                          </button>
                        )}
                      </span>
                      <span className="text-xs font-semibold text-[#201e1a] tabular-nums">
                        {taxRate > 0 ? (() => {
                          const baseTotal = (orderDiscount?.has_discount && (orderDiscount.discounted_total ?? 0) > 0)
                            ? orderDiscount.discounted_total + summary.totalLaborCost
                            : summary.totalPrice + summary.totalLaborCost;
                          const customAmt = customDiscountValue > 0
                            ? customDiscountType === 'percentage' ? Math.min(baseTotal, (baseTotal * customDiscountValue) / 100) : Math.min(baseTotal, customDiscountValue)
                            : 0;
                          return `${currencySymbol}${fmt(((baseTotal - customAmt) * taxRate) / 100)}`;
                        })() : '--'}
                      </span>
                    </div>
                  )}

                  {/* Total with Tax */}
                  {taxRate > 0 && (summary.totalPrice > 0 || summary.totalLaborCost > 0) && summary.pendingPriceCount === 0 && (
                    <div className="flex items-center justify-between bg-[#f6f4f0] rounded-lg px-2.5 py-1.5">
                      <span className="text-xs font-bold text-[#201e1a]">
                        {t('cart.sidePanel.summary.totalWithTax', { defaultValue: 'Total (incl. Tax)' })}
                      </span>
                      <span className="text-xs font-bold text-[#201e1a] tabular-nums">
                        {(() => {
                          const baseTotal = (orderDiscount?.has_discount && (orderDiscount.discounted_total ?? 0) > 0)
                            ? orderDiscount.discounted_total + summary.totalLaborCost
                            : summary.totalPrice + summary.totalLaborCost;
                          const customAmt = customDiscountValue > 0
                            ? customDiscountType === 'percentage' ? Math.min(baseTotal, (baseTotal * customDiscountValue) / 100) : Math.min(baseTotal, customDiscountValue)
                            : 0;
                          return `${currencySymbol}${fmt((baseTotal - customAmt) * (1 + taxRate / 100))}`;
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Submit Order Button */}
                  {getOrderObject() && !readOnly && (
                    <>
                      <div className="border-t border-[#efebe2] my-1" />
                      <div className="pt-1 flex flex-col gap-2">
                        {/* Approve & Send — shown when order came from a channel (email/whatsapp)
                            and is still a draft with all prices filled in.
                            When this button is shown, the regular Submit button is hidden. */}
                        {(() => {
                          const order = getOrderObject();
                          const channelSource = order?.source;
                          const isChannelOrder = channelSource === 'email' || channelSource === 'whatsapp';
                          const allPricesFilled = cartItems.length > 0 && summary.pendingPriceCount === 0 && summary.missingPriceCount === 0;
                          if (!isChannelOrder || order?.status !== 'draft' || !allPricesFilled) return null;

                          const channelIcon = channelSource === 'whatsapp'
                            ? <MessageCircle className="w-4 h-4 flex-shrink-0" />
                            : <Mail className="w-4 h-4 flex-shrink-0" />;
                          const channelLabel = channelSource === 'whatsapp'
                            ? t('cart.sidePanel.actions.approveAndSendWhatsApp', 'Approve & Send via WhatsApp')
                            : t('cart.sidePanel.actions.approveAndSendEmail', 'Approve & Send via Email');

                          return (
                            <button
                              onClick={async () => {
                                const orderId = getActiveOrderId();
                                if (!orderId) return;
                                setSharingChannel(channelSource as 'email' | 'whatsapp');
                                try {
                                  if (channelSource === 'email') {
                                    // The server replies to the original sender on their thread; only
                                    // pass buyer when it is an address (it is usually a display name).
                                    const senderEmail = order?.buyer && order.buyer.includes('@') ? order.buyer : undefined;
                                    await orderExportService.sendQuotationPdfViaEmail(order, senderEmail, buildPdfPayload());
                                    showNotification(t('cart.sidePanel.notifications.approvedAndSentEmail', 'Quotation approved and sent via email!'));
                                  } else {
                                    await orderExportService.sendQuotationPdfViaWhatsApp(order, buildPdfPayload());
                                    showNotification(t('cart.sidePanel.notifications.approvedAndSentWhatsApp', 'Quotation approved and sent via WhatsApp!'));
                                  }
                                } catch (err: any) {
                                  console.error('Approve & Send failed:', err);
                                  const detail = err?.response?.data?.detail;
                                  showNotification(
                                    typeof detail === 'string' && detail
                                      ? `${t('cart.sidePanel.notifications.sendFailed', 'Failed to send quotation.')} ${detail}`
                                      : t('cart.sidePanel.notifications.sendFailed', 'Failed to send quotation.'),
                                    'error',
                                  );
                                  setSharingChannel(null);
                                  return;
                                }
                                // The quote is out — a status hiccup must not read as "send failed".
                                try {
                                  await orderService.updateOrderStatus(orderId, 'completed');
                                  setOrderObject({ ...order, status: 'completed' });
                                  if (onOrderSubmitted) onOrderSubmitted();
                                } catch (err) {
                                  console.error('Quote sent, but marking the order completed failed:', err);
                                } finally {
                                  setSharingChannel(null);
                                }
                              }}
                              disabled={!!sharingChannel || !!isSubmittingOrder}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700 disabled:hover:bg-emerald-600"
                            >
                              {sharingChannel
                                ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                : channelIcon}
                              <span>
                                {sharingChannel
                                  ? t('cart.sidePanel.actions.sending', 'Sending...')
                                  : channelLabel}
                              </span>
                            </button>
                          );
                        })()}

                        {/* Regular Submit button — hidden when the Approve & Send button is shown */}
                        {(() => {
                          const order = getOrderObject();
                          const channelSource = order?.source;
                          const isChannelOrder = channelSource === 'email' || channelSource === 'whatsapp';
                          const allPricesFilled = cartItems.length > 0 && summary.pendingPriceCount === 0 && summary.missingPriceCount === 0;
                          const showApproveButton = isChannelOrder && order?.status === 'draft' && allPricesFilled;
                          if (showApproveButton) return null;
                          if (order?.status !== 'draft') return (
                            <div className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-400 bg-gray-100 rounded-[10px] cursor-not-allowed select-none">
                              <CheckCircle className="w-4 h-4" />
                              <span>
                                {String(t(`orders.status.${order?.status}`, order?.status ?? ''))}
                              </span>
                            </div>
                          );
                          return (
                            <button
                              onClick={async () => {
                                const orderId = getActiveOrderId();
                                if (!orderId) return;
                                setIsSubmittingOrder(true);
                                try {
                                  const order = getOrderObject();
                                  const buyerName = selectedCustomerName || order?.buyer;

                                  // Build line items from the current cart for discount resolution
                                  const lineItems = cartItems
                                    .filter(item => !isNotFoundItem(item) && !isServiceItem(item))
                                    .map((item, idx) => {
                                      const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
                                      const itemKey = item.id || item.part_id || partData.part_number || `${idx}`;
                                      const pricingArr: any[] = Array.isArray(item.pricing_data) ? item.pricing_data : [];

                                      const options = getPriceOptions(pricingArr, t, numberFormat);
                                      const selectedStr = selectedPricingIndices[itemKey];
                                      let selectedOption: PriceOption | null = null;
                                      if (selectedStr) {
                                        const [idxStr, type] = selectedStr.split(':');
                                        selectedOption = options.find(o => o.idx === parseInt(idxStr, 10) && o.type === type) ?? null;
                                      }
                                      if (!selectedOption && options.length > 0) selectedOption = options[0];

                                      const price = itemKey in manualPrices ? manualPrices[itemKey] : (selectedOption?.price ?? getIntegrationPrice(item, availabilityConfig, numberFormat));
                                      if (price === null) return null;

                                      const selectedEntry = selectedOption ? pricingArr[selectedOption.idx] : null;
                                      return {
                                        line_id: itemKey,
                                        description: item.translated_description || partData.description || partData.DESCRIPTION || '',
                                        retail_price: price,
                                        quantity: item.quantity || 1,
                                        part_brand: partData.brand || selectedEntry?.brand || undefined,
                                      };
                                    })
                                    .filter(Boolean) as any[];

                                  if (buyerName && lineItems.length > 0) {
                                    // Resolve discounts + submit in one call (also persists snapshot)
                                    const res = await discountService.resolveAndPersistOrderPricing(orderId, {
                                      customer_name: buyerName,
                                      currency: currentUser?.currency || 'USD',
                                      items: lineItems,
                                    });
                                    const updated = { ...order, status: 'submitted', discount_snapshot: res.data };
                                    setOrderObject(updated);
                                    if (res.data.has_discount) {
                                      setOrderDiscount(res.data);
                                    }
                                  } else {
                                    // No priceable parts or no buyer — fall back to plain status update
                                    await orderService.updateOrderStatus(orderId, 'submitted');
                                    const updated = { ...order, status: 'submitted' };
                                    setOrderObject(updated);
                                  }
                                  if (onOrderSubmitted) onOrderSubmitted();
                                } catch (err) {
                                  console.error('Failed to submit order:', err);
                                } finally {
                                  setIsSubmittingOrder(false);
                                }
                              }}
                              disabled={isSubmittingOrder || cartItems.length === 0 || summary.pendingPriceCount > 0 || (waitForPricing && summary.missingPriceCount > 0)}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-[#201e1a] hover:bg-[#3a3632] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#201e1a] rounded-[10px] transition-colors"
                              title={
                                summary.pendingPriceCount > 0
                                  ? t('cart.sidePanel.actions.submitBlockedPending', 'Waiting for prices to load...')
                                  : summary.missingPriceCount > 0
                                    ? t('cart.sidePanel.actions.submitBlockedMissing', 'Set a price for all items before submitting')
                                    : undefined
                              }
                            >
                              {isSubmittingOrder
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : summary.pendingPriceCount > 0
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : summary.missingPriceCount > 0
                                    ? <AlertTriangle className="w-4 h-4" />
                                    : <CheckCircle className="w-4 h-4" />
                              }
                              <span>
                                {isSubmittingOrder
                                  ? t('cart.sidePanel.actions.submitting')
                                  : summary.pendingPriceCount > 0
                                    ? t('cart.sidePanel.actions.submitBlockedPending', 'Fetching prices...')
                                    : summary.missingPriceCount > 0
                                      ? t('cart.sidePanel.actions.submitBlockedMissing', 'Missing prices')
                                      : t('cart.sidePanel.actions.submitOrder')}
                              </span>
                            </button>
                          );
                        })()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 cursor-zoom-out"
          onClick={() => setZoomedImage(null)}
        >
          <button
            onClick={() => setZoomedImage(null)}
            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={zoomedImage}
            alt="Part"
            className="max-w-[96vw] max-h-[92vh] object-contain rounded-lg shadow-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Magic Edit Modal */}
      <MagicEditModal
        isOpen={!!magicEditPart}
        onClose={() => setMagicEditPart(null)}
        part={magicEditPart}
        onSubmit={(prompt) => {
          if (onMagicEdit && magicEditPart) {
            onMagicEdit(magicEditPart, prompt);
          }
        }}
      />

      {/* Labor / Repair Estimate Modal */}
      <LaborEstimateModal
        isOpen={!!laborEditPart}
        onClose={() => setLaborEditPart(null)}
        item={laborEditPart}
        defaultRate={DEFAULT_LABOR_RATE}
        onSave={async (item, hours, rate, note, repairType) => {
          if (onLaborUpdate) {
            await onLaborUpdate(item, { labor_hours: hours, labor_rate: rate, labor_note: note || undefined, repair_type: repairType });
          }
          setLaborEditPart(null);
        }}
        onRemove={async (item) => {
          if (onLaborUpdate) {
            await onLaborUpdate(item, null);
          }
        }}
      />

      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-fadeIn ${notification.type === 'success'
            ? 'bg-[#201e1a] text-white'
            : 'bg-red-600 text-white'
            }`}
        >
          {notification.type === 'success'
            ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
            : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
          {notification.message}
        </div>
      )}
    </>
  );
};

export default CartSidePanel;
